import { useState, useEffect, useRef, useCallback, Component } from "react";
import html2canvas from "html2canvas";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, Radar,
  PieChart, Pie, Cell
} from "recharts";

/* -- GOOGLE FONTS ------------------------------------------------------- */
(()=>{
  if(document.getElementById("meq-fonts")) return;
  const l=document.createElement("link");l.id="meq-fonts";l.rel="stylesheet";
  l.href="https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Inter:wght@300;400;500;600;700&family=DM+Mono:wght@300;400;500&display=swap";
  document.head.appendChild(l);
})();

/* -- GLOBAL CSS --------------------------------------------------------- */
const CSS = `
  @keyframes fadeUp   { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
  @keyframes scaleIn  { from{opacity:0;transform:scale(0.96)} to{opacity:1;transform:scale(1)} }
  @keyframes shimmer  { 0%{background-position:200% center} 100%{background-position:-200% center} }
  @keyframes pulse2   { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.6;transform:scale(0.95)} }
  @keyframes slideR   { from{transform:translateX(-12px);opacity:0} to{transform:translateX(0);opacity:1} }
  @keyframes glow     { 0%,100%{box-shadow:0 0 20px rgba(99,102,241,0.15)} 50%{box-shadow:0 0 40px rgba(99,102,241,0.35)} }
  .fadeUp   { animation: fadeUp 0.55s cubic-bezier(.22,.68,0,1.1) both; }
  .scaleIn  { animation: scaleIn 0.45s cubic-bezier(.22,.68,0,1.1) both; }
  .slideR   { animation: slideR 0.4s ease both; }
  * { box-sizing:border-box; margin:0; padding:0; }
  ::-webkit-scrollbar { width:5px; height:5px; }
  ::-webkit-scrollbar-track { background:transparent; }
  ::-webkit-scrollbar-thumb { background:rgba(99,102,241,0.25); border-radius:10px; }
  .card-hover { transition: transform 0.25s ease, box-shadow 0.25s ease; }
  .card-hover:hover { transform: translateY(-3px); box-shadow: 0 20px 60px rgba(99,102,241,0.12), 0 4px 20px rgba(0,0,0,0.06) !important; }
  .tab-btn { transition: all 0.2s ease; }
  .tab-btn:hover { background: rgba(99,102,241,0.06) !important; }
  input { outline:none; }
  input:focus { border-color: #6366F1 !important; box-shadow: 0 0 0 3px rgba(99,102,241,0.12) !important; }
  button:not(:disabled) { cursor: pointer; }
  button:disabled { cursor: not-allowed; }
  @media print {
    .no-print { display: none !important; }
    body { background: #fff !important; }
    #informe-print { max-width: 100% !important; }
    @page { margin: 18mm 15mm; size: A4; }
  }
`;

/* -- GLOBAL CSS --------------------------------------------------------- */

/* -- DESIGN TOKENS ------------------------------------------------------ */
const C = {
  bg:     "#F4F6FF",
  surface:"#FFFFFF",
  surfaceAlt:"#F9FAFF",
  border: "rgba(99,102,241,0.12)",
  borderHov:"rgba(99,102,241,0.3)",

  indigo: "#6366F1",
  indigoD:"#4F46E5",
  indigoL:"rgba(99,102,241,0.1)",
  indigoG:"linear-gradient(135deg,#6366F1,#8B5CF6)",

  emerald:"#059669",
  emeraldL:"rgba(5,150,105,0.1)",
  emeraldG:"linear-gradient(135deg,#059669,#10B981)",

  rose:   "#F43F5E",
  roseL:  "rgba(244,63,94,0.1)",
  roseG:  "linear-gradient(135deg,#F43F5E,#FB7185)",

  amber:  "#D97706",
  amberL: "rgba(217,119,6,0.1)",
  amberG: "linear-gradient(135deg,#D97706,#F59E0B)",

  cyan:   "#0891B2",
  cyanL:  "rgba(8,145,178,0.1)",
  cyanG:  "linear-gradient(135deg,#0891B2,#06B6D4)",

  ink:    "#0F1117",
  inkB:   "#2D3048",
  inkC:   "#6B7280",
  inkD:   "#9CA3AF",

  fD: "'DM Serif Display', Georgia, serif",
  fB: "'Inter', system-ui, sans-serif",
  fM: "'DM Mono', monospace",
};

/* -- DATA --------------------------------------------------------------- */
const MES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

const ING25  = [0,0,0,0,0,0,0,0,0,0,0,0];
const ING24  = [0,0,0,0,0,0,0,0,0,0,0,0];
const GADM25 = [0,0,0,0,0,0,0,0,0,0,0,0];
const GVTA25 = [0,0,0,0,0,0,0,0,0,0,0,0];
const GNOOP25 = [0,0,0,0,0,0,0,0,0,0,0,0];
const UTIL25 = [0,0,0,0,0,0,0,0,0,0,0,0];
const DISP25 = [0,0,0,0,0,0,0,0,0,0,0,0];
const CXC25  = [0,0,0,0,0,0,0,0,0,0,0,0];
const INV25  = [0,0,0,0,0,0,0,0,0,0,0,0];
const PPE25  = [0,0,0,0,0,0,0,0,0,0,0,0];
const TACT25 = [0,0,0,0,0,0,0,0,0,0,0,0];
const OBLF25 = [0,0,0,0,0,0,0,0,0,0,0,0];
const PROV25 = [0,0,0,0,0,0,0,0,0,0,0,0];
const CXP25  = [0,0,0,0,0,0,0,0,0,0,0,0];
const TPAS25 = [0,0,0,0,0,0,0,0,0,0,0,0];
const PAT25  = [0,0,0,0,0,0,0,0,0,0,0,0];
const IMP25  = [0,0,0,0,0,0,0,0,0,0,0,0];
const OBL25  = [0,0,0,0,0,0,0,0,0,0,0,0];
const UTIL24 = [0,0,0,0,0,0,0,0,0,0,0,0];
const GADM24 = [0,0,0,0,0,0,0,0,0,0,0,0];
const GVTA24 = [0,0,0,0,0,0,0,0,0,0,0,0];
const CV25   = [0,0,0,0,0,0,0,0,0,0,0,0];
const CP25   = [0,0,0,0,0,0,0,0,0,0,0,0];
const G5_25  = [0,0,0,0,0,0,0,0,0,0,0,0];
const ACTC25 = [0,0,0,0,0,0,0,0,0,0,0,0]; // Total Activo Corriente NIIF
const PASC25 = [0,0,0,0,0,0,0,0,0,0,0,0]; // Total Pasivo Corriente NIIF
const BAL24 = {disp:0,cxc:0,inv:0,tact:0,tpas:0,pat:0,oblf:0,prov:0,ppe:0,actc:0,pasc:0};


const sum = a => a.reduce((s,v)=>s+v,0);
var IT=sum(ING25),IT24=sum(ING24),UT=sum(UTIL25),UT24=0,GT=sum(GADM25),GV=sum(GVTA25),GN=sum(GNOOP25);
var DB={
  disp:DISP25[11],cxc:CXC25[11],inv:INV25[11],ppe:PPE25[11],
  acte:DISP25[11]+CXC25[11]+INV25[11],tact:TACT25[11],
  oblf:OBLF25[11],prov:PROV25[11],cxp:CXP25[11],
  tpas:TPAS25[11],pat:PAT25[11],imp:IMP25[11],obl:OBL25[11],
};
var PAC=DB.prov+DB.cxp+DB.imp+DB.obl;
var IT25=sum(ING25);
var UT25=sum(UTIL25);
var GT25=sum(GADM25);
var GV25=sum(GVTA25);
var TG25=GT25+GV25+sum(GNOOP25);
var MG_UT=(UT/IT*100);
var MG_UT24=(UT24/IT24*100);
var ROE=(UT/DB.pat*100).toFixed(1);
var ROA=(UT/DB.tact*100).toFixed(1);
var END=(DB.tpas/DB.tact*100).toFixed(1);
var LIQ=(DB.acte/PAC).toFixed(2);
var ACID=((DB.acte-DB.inv)/PAC).toFixed(2);
var LIQI=(DB.disp/PAC).toFixed(2);
var YOY=((IT-IT24)/IT24*100).toFixed(1);
var YOUTIL=((UT-UT24)/UT24*100).toFixed(0);
var DCXC=Math.round(365/(IT/DB.cxc));
var DCXC24=Math.round(365/(IT24/BAL24.cxc));
var APLEV=(DB.tact/DB.pat).toFixed(2);
var ROT=(IT/DB.tact).toFixed(2);
var MGC=((IT-GT-GV)/IT*100).toFixed(1);
var MGB=0,MGO=0,ROTINV=0,DINV=0;
var GT24=sum(GADM24),GV24=sum(GVTA24);
var MANUAL_AC=0,MANUAL_PC=0,MANUAL_INV=0; // Valores manuales (prioridad maxima)
var YOGT=(GT24>0?(GT-GT24)/GT24*100:0).toFixed(1);
var YOGV=(GV24>0?(GV-GV24)/GV24*100:0).toFixed(1);

function recompute(){
  IT=sum(ING25); IT24=sum(ING24); UT=sum(UTIL25); UT24=sum(UTIL24)||0;
  GT=sum(GADM25); GV=sum(GVTA25); GN=sum(GNOOP25);
  GT24=sum(GADM24); GV24=sum(GVTA24);
  IT25=sum(ING25); UT25=sum(UTIL25); GT25=sum(GADM25); GV25=sum(GVTA25);
  TG25=GT25+GV25+sum(GNOOP25);
  DB.disp=DISP25[11]||0; DB.cxc=CXC25[11]||0; DB.inv=INV25[11]||0;
  // Helper: valor de cierre (ultimo no-cero del array, por si el balance no tiene columnas mensuales)
  const vCierre=(arr)=>{let v=arr[11];if(!v){for(let i=11;i>=0;i--){if(arr[i]>0){v=arr[i];break;}}}return v||0;};
  DB.tact=vCierre(TACT25); DB.oblf=vCierre(OBLF25); DB.prov=vCierre(PROV25);
  DB.cxp=vCierre(CXP25); DB.tpas=vCierre(TPAS25); DB.pat=vCierre(PAT25);
  DB.imp=vCierre(IMP25); DB.obl=vCierre(OBL25);
  DB.disp=vCierre(DISP25); DB.cxc=vCierre(CXC25); DB.inv=vCierre(INV25);
  DB.ppe=vCierre(PPE25)||0;
  // Activo Corriente (prioridad: manual > NIIF vCierre > suma cuentas base)
  var AC=MANUAL_AC>0 ? MANUAL_AC : (vCierre(ACTC25)>0 ? vCierre(ACTC25) : (DB.disp+DB.cxc+DB.inv));
  DB.acte=AC;
  // Pasivo Corriente (prioridad: manual > NIIF vCierre > suma cuentas conocidas)
  var PC=MANUAL_PC>0 ? MANUAL_PC : (vCierre(PASC25)>0 ? vCierre(PASC25) : (DB.oblf+DB.prov+DB.cxp+DB.imp+DB.obl||1));
  PAC=PC;
  // Inventarios para Prueba Acida (prioridad: manual > Excel)
  var INV_ACID=MANUAL_INV>0 ? MANUAL_INV : DB.inv;
  var CVT=sum(CV25)+sum(CP25)||1;
  MG_UT=(IT>0?UT/IT*100:0);
  MG_UT24=(IT24>0?UT24/IT24*100:0);
  MGB=(IT>0?(IT-CVT)/IT*100:0);
  MGO=(IT>0?(IT-CVT-GT-GV)/IT*100:0);
  ROE=(DB.pat>0?UT/DB.pat*100:0).toFixed(1);
  ROA=(DB.tact>0?UT/DB.tact*100:0).toFixed(1);
  END=(DB.tact>0?DB.tpas/DB.tact*100:0).toFixed(1);
  LIQ=(PC>0?AC/PC:0).toFixed(2);
  ACID=(PC>0?(AC-INV_ACID)/PC:0).toFixed(2);
  LIQI=(PC>0?DB.disp/PC:0).toFixed(3);
  APLEV=(DB.pat>0?DB.tpas/DB.pat:0).toFixed(2);
  ROT=(DB.tact>0?IT/DB.tact:0).toFixed(2);
  ROTINV=(CVT>0?CVT/DB.inv:0).toFixed(2);
  DINV=(CVT>0?Math.round(DB.inv/CVT*365):0);
  MGC=(IT>0?(IT-GT-GV)/IT*100:0).toFixed(1);
  YOY=(IT24>0?(IT-IT24)/IT24*100:0).toFixed(1);
  YOUTIL=(UT24>0?(UT-UT24)/UT24*100:0).toFixed(0);
  YOGT=(GT24>0?(GT-GT24)/GT24*100:0).toFixed(1);
  YOGV=(GV24>0?(GV-GV24)/GV24*100:0).toFixed(1);
  DCXC=(IT>0?Math.round(DB.cxc/IT*365):0);
  DCXC24=(IT24>0&&BAL24.cxc>0?Math.round(BAL24.cxc/IT24*365):0);
}


/* -- FORMATTERS --------------------------------------------------------- */
const fm=v=>{const a=Math.abs(v);if(a>=1e6)return`$${(v/1e6).toFixed(2)}B`;if(a>=1e3)return`$${(v/1e3).toFixed(0)}MM`;return`$${v.toFixed(0)}M`;};
const fmm=v=>`$${(v/1e3).toFixed(1)}MM`;
const pct=v=>`${v}%`;

/* -- ANIMATED NUMBER ---------------------------------------------------- */
function AnimNum({target,prefix="",suffix="",decimals=0,duration=1200,trigger=true}){
  const [val,setVal]=useState(0);
  useEffect(()=>{
    if(!trigger)return;
    const start=Date.now(),from=0,to=parseFloat(target);
    const tick=()=>{
      const p=Math.min((Date.now()-start)/duration,1);
      const ease=1-Math.pow(1-p,3);
      setVal(from+(to-from)*ease);
      if(p<1)requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  },[target,trigger,duration]);
  return <span>{prefix}{val.toFixed(decimals)}{suffix}</span>;
}

/* -- TOOLTIP ------------------------------------------------------------ */
const PremTT=({active,payload,label})=>{
  if(!active||!(payload&&payload.length))return null;
  return(
    <div style={{background:"rgba(255,255,255,0.98)",border:`1px solid ${C.border}`,borderRadius:12,padding:"12px 16px",boxShadow:"0 20px 60px rgba(99,102,241,0.15)"}}>
      <div style={{fontFamily:C.fM,fontSize:9,color:C.inkD,marginBottom:8,letterSpacing:"0.1em"}}>{label}</div>
      {payload.map((p,i)=>(
        <div key={i} style={{display:"flex",justifyContent:"space-between",gap:20,marginBottom:3,alignItems:"center"}}>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <div style={{width:6,height:6,borderRadius:"50%",background:p.color||p.stroke,flexShrink:0}}/>
            <span style={{fontFamily:C.fB,fontSize:11,color:C.inkC,fontWeight:300}}>{p.name}</span>
          </div>
          <span style={{fontFamily:C.fM,fontSize:11,color:C.inkB,fontWeight:500}}>{typeof p.value==="number"?fm(p.value):p.value}</span>
        </div>
      ))}
    </div>
  );
};

/* -- GRADIENT DEFS ------------------------------------------------------ */
const GDefs=()=>(
  <defs>
    {[
      ["gInd","#6366F1","#8B5CF6"],["gEme","#059669","#10B981"],
      ["gRos","#F43F5E","#FB7185"],["gAmb","#D97706","#F59E0B"],
      ["gCya","#0891B2","#06B6D4"],
    ].map(([id,c1,c2])=>(
      <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={c1} stopOpacity={0.25}/>
        <stop offset="100%" stopColor={c2} stopOpacity={0}/>
      </linearGradient>
    ))}
  </defs>
);

/* -- GLASS CARD --------------------------------------------------------- */
const GCard=({children,style,glow,hover,delay})=>{
  const [vis,setVis]=useState(false);
  useEffect(()=>{
  const _s=style||{},_h=hover!==false,_d=delay||0;const t=setTimeout(()=>setVis(true),delay);return()=>clearTimeout(t);},[delay]);
  return(
    <div className={hover?"card-hover":""} style={{
      background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,
      boxShadow:`0 4px 24px rgba(99,102,241,0.06), 0 1px 4px rgba(0,0,0,0.04)`,
      overflow:"hidden",position:"relative",
      opacity:vis?1:0,transform:vis?"translateY(0)":"translateY(14px)",
      transition:"opacity 0.5s ease, transform 0.5s ease",
      ...(glow?{boxShadow:`0 4px 40px ${glow}20,0 1px 4px rgba(0,0,0,0.04)`}:{}),
      ...style
    }}>{children}</div>
  );
};

/* -- KPI CARD ----------------------------------------------------------- */
const KpiCard=({label,value,sub,delta,icon,grad,delay,suffix})=>{
  const _d=delay||0,_sx=suffix||"";
  const [vis,setVis]=useState(false);
  useEffect(()=>{const t=setTimeout(()=>setVis(true),delay);return()=>clearTimeout(t);},[delay]);
  const pos=delta?parseFloat(delta)>=0:null;
  return(
    <div className="card-hover" style={{
      background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:"24px",
      position:"relative",overflow:"hidden",
      boxShadow:"0 4px 24px rgba(99,102,241,0.06)",
      opacity:vis?1:0,transform:vis?"translateY(0)":"translateY(14px)",
      transition:"opacity 0.5s ease, transform 0.5s ease",
    }}>
      {/* gradient accent top */}
      <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:grad}}/>
      {/* bg orb */}
      <div style={{position:"absolute",top:-20,right:-20,width:80,height:80,borderRadius:"50%",background:grad,opacity:0.08}}/>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
        <div style={{fontFamily:C.fM,fontSize:10,letterSpacing:"0.12em",color:C.inkC,textTransform:"uppercase",fontWeight:500}}>{label}</div>
        <div style={{fontSize:18}}>{icon}</div>
      </div>
      <div style={{fontFamily:C.fD,fontSize:26,fontWeight:400,color:C.inkB,letterSpacing:"-0.01em",lineHeight:1}}>
        {vis?<AnimNum target={parseFloat(value.replace(/[^0-9.]/g,""))} prefix={value.match(/^\$/)?'$':''} suffix={value.includes("MM")?"MM":value.includes("B")?"B":value.includes("%")?"%":suffix} decimals={value.includes(".")?1:0}/>:value}
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:12}}>
        <div style={{fontFamily:C.fB,fontSize:11,color:C.inkD,fontWeight:300}}>
          {typeof sub==="string"&&sub.includes("⚠ Desfavorable")?
            <>{sub.replace(" · ⚠ Desfavorable","")} · <span style={{color:C.rose,fontWeight:600}}>⚠ Desfavorable</span></>
          :typeof sub==="string"&&sub.includes("✅ Favorable")?
            <>{sub.replace(" · ✅ Favorable","")} · <span style={{color:C.emerald,fontWeight:600}}>✅ Favorable</span></>
          :sub}
        </div>
        {delta!==null&&delta!==undefined&&(
          <div style={{display:"flex",alignItems:"center",gap:4,background:pos?C.emeraldL:C.roseL,padding:"3px 8px",borderRadius:20}}>
            <span style={{fontSize:9}}>{pos?"▲":"▼"}</span>
            <span style={{fontFamily:C.fM,fontSize:9,color:pos?C.emerald:C.rose,fontWeight:500}}>{Math.abs(parseFloat(delta)).toFixed(1)}%</span>
          </div>
        )}
      </div>
    </div>
  );
};

/* -- SECTION HEADER ----------------------------------------------------- */
const SH=({pill,title,sub})=>(
  <div style={{marginBottom:24}}>
    <div style={{display:"inline-flex",alignItems:"center",gap:6,background:C.indigoL,borderRadius:20,padding:"4px 12px",marginBottom:10}}>
      <div style={{width:5,height:5,borderRadius:"50%",background:C.indigo}}/>
      <span style={{fontFamily:C.fM,fontSize:8.5,letterSpacing:"0.14em",color:C.indigo,textTransform:"uppercase"}}>{pill}</span>
    </div>
    <div style={{fontFamily:C.fD,fontSize:22,fontWeight:400,color:C.inkB,letterSpacing:"-0.01em",lineHeight:1.25}}>{title}</div>
    {sub&&<div style={{fontFamily:C.fB,fontSize:12,color:C.inkD,marginTop:6,fontWeight:300}}>{sub}</div>}
  </div>
);

/* -- BADGE -------------------------------------------------------------- */
const Bdg=({label,ok,warn})=>{
  const c=ok===true?C.emerald:ok===false?C.rose:C.amber;
  const bg=ok===true?C.emeraldL:ok===false?C.roseL:C.amberL;
  return <span style={{fontFamily:C.fM,fontSize:8,letterSpacing:"0.12em",color:c,background:bg,padding:"3px 9px",borderRadius:20,display:"inline-block",textTransform:"uppercase"}}>{label}</span>;
};

/* -- NAV CONFIG --------------------------------------------------------- */
const TABS=[
  {id:"datos",       icon:"⊕", label:"Cargar Datos"},
  {id:"overview",    icon:"⬡", label:"Overview"},
  {id:"pyg",         icon:"◈", label:"P&G"},
  {id:"balance",     icon:"◎", label:"Balance"},
  {id:"ratios",      icon:"◆", label:"Indicadores"},
  {id:"riesgos",     icon:"◉", label:"Riesgos"},
  {id:"comparativo", icon:"⇄", label:"Comparativo"},
  {id:"ejecutivo",   icon:"▣", label:"Informe Ejecutivo"},
  {id:"ia",          icon:"✦", label:"Informe IA"},
];

/* ---------------------------------------------------------------------- */

const PE_VIEW=()=>{
  /* opciones de clasificación de costos */
  const OPCIONES=[
    {key:"gadm",  label:"G. Administración",   arr:GADM25, desc:"Nómina adm · Arrendamiento · Servicios",  color:"#E11D48"},
    {key:"gvta",  label:"G. Ventas",            arr:GVTA25, desc:"Comisiones · Fletes · Viáticos",          color:"#D97706"},
    {key:"gnoop", label:"G. No Operacionales",  arr:GNOOP25,desc:"Intereses · Dif. en cambio · Financieros",color:"#7C3AED"},
  ];

  /* Estado: qué líneas son CF y cuáles CV (por defecto: NIIF estándar) */
  const [cfKeys,setCfKeys]=useState(["gadm","gnoop"]);
  const [cvKeys,setCvKeys]=useState(["gvta"]);
  const [anio,setAnio]=useState("2026");

  /* toggle CF */
  const toggleCF=k=>{
    if(cfKeys.includes(k)){
      if(cfKeys.length===1) return; // mínimo 1 CF
      setCfKeys(cfKeys.filter(x=>x!==k));
    } else {
      setCfKeys([...cfKeys,k]);
      setCvKeys(cvKeys.filter(x=>x!==k));
    }
  };
  /* toggle CV */
  const toggleCV=k=>{
    if(cvKeys.includes(k)){
      if(cvKeys.length===1) return;
      setCvKeys(cvKeys.filter(x=>x!==k));
    } else {
      setCvKeys([...cvKeys,k]);
      setCfKeys(cfKeys.filter(x=>x!==k));
    }
  };

  const ING = anio==="2026"?ING25:ING24;
  const CF24_MES  = 978/12;
  const CV24_RATE = 2522/11980;

  /* calcular PE con clasificación actual */
  const calcPE=(i)=>{
    const ing = ING[i];
    const cf  = cfKeys.reduce((a,k)=>a+((OPCIONES.find(o=>o.key===k)||{})["arr"][i]||0),0);
    const cv  = cvKeys.reduce((a,k)=>a+((OPCIONES.find(o=>o.key===k)||{})["arr"][i]||0),0);
    const mc  = ing>0?(ing-cv)/ing:0;
    const pe  = mc>0?cf/mc:0;
    const ms  = ing>0?((ing-pe)/ing*100):0;
    const sobre=ing>=pe;
    const cob  =pe>0?ing/pe:0;
    const sem  =!sobre?"rojo":ms>=30?"verde":"amarillo";
    return {m:MES[i],ing,cf,cv,mc:mc*100,pe,ms,sobre,cob,sem};
  };

  const PE_DATA=MES.map((_,i)=>calcPE(i));
  const PE24=MES.map((_,i)=>{
    const ing=ING24[i],cf=CF24_MES,cv=ing*CV24_RATE;
    const mc=(ing-cv)/ing,pe=cf/mc,ms=(ing-pe)/ing*100;
    return {pe,ms,sobre:ing>=pe};
  });

  const msSob = PE_DATA.filter(d=>d.sobre).length;
  const msSob24= PE24.filter(d=>d.sobre).length;
  const msAvg  = PE_DATA.reduce((a,d)=>a+d.ms,0)/12;
  const peAvg  = PE_DATA.reduce((a,d)=>a+d.pe,0)/12;
  const mcAvg  = PE_DATA.reduce((a,d)=>a+d.mc,0)/12;
  const IT_sel = ING.reduce((a,v)=>a+v,0);

  const sC ={verde:C.emerald,amarillo:C.amber,rojo:C.rose};
  const sBg={verde:"rgba(5,150,105,0.06)",amarillo:"rgba(217,119,6,0.06)",rojo:"rgba(244,63,94,0.06)"};
  const sBr={verde:"rgba(5,150,105,0.2)",amarillo:"rgba(217,119,6,0.2)",rojo:"rgba(244,63,94,0.25)"};
  const sLb={verde:"✅ SOBRE PE",amarillo:"⚠ MARGEN BAJO",rojo:"🔴 BAJO PE"};

  const BtnToggle=({opt,isActive,type,onToggle})=>(
    <div
      onClick={()=>onToggle(opt.key)}
      style={{
        border:`2px solid ${isActive?opt.color:"rgba(99,102,241,0.15)"}`,
        borderRadius:10,padding:"12px 14px",cursor:"pointer",
        background:isActive?`${opt.color}10`:"rgba(249,250,255,0.6)",
        transition:"all 0.18s",userSelect:"none",position:"relative",
      }}
    >
      <div style={{position:"absolute",top:8,right:10,width:18,height:18,borderRadius:4,
        background:isActive?opt.color:"rgba(99,102,241,0.08)",
        display:"flex",alignItems:"center",justifyContent:"center",
        fontSize:10,color:"#fff",fontWeight:700,transition:"all 0.18s"}}>
        {isActive?"✓":""}
      </div>
      <div style={{fontFamily:C.fD,fontSize:12,fontWeight:700,color:isActive?opt.color:C.inkC,marginBottom:3}}>{opt.label}</div>
      <div style={{fontFamily:C.fB,fontSize:9.5,color:C.inkD,fontWeight:300,lineHeight:1.5}}>{opt.desc}</div>
      <div style={{marginTop:8,display:"inline-block",padding:"2px 8px",borderRadius:20,
        background:isActive?`${opt.color}15`:"transparent",
        fontFamily:C.fM,fontSize:8,letterSpacing:"0.8px",fontWeight:700,
        color:isActive?opt.color:C.inkD}}>
        {isActive?type.toUpperCase():"SIN CLASIFICAR"}
      </div>
    </div>
  );

  return(
    <div>

      {/* -- CONFIGURADOR DE CLASIFICACIÓN ------------------------------- */}
      <div style={{background:"#fff",border:"1px solid rgba(99,102,241,0.12)",borderRadius:14,padding:"20px 24px",marginBottom:20}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:10}}>
          <div>
            <div style={{fontFamily:C.fD,fontSize:15,fontWeight:700,color:C.inkB,marginBottom:2}}>⚙️ Clasificar Costos para el PE</div>
            <div style={{fontFamily:C.fB,fontSize:10.5,color:C.inkD,fontWeight:300}}>Selecciona qué líneas son <span style={{color:C.rose,fontWeight:600}}>Costos Fijos (CF)</span> y cuáles son <span style={{color:C.amber,fontWeight:600}}>Costos Variables (CV)</span></div>
          </div>
          {/* selector año */}
          <div style={{display:"flex",gap:4,background:"rgba(99,102,241,0.06)",borderRadius:8,padding:3}}>
            {["2026","2025"].map(y=>(
              <button key={y} onClick={()=>setAnio(y)} style={{
                padding:"6px 14px",borderRadius:6,border:"none",cursor:"pointer",
                background:anio===y?"#fff":"transparent",
                fontFamily:C.fM,fontSize:11,fontWeight:anio===y?700:400,
                color:anio===y?C.indigo:C.inkD,
                boxShadow:anio===y?"0 1px 4px rgba(99,102,241,0.15)":"none",
                transition:"all 0.15s",
              }}>{y}</button>
            ))}
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
          {/* CF panel */}
          <div>
            <div style={{fontFamily:C.fM,fontSize:8,letterSpacing:"0.15em",textTransform:"uppercase",color:C.rose,marginBottom:8,paddingLeft:2}}>
              Costos Fijos (CF) — no varían con las ventas
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {OPCIONES.map(opt=>(
                <BtnToggle key={opt.key} opt={opt} isActive={cfKeys.includes(opt.key)} type="CF" onToggle={toggleCF}/>
              ))}
            </div>
          </div>
          {/* CV panel */}
          <div>
            <div style={{fontFamily:C.fM,fontSize:8,letterSpacing:"0.15em",textTransform:"uppercase",color:C.amber,marginBottom:8,paddingLeft:2}}>
              Costos Variables (CV) — proporcionales a ingresos
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {OPCIONES.map(opt=>(
                <BtnToggle key={opt.key} opt={opt} isActive={cvKeys.includes(opt.key)} type="CV" onToggle={toggleCV}/>
              ))}
            </div>
          </div>
        </div>

        {/* Resumen configuración activa */}
        <div style={{background:"linear-gradient(135deg,rgba(99,102,241,0.04),rgba(5,150,105,0.03))",border:"1px solid rgba(99,102,241,0.08)",borderRadius:8,padding:"10px 14px",display:"flex",gap:20,flexWrap:"wrap",alignItems:"center"}}>
          <div style={{fontFamily:C.fM,fontSize:8.5,color:C.inkD,letterSpacing:"0.1em"}}>CONFIGURACIÓN ACTIVA:</div>
          <div style={{fontFamily:C.fB,fontSize:11,color:C.inkC}}>
            <span style={{color:C.rose,fontWeight:600}}>CF:</span> {cfKeys.map(k=>(OPCIONES.find(o=>o.key===k)||{})["label"]).join(" + ")}
          </div>
          <div style={{fontFamily:C.fB,fontSize:11,color:C.inkC}}>
            <span style={{color:C.amber,fontWeight:600}}>CV:</span> {cvKeys.map(k=>(OPCIONES.find(o=>o.key===k)||{})["label"]).join(" + ")}
          </div>
          <div style={{fontFamily:C.fM,fontSize:9,color:C.indigo,marginLeft:"auto",letterSpacing:"0.05em"}}>Año: {anio}</div>
        </div>
      </div>

      {/* -- 4 KPIs ------------------------------------------------------- */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
        {[
          {l:"PE Promedio Mensual",  v:fm(peAvg),       sub:"Ingresos mínimos requeridos", c:C.rose,   icon:"⚖️"},
          {l:"Margen Seg. Promedio", v:pct(msAvg.toFixed(1)), sub:"Distancia promedio al PE",   c:msAvg>=30?C.emerald:msAvg>=0?C.amber:C.rose, icon:"🛡"},
          {l:"MC% Promedio",         v:pct(mcAvg.toFixed(1)), sub:"Margen de contribución",     c:C.indigo, icon:"📐"},
          {l:"Meses sobre el PE",    v:`${msSob}/12`,   sub:`2025: ${msSob24}/12 meses`,  c:msSob>=10?C.emerald:msSob>=8?C.amber:C.rose, icon:"📅"},
        ].map((k,i)=>(
          <GCard key={i} delay={i*60}>
            <div style={{padding:"18px 20px"}}>
              <div style={{fontSize:20,marginBottom:6}}>{k.icon}</div>
              <div style={{fontFamily:C.fM,fontSize:8,color:C.inkD,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:4}}>{k.l}</div>
              <div style={{fontFamily:C.fD,fontSize:22,fontWeight:900,color:k.c,letterSpacing:"-0.5px",lineHeight:1,marginBottom:3}}>{k.v}</div>
              <div style={{fontFamily:C.fB,fontSize:10,color:C.inkD,fontWeight:300}}>{k.sub}</div>
            </div>
          </GCard>
        ))}
      </div>

      {/* -- SEMÁFORO 12 MESES -------------------------------------------- */}
      <GCard delay={0} hover={false} style={{marginBottom:16}}>
        <div style={{padding:"18px 22px 14px"}}>
          <div style={{fontFamily:C.fD,fontSize:15,fontWeight:700,color:C.inkB,marginBottom:4}}>Semáforo de Punto de Equilibrio {anio} — Vista Comité</div>
          <div style={{fontFamily:C.fB,fontSize:10,color:C.inkD,fontWeight:300,marginBottom:16}}>Verde: MS &gt;30% · Amarillo: MS 0–30% · Rojo: bajo el PE</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
            {PE_DATA.map((d,i)=>(
              <div key={i} style={{border:`1.5px solid ${sBr[d.sem]}`,background:sBg[d.sem],borderRadius:10,padding:"14px 16px",borderTop:`3px solid ${sC[d.sem]}`}}>
                <div style={{fontFamily:C.fM,fontSize:8.5,color:C.inkD,letterSpacing:"0.1em",marginBottom:6}}>{d.m.toUpperCase()}</div>
                <div style={{fontFamily:C.fD,fontSize:15,fontWeight:800,color:sC[d.sem],marginBottom:4}}>{fm(d.ing)}</div>
                <div style={{fontFamily:C.fM,fontSize:8,color:C.inkD,marginBottom:3}}>PE: <span style={{fontWeight:600,color:sC[d.sem]}}>{fm(d.pe)}</span></div>
                <div style={{fontFamily:C.fB,fontSize:9,color:C.inkD,fontWeight:300}}>MS: <span style={{fontWeight:600,color:sC[d.sem]}}>{d.ms.toFixed(1)}%</span></div>
                <div style={{marginTop:8,fontFamily:C.fM,fontSize:7,letterSpacing:"0.08em",color:sC[d.sem],fontWeight:700}}>{sLb[d.sem]}</div>
              </div>
            ))}
          </div>
        </div>
      </GCard>

      {/* -- GRÁFICA INGRESOS vs PE --------------------------------------- */}
      <GCard delay={0} hover={false} style={{marginBottom:16}}>
        <div style={{padding:"18px 22px 0"}}>
          <div style={{fontFamily:C.fD,fontSize:14,fontWeight:700,color:C.inkB,marginBottom:2}}>Ingresos vs Punto de Equilibrio — Mensual {anio}</div>
          <div style={{fontFamily:C.fB,fontSize:10,color:C.inkD,fontWeight:300,marginBottom:10}}>Barras = Ingresos · Línea roja = PE mínimo a alcanzar cada mes</div>
          <div style={{display:"flex",gap:16,marginBottom:10}}>
            {[{c:C.indigo,l:"Ingresos"},{c:C.rose,l:"Punto Equilibrio"}].map((k,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:5}}>
                <div style={{width:12,height:i===1?2:10,borderRadius:2,background:k.c}}/>
                <span style={{fontFamily:C.fB,fontSize:9,color:C.inkD,fontWeight:300}}>{k.l}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{height:260,padding:"0 22px 18px"}}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={PE_DATA} margin={{top:8,right:20,left:0,bottom:8}} barSize={24}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(99,102,241,0.07)"/>
              <XAxis dataKey="m" tick={{fontFamily:C.fM,fontSize:9,fill:C.inkD}}/>
              <YAxis tick={{fontFamily:C.fM,fontSize:9,fill:C.inkD}} tickFormatter={v=>v>=1000?`${(v/1000).toFixed(1)}B`:`${v}M`}/>
              <Tooltip contentStyle={{fontFamily:C.fB,fontSize:10,borderRadius:8,border:"1px solid rgba(99,102,241,0.1)",background:"rgba(255,255,255,0.97)"}}
                formatter={(val,name)=>[`${fm(val)} M`,name==="ing"?"Ingresos":"Punto Equilibrio"]}/>
              <Bar dataKey="ing" fill="rgba(99,102,241,0.72)" name="ing" radius={[4,4,0,0]}>
                {PE_DATA.map((d,i)=><Cell key={i} fill={sC[d.sem]} fillOpacity={0.7}/>)}
              </Bar>
              <Line type="monotone" dataKey="pe" stroke={C.rose} strokeWidth={2.5} dot={{r:4,fill:C.rose,stroke:"#fff",strokeWidth:2}} name="pe"/>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </GCard>

      {/* -- TABLA PARA COMITÉ -------------------------------------------- */}
      <GCard delay={0} hover={false} style={{marginBottom:16}}>
        <div style={{padding:"18px 22px 0"}}>
          <div style={{fontFamily:C.fD,fontSize:14,fontWeight:700,color:C.inkB,marginBottom:2}}>Tabla Punto de Equilibrio — Para Comité · {anio}</div>
          <div style={{fontFamily:C.fB,fontSize:10,color:C.inkD,fontWeight:300,marginBottom:14}}>Cifras en millones COP · CF: {cfKeys.map(k=>(OPCIONES.find(o=>o.key===k)||{})["label"]).join("+")} · CV: {cvKeys.map(k=>(OPCIONES.find(o=>o.key===k)||{})["label"]).join("+")}</div>
        </div>
        <div style={{overflowX:"auto",padding:"0 22px 18px"}}>
          <table style={{width:"100%",borderCollapse:"collapse",minWidth:720}}>
            <thead>
              <tr style={{background:"linear-gradient(135deg,#0F1117,#1E1B4B)"}}>
                {["MES","CF ($M)","CV ($M)","MC%","PE ($M)","INGRESOS","M.SEG %","COBERT.","PE 2025","ESTADO"].map((h,j)=>(
                  <th key={j} style={{padding:"10px 12px",textAlign:j===0?"left":"right",color:"rgba(165,180,252,0.7)",fontFamily:C.fM,fontSize:8,letterSpacing:"1px",whiteSpace:"nowrap"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PE_DATA.map((d,i)=>(
                <tr key={i} style={{borderBottom:"1px solid rgba(99,102,241,0.06)",background:i%2===0?"rgba(99,102,241,0.015)":"transparent"}}>
                  <td style={{padding:"9px 12px",fontFamily:C.fM,fontSize:10,color:C.inkB,fontWeight:600}}>{d.m}</td>
                  <td style={{padding:"9px 12px",textAlign:"right",fontFamily:C.fM,fontSize:10,color:C.rose}}>{d.cf.toFixed(1)}</td>
                  <td style={{padding:"9px 12px",textAlign:"right",fontFamily:C.fM,fontSize:10,color:C.amber}}>{d.cv.toFixed(1)}</td>
                  <td style={{padding:"9px 12px",textAlign:"right",fontFamily:C.fM,fontSize:10,color:C.indigo,fontWeight:600}}>{d.mc.toFixed(1)}%</td>
                  <td style={{padding:"9px 12px",textAlign:"right",fontFamily:C.fM,fontSize:10,color:C.rose,fontWeight:600}}>{fm(d.pe)}</td>
                  <td style={{padding:"9px 12px",textAlign:"right",fontFamily:C.fM,fontSize:10,color:C.indigo,fontWeight:600}}>{fm(d.ing)}</td>
                  <td style={{padding:"9px 12px",textAlign:"right",fontFamily:C.fM,fontSize:11,fontWeight:700,color:sC[d.sem]}}>{d.ms.toFixed(1)}%</td>
                  <td style={{padding:"9px 12px",textAlign:"right",fontFamily:C.fM,fontSize:10,fontWeight:600,color:d.cob>=2?C.emerald:d.cob>=1?C.amber:C.rose}}>{d.cob.toFixed(2)}x</td>
                  <td style={{padding:"9px 12px",textAlign:"right",fontFamily:C.fM,fontSize:9,color:C.inkD}}>{fm(PE24[i].pe)}</td>
                  <td style={{padding:"9px 12px",textAlign:"right"}}>
                    <span style={{display:"inline-block",padding:"3px 9px",borderRadius:20,background:sBg[d.sem],border:`1px solid ${sBr[d.sem]}`,fontFamily:C.fM,fontSize:7.5,fontWeight:700,color:sC[d.sem],whiteSpace:"nowrap"}}>{sLb[d.sem]}</span>
                  </td>
                </tr>
              ))}
              <tr style={{background:"rgba(99,102,241,0.05)",borderTop:"2px solid rgba(99,102,241,0.15)"}}>
                <td style={{padding:"11px 12px",fontFamily:C.fD,fontSize:11,fontWeight:700,color:C.inkB}}>TOTAL / PROM.</td>
                <td style={{padding:"11px 12px",textAlign:"right",fontFamily:C.fD,fontSize:11,fontWeight:700,color:C.rose}}>{PE_DATA.reduce((a,d)=>a+d.cf,0).toFixed(0)}</td>
                <td style={{padding:"11px 12px",textAlign:"right",fontFamily:C.fD,fontSize:11,fontWeight:700,color:C.amber}}>{PE_DATA.reduce((a,d)=>a+d.cv,0).toFixed(0)}</td>
                <td style={{padding:"11px 12px",textAlign:"right",fontFamily:C.fD,fontSize:11,fontWeight:700,color:C.indigo}}>{mcAvg.toFixed(1)}%</td>
                <td style={{padding:"11px 12px",textAlign:"right",fontFamily:C.fD,fontSize:11,fontWeight:700,color:C.rose}}>{fm(peAvg)}</td>
                <td style={{padding:"11px 12px",textAlign:"right",fontFamily:C.fD,fontSize:11,fontWeight:700,color:C.indigo}}>{fm(IT_sel/12)}</td>
                <td style={{padding:"11px 12px",textAlign:"right",fontFamily:C.fD,fontSize:11,fontWeight:700,color:msAvg>=30?C.emerald:C.amber}}>{msAvg.toFixed(1)}%</td>
                <td style={{padding:"11px 12px",textAlign:"right",fontFamily:C.fD,fontSize:11,fontWeight:700,color:C.emerald}}>{(PE_DATA.reduce((a,d)=>a+d.cob,0)/12).toFixed(2)}x</td>
                <td style={{padding:"11px 12px",textAlign:"right",fontFamily:C.fD,fontSize:10,color:C.inkD}}>{fm(PE24.reduce((a,d)=>a+d.pe,0)/12)}</td>
                <td style={{padding:"11px 12px",textAlign:"right",fontFamily:C.fD,fontSize:10,fontWeight:700,color:msSob>=10?C.emerald:C.amber}}>{msSob}/12 sobre PE</td>
              </tr>
            </tbody>
          </table>
        </div>
      </GCard>

      {/* -- MARGEN SEGURIDAD CHART --------------------------------------- */}
      <GCard delay={0} hover={false} style={{marginBottom:16}}>
        <div style={{padding:"18px 22px 0"}}>
          <div style={{fontFamily:C.fD,fontSize:14,fontWeight:700,color:C.inkB,marginBottom:2}}>Margen de Seguridad Mensual (%) · {anio} vs 2025</div>
          <div style={{fontFamily:C.fB,fontSize:10,color:C.inkD,fontWeight:300,marginBottom:14}}>Meta comité: &gt;30% todos los meses · Línea verde = objetivo</div>
        </div>
        <div style={{height:220,padding:"0 22px 18px"}}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={MES.map((m,i)=>({m,ms25:PE_DATA[i].ms,ms24:PE24[i].ms,target:30}))} margin={{top:8,right:20,left:0,bottom:8}}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(99,102,241,0.07)"/>
              <XAxis dataKey="m" tick={{fontFamily:C.fM,fontSize:9,fill:C.inkD}}/>
              <YAxis tick={{fontFamily:C.fM,fontSize:9,fill:C.inkD}} tickFormatter={v=>`${v.toFixed(0)}%`}/>
              <Tooltip contentStyle={{fontFamily:C.fB,fontSize:10,borderRadius:8,border:"1px solid rgba(99,102,241,0.1)"}}
                formatter={(v,n)=>[`${Number(v).toFixed(1)}%`,n==="ms25"?`M.Seg. ${anio}`:n==="ms24"?"M.Seg. 2025":"Meta ≥30%"]}/>
              <Bar dataKey="ms25" name="ms25" radius={[4,4,0,0]}>
                {PE_DATA.map((d,i)=><Cell key={i} fill={sC[d.sem]} fillOpacity={0.7}/>)}
              </Bar>
              <Line type="monotone" dataKey="ms24" stroke={C.amber} strokeWidth={1.5} strokeDasharray="4 3" dot={{r:3,fill:C.amber}} name="ms24"/>
              <Line type="monotone" dataKey="target" stroke={C.emerald} strokeWidth={1.5} strokeDasharray="6 3" dot={false} name="target"/>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </GCard>

      {/* -- CONCLUSIÓN COMITÉ -------------------------------------------- */}
      <div style={{background:"linear-gradient(135deg,rgba(15,17,23,0.96),rgba(30,27,75,0.96))",borderRadius:12,padding:"22px 26px",color:"#fff"}}>
        <div style={{fontFamily:C.fD,fontSize:14,fontWeight:700,color:"#fff",marginBottom:14}}>📋 Conclusión para Comité · {anio}</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
          <div>
            <div style={{fontFamily:C.fM,fontSize:7.5,letterSpacing:"0.15em",color:"rgba(165,180,252,0.5)",marginBottom:8,textTransform:"uppercase"}}>Hallazgos clave</div>
            <ul style={{fontFamily:C.fB,fontSize:10.5,color:"rgba(255,255,255,0.8)",fontWeight:300,lineHeight:1.9,paddingLeft:16}}>
              <li>MEQ operó <strong style={{color:"#6EE7B7"}}>{msSob} de 12 meses</strong> sobre el punto de equilibrio en {anio}</li>
              <li>PE promedio mensual: <strong style={{color:"#F87171"}}>{fm(peAvg)}</strong></li>
              <li>Margen de seguridad: <strong style={{color:msAvg>=30?"#6EE7B7":"#FCD34D"}}>{msAvg.toFixed(1)}%</strong> {msAvg>=30?"✅ cumple meta":"⚠ bajo meta 30%"}</li>
              <li>MC% promedio: <strong style={{color:"#A5B4FC"}}>{mcAvg.toFixed(1)}%</strong></li>
              <li>Meses bajo PE: {PE_DATA.filter(d=>d.sem==="rojo").length===0?"Ninguno 🟢":PE_DATA.filter(d=>d.sem==="rojo").map(d=>d.m).join(", ")+" 🔴"}</li>
            </ul>
          </div>
          <div>
            <div style={{fontFamily:C.fM,fontSize:7.5,letterSpacing:"0.15em",color:"rgba(165,180,252,0.5)",marginBottom:8,textTransform:"uppercase"}}>Recomendaciones</div>
            <ul style={{fontFamily:C.fB,fontSize:10.5,color:"rgba(255,255,255,0.8)",fontWeight:300,lineHeight:1.9,paddingLeft:16}}>
              <li>Presentar el PE mensual como indicador fijo de cada comité</li>
              <li>Ene–Feb con margen ajustado: reforzar pedidos en cartera para Q1</li>
              <li>La palanca es el <strong style={{color:"#A5B4FC"}}>volumen de ventas</strong>, no el recorte de costos</li>
              <li>Meta 2026: PE &lt;$120M mensual · MS &gt;35% todo el año</li>
            </ul>
          </div>
        </div>
      </div>

    </div>
  );
};
export default function TableroFinanciero(){
  const [tab,setTab]=useState("overview");
  const [empresa,setEmpresa]=useState("");
  const [nit,setNit]=useState("");
  const [sector,setSector]=useState("");
  const [periodo,setPeriodo]=useState("Enero – Diciembre 2026");
  const [manualAC,setManualAC]=useState("");    // Activo Corriente Año Activo
  const [manualPC,setManualPC]=useState("");    // Pasivo Corriente Año Activo
  const [manualINV,setManualINV]=useState("");  // Inventarios Año Activo
  const [manualAC24,setManualAC24]=useState(""); // Activo Corriente Año Anterior
  const [manualPC24,setManualPC24]=useState(""); // Pasivo Corriente Año Anterior
  const [manualINV24,setManualINV24]=useState(""); // Inventarios Año Anterior
  const [ai,setAi]=useState("");
  const [loading,setLoading]=useState(false);
  const [done,setDone]=useState(false);
  const [hovTab,setHovTab]=useState(null);
  const key=useRef(0);

  /* == SECTORES ================================================== */
  const SECTORES={
    med:{grupo:"Salud",label:"Distribucion Equipos Medicos",
      desc:"CIIU 4659 - Comercio al por mayor otros equipos NCP",
      ciuu:["4659","4649","4653","4651","4652","4654","4655","4656","4657","4658"],
      roe:[14,35],roa:[8,18],end:[30,54],liq:[1.2,2.5],mg:[8,20],yoy:[5,15],dcxc:[45,75],rot:[0.8,1.5],invact:38.5,
      benchmarks:{"Sueldos y Prestaciones":[4.0,8.0],"Arrendamientos":[1.5,3.5],"Gastos Bancarios":[0.5,1.5]}},
    comercio:{grupo:"Comercio",label:"Comercio al por Mayor",
      desc:"CIIU 46xx - Comercio al por mayor no especializado",
      ciuu:["4690","4631","4632","4633","4634","4635","4636","4637","4638","4639","4641","4642","4643","4644","4645","4646","4647","4648","4661","4662","4663","4664","4665","4669"],
      roe:[12,28],roa:[6,15],end:[35,60],liq:[1.1,2.2],mg:[5,15],yoy:[4,12],dcxc:[50,85],rot:[0.7,1.3],invact:42.0,
      benchmarks:{"Sueldos y Prestaciones":[5.0,10.0],"Arrendamientos":[2.0,5.0],"Gastos Bancarios":[0.5,1.5]}},
    retail:{grupo:"Retail",label:"Comercio al por Menor",
      desc:"CIIU 47xx - Comercio al por menor",
      ciuu:["4711","4712","4719","4721","4722","4723","4724","4725","4726","4729","4731","4732","4741","4742","4751","4752","4753","4754","4755","4759","4761","4762","4763","4771","4772","4773","4774","4775","4781","4789","4791","4799"],
      roe:[10,25],roa:[5,14],end:[40,65],liq:[1.0,2.0],mg:[3,10],yoy:[3,10],dcxc:[20,45],rot:[1.5,3.0],invact:35.0,
      benchmarks:{"Sueldos y Prestaciones":[8.0,15.0],"Arrendamientos":[3.0,8.0],"Gastos Bancarios":[0.5,1.5]}},
    tech:{grupo:"Tecnologia",label:"Software y TI",
      desc:"CIIU 62xx - Actividades de programacion y consultoria informatica",
      ciuu:["6201","6202","6209","6311","6312","6391","6399","5811","5812","5820"],
      roe:[18,45],roa:[12,30],end:[20,45],liq:[1.5,3.5],mg:[15,35],yoy:[8,25],dcxc:[30,60],rot:[0.6,1.2],invact:5.0,
      benchmarks:{"Sueldos y Prestaciones":[25.0,50.0],"Arrendamientos":[2.0,6.0],"Gastos Bancarios":[0.3,1.0]}},
    serviprof:{grupo:"Servicios",label:"Consultoria y Servicios Prof.",
      desc:"CIIU 69xx-70xx - Actividades juridicas, contables, consultoria",
      ciuu:["6910","6920","7010","7020","7110","7120","7210","7220","7310","7320","7410","7420","7490"],
      roe:[20,50],roa:[15,35],end:[15,40],liq:[1.5,4.0],mg:[20,45],yoy:[5,20],dcxc:[25,55],rot:[0.5,1.0],invact:3.0,
      benchmarks:{"Sueldos y Prestaciones":[35.0,65.0],"Arrendamientos":[2.0,7.0],"Gastos Bancarios":[0.3,0.8]}},
    manufactura:{grupo:"Manufactura",label:"Industria Manufacturera",
      desc:"CIIU 10xx-33xx - Industrias manufactureras",
      ciuu:["1011","1012","1020","1040","1051","1061","1071","1081","1090","2011","2012","2021","2030","2100","2211","2221","2310","2410","2420","2511","2591","2610","2620","2630","2710","2720","2750","2811","2821","2910","2920","3011","3020","3030","3110","3120","3130","3190","3211","3212","3220","3240","3250","3290","3311","3312","3319","3320"],
      roe:[10,22],roa:[6,14],end:[35,60],liq:[1.2,2.5],mg:[5,15],yoy:[3,10],dcxc:[40,70],rot:[0.6,1.2],invact:45.0,
      benchmarks:{"Sueldos y Prestaciones":[12.0,22.0],"Arrendamientos":[1.0,3.0],"Gastos Bancarios":[0.5,1.5]}},
    construccion:{grupo:"Construccion",label:"Construccion",
      desc:"CIIU 41xx-43xx - Construccion de edificios y obras civiles",
      ciuu:["4111","4112","4210","4220","4290","4311","4312","4321","4322","4329","4330","4390"],
      roe:[8,20],roa:[4,12],end:[40,65],liq:[1.1,2.2],mg:[4,12],yoy:[3,12],dcxc:[50,90],rot:[0.5,1.1],invact:30.0,
      benchmarks:{"Sueldos y Prestaciones":[15.0,28.0],"Arrendamientos":[1.0,3.0],"Gastos Bancarios":[0.5,1.5]}},
    salud:{grupo:"Salud",label:"Actividades de Salud",
      desc:"CIIU 86xx-87xx - Hospitales, clinicas y actividades de salud",
      ciuu:["8610","8621","8622","8691","8692","8699","8710","8720","8730","8790"],
      roe:[12,30],roa:[7,18],end:[25,50],liq:[1.2,2.8],mg:[6,18],yoy:[4,12],dcxc:[35,65],rot:[0.7,1.4],invact:20.0,
      benchmarks:{"Sueldos y Prestaciones":[30.0,55.0],"Arrendamientos":[2.0,6.0],"Gastos Bancarios":[0.5,1.2]}},
    transporte:{grupo:"Transporte",label:"Transporte y Logistica",
      desc:"CIIU 49xx-53xx - Transporte terrestre y almacenamiento",
      ciuu:["4911","4912","4921","4922","4923","5011","5012","5111","5112","5210","5221","5222","5223","5310","5320"],
      roe:[10,24],roa:[5,14],end:[35,60],liq:[1.0,2.0],mg:[5,14],yoy:[3,10],dcxc:[30,60],rot:[0.8,1.5],invact:10.0,
      benchmarks:{"Sueldos y Prestaciones":[20.0,40.0],"Arrendamientos":[2.0,5.0],"Gastos Bancarios":[0.5,1.5]}},
    agro:{grupo:"Agropecuario",label:"Agricultura y Ganaderia",
      desc:"CIIU 01xx-03xx - Agricultura, ganaderia, silvicultura y pesca",
      ciuu:["0111","0112","0113","0114","0119","0121","0122","0129","0130","0141","0149","0150","0161","0170","0210","0220","0311","0312","0321","0322"],
      roe:[8,18],roa:[4,10],end:[30,55],liq:[1.0,2.2],mg:[5,12],yoy:[2,8],dcxc:[30,60],rot:[0.5,1.0],invact:50.0,
      benchmarks:{"Sueldos y Prestaciones":[10.0,22.0],"Arrendamientos":[0.5,2.0],"Gastos Bancarios":[0.5,1.5]}},
    educacion:{grupo:"Educacion",label:"Educacion",
      desc:"CIIU 85xx - Educacion preescolar, basica, media y superior",
      ciuu:["8511","8512","8521","8522","8530","8541","8542","8549","8551","8552","8559","8560"],
      roe:[10,22],roa:[6,15],end:[25,50],liq:[1.2,2.5],mg:[8,20],yoy:[3,10],dcxc:[20,45],rot:[0.5,1.0],invact:8.0,
      benchmarks:{"Sueldos y Prestaciones":[35.0,60.0],"Arrendamientos":[3.0,8.0],"Gastos Bancarios":[0.3,0.8]}},
    inmobiliario:{grupo:"Inmobiliario",label:"Actividades Inmobiliarias",
      desc:"CIIU 68xx - Actividades inmobiliarias",
      ciuu:["6810","6820"],
      roe:[6,16],roa:[3,9],end:[30,60],liq:[0.8,1.8],mg:[20,45],yoy:[2,8],dcxc:[20,40],rot:[0.1,0.4],invact:5.0,
      benchmarks:{"Sueldos y Prestaciones":[5.0,12.0],"Arrendamientos":[1.0,3.0],"Gastos Bancarios":[0.5,1.5]}},
    hosteleria:{grupo:"Hosteleria",label:"Hoteles y Restaurantes",
      desc:"CIIU 55xx-56xx - Alojamiento y servicio de comidas",
      ciuu:["5511","5512","5519","5520","5530","5590","5611","5612","5619","5621","5629","5630"],
      roe:[8,20],roa:[4,12],end:[40,65],liq:[0.9,1.8],mg:[5,15],yoy:[4,12],dcxc:[10,25],rot:[1.0,2.5],invact:15.0,
      benchmarks:{"Sueldos y Prestaciones":[18.0,35.0],"Arrendamientos":[5.0,12.0],"Gastos Bancarios":[0.5,1.5]}},
  };
  const ciuuToSector=Object.entries(SECTORES).reduce((acc,[k,v])=>{
    (v.ciuu||[]).forEach(code=>{acc[code]=k;});
    return acc;
  },{});
  const bmarks=(SECTORES[sector]&&SECTORES[sector].benchmarks)||SECTORES.med.benchmarks;
  const SEC=SECTORES[sector]||SECTORES.med;
  const B={roe:SEC.roe||[14,35],roa:SEC.roa||[8,18],end:SEC.end||[30,54],liq:SEC.liq||[1.2,2.5],mg:SEC.mg||[8,20],yoy:SEC.yoy||[5,15],dcxc:SEC.dcxc||[45,75],rot:SEC.rot||[0.8,1.5],invact:SEC.invact||38.5};

  const changeTab=useCallback((t)=>{setTab(t);key.current++;},[]);

  const runAI=async()=>{
    setLoading(true);setAi("");setDone(false);
    const p=`Eres una contadora financiera con 20 años de experiencia en empresas colombianas de tecnología médica. Redacta un informe ejecutivo financiero completo, en español, tono formal y directivo, para junta directiva de ${empresa}, período ${periodo}.

DATOS REALES (millones COP, fuente Sistema Contable):
• Ingresos 2026: $${IT.toFixed(0)}M | 2025: $${IT24.toFixed(0)}M | Crecimiento: +${YOY}%
• Resultado 2026: $${UT.toFixed(0)}M | 2025: $${UT24}M | Variación: +${YOUTIL}%
• Gastos Adm: $${GT.toFixed(0)}M (${(GT/IT*100).toFixed(1)}%) | Ventas: $${GV.toFixed(0)}M (${(GV/IT*100).toFixed(1)}%)
• Total Activos Dic: $${DB.tact.toFixed(0)}M (+${((DB.tact-BAL24.tact)/BAL24.tact*100).toFixed(1)}% vs 2025)
• Disponible: $${DB.disp.toFixed(0)}M | CxC: $${DB.cxc.toFixed(0)}M | Inventario: ${fm(DB.inv)}${BAL24.inv>0?" (+"+((DB.inv-BAL24.inv)/BAL24.inv*100).toFixed(1)+"%)":""}
• Total Pasivos: $${DB.tpas.toFixed(0)}M | Patrimonio: $${DB.pat.toFixed(0)}M (+${((DB.pat-BAL24.pat)/BAL24.pat*100).toFixed(1)}%)
• ROE: ${ROE}% | ROA: ${ROA}% | Endeudamiento: ${END}% | Días Cartera: ${DCXC}d (2025: ${DCXC24}d)
• Razón Corriente: ${LIQ} | Prueba Ácida: ${ACID} | Liquidez Inmediata: ${LIQI}

Secciones numeradas con romanos (I. II. III. etc.):
I. SÍNTESIS EJECUTIVA
II. ANÁLISIS DE INGRESOS Y CRECIMIENTO
III. ESTRUCTURA DE COSTOS Y GASTOS
IV. POSICIÓN DE LIQUIDEZ
V. GESTIÓN DE INVENTARIOS — ALERTA CRÍTICA
VI. CARTERA Y CICLO DE COBRO
VII. ESTRUCTURA FINANCIERA Y DEUDA
VIII. INDICADORES DE RENTABILIDAD
IX. RIESGOS Y PLAN DE MITIGACIÓN
X. RECOMENDACIONES ESTRATÉGICAS
XI. PERSPECTIVAS Q1 2026

3-4 párrafos por sección, cifras concretas, lenguaje directivo-financiero.`;
    try{
      const r=await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:"claude-sonnet-4-5",max_tokens:4000,messages:[{role:"user",content:p}]})});
      const d=await r.json();
      setAi((d.content&&d.content.map)(b=>b.text||"").join("")||"Error.");
    }catch(e){setAi("Error de conexión.");}
    setLoading(false);setDone(true);
  };

  /* ----------- OVERVIEW ----------------------------------------------- */
  const Overview=()=>(
    <div key={key.current}>
      {/* HERO */}
      <div style={{background:`linear-gradient(135deg, #0F1117 0%, #1E1B4B 50%, #1E3A5F 100%)`,borderRadius:20,padding:"40px 44px",marginBottom:20,position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-60,right:-60,width:260,height:260,borderRadius:"50%",background:"radial-gradient(circle,rgba(99,102,241,0.3),transparent 70%)"}}/>
        <div style={{position:"absolute",bottom:-40,left:"30%",width:180,height:180,borderRadius:"50%",background:"radial-gradient(circle,rgba(16,185,129,0.2),transparent 70%)"}}/>
        <div style={{position:"absolute",top:"30%",right:"20%",width:120,height:120,borderRadius:"50%",background:"radial-gradient(circle,rgba(244,63,94,0.15),transparent 70%)"}}/>
        <div style={{position:"relative"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:32}}>
            <div>
              <div style={{display:"inline-flex",alignItems:"center",gap:6,background:"rgba(99,102,241,0.2)",borderRadius:20,padding:"4px 12px",marginBottom:14,backdropFilter:"blur(10px)"}}>
                <div style={{width:5,height:5,borderRadius:"50%",background:"#6366F1",animation:"pulse2 2s infinite"}}/>
                <span style={{fontFamily:C.fM,fontSize:8.5,letterSpacing:"0.14em",color:"#A5B4FC",textTransform:"uppercase"}}>Informe Ejecutivo</span>
              </div>
              <div style={{fontFamily:C.fD,fontSize:32,fontWeight:800,color:"#FFFFFF",letterSpacing:"-0.03em",lineHeight:1.1}}>{empresa}</div>
              <div style={{fontFamily:C.fB,fontSize:13,color:"rgba(255,255,255,0.45)",marginTop:6,fontWeight:300}}>Enero – Diciembre 2026 · Sistema Contable · Estado de Situación Financiera + P&G Real</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontFamily:C.fM,fontSize:8,color:"rgba(255,255,255,0.3)",letterSpacing:"0.1em",marginBottom:4}}>SALUD FINANCIERA</div>
              <div style={{fontFamily:C.fD,fontSize:40,fontWeight:800,color:"#A5B4FC"}}>61<span style={{fontSize:20,color:"rgba(165,180,252,0.5)"}}>/100</span></div>
              <div style={{fontFamily:C.fB,fontSize:10,color:"rgba(255,255,255,0.35)"}}>Posición Moderada</div>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:1}}>
            {[
              {l:"Ingresos 2026",    v:fm(IT),      d:YOY,    c:"#A5B4FC"},
              {l:"Resultado Ejrc.",  v:fm(UT),      d:YOUTIL, c:"#6EE7B7"},
              {l:"Total Activos",    v:fm(DB.tact), d:BAL24.tact>0?((DB.tact-BAL24.tact)/BAL24.tact*100).toFixed(1)+"":null, c:"#FFFFFF"},
              {l:"Patrimonio",       v:fm(DB.pat),  d:BAL24.pat>0?((DB.pat-BAL24.pat)/BAL24.pat*100).toFixed(1)+"":null, c:"#6EE7B7"},
              {l:"Endeudamiento",    v:pct(END),    d:null,   c:parseFloat(END)<50?"#6EE7B7":"#FCA5A5"},
            ].map((k,i)=>(
              <div key={i} style={{background:"rgba(255,255,255,0.06)",backdropFilter:"blur(10px)",borderRadius:12,padding:"18px 20px",border:"1px solid rgba(255,255,255,0.1)"}}>
                <div style={{fontFamily:C.fM,fontSize:7.5,color:"rgba(255,255,255,0.35)",letterSpacing:"0.14em",marginBottom:8}}>{k.l}</div>
                <div style={{fontFamily:C.fD,fontSize:20,fontWeight:700,color:k.c,letterSpacing:"-0.02em"}}>{k.v}</div>
                {k.d&&<div style={{fontFamily:C.fM,fontSize:9,color:parseFloat(k.d)>=0?"#6EE7B7":"#FCA5A5",marginTop:5}}>▲ {k.d}%</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* KPI CARDS */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
        <KpiCard label="Ingresos 2026"    value={fm(IT)}      sub="Revenue anual total"   delta={YOY}    icon="💰" grad={C.indigoG} delay={0}/>
        <KpiCard label="Resultado Ejrc."  value={fm(UT)}      sub="Balance Dic 2026"      delta={YOUTIL}  icon="📈" grad={C.emeraldG} delay={60}/>
        <KpiCard label="Total Activos"    value={fm(DB.tact)} sub="Dic 2026"              delta={BAL24.tact>0?((DB.tact-BAL24.tact)/BAL24.tact*100).toFixed(1):null} icon="🏦" grad={C.cyanG} delay={120}/>
        <KpiCard label="Patrimonio"       value={fm(DB.pat)}  sub={BAL24.pat>0?"vs "+fm(BAL24.pat)+" en 2025":""}   delta={BAL24.pat>0?((DB.pat-BAL24.pat)/BAL24.pat*100).toFixed(1):null} icon="🎯" grad={C.amberG} delay={180}/>
      </div>

      {/* MAIN CHARTS ROW */}
      <div style={{display:"grid",gridTemplateColumns:"3fr 2fr",gap:12,marginBottom:12}}>
        <GCard delay={200}>
          <div style={{padding:"22px 24px 6px"}}>
            <div style={{fontFamily:C.fM,fontSize:8.5,letterSpacing:"0.12em",color:C.inkD,textTransform:"uppercase",marginBottom:4}}>Evolución mensual de ingresos 2026</div>
            <div style={{fontFamily:C.fD,fontSize:16,fontWeight:700,color:C.inkB,letterSpacing:"-0.01em"}}>Ingresos vs Gastos vs Resultado</div>
          </div>
          <ResponsiveContainer width="100%" height={210}>
            <ComposedChart data={MES.map((m,i)=>({m,ing:ING25[i],gadm:GADM25[i],gvta:GVTA25[i],util:UTIL25[i]}))} margin={{top:8,right:20,left:0,bottom:8}}>
              <GDefs/>
              <CartesianGrid strokeDasharray="2 6" stroke="rgba(99,102,241,0.06)" vertical={false}/>
              <XAxis dataKey="m" tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false}/>
              <YAxis tickFormatter={v=>`$${(v/1e3).toFixed(0)}MM`} tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false} width={50}/>
              <Tooltip content={<PremTT/>}/>
              <Area type="monotone" dataKey="ing"  name="Ingresos"   stroke="#6366F1" strokeWidth={2} fill="url(#gInd)"/>
              <Bar dataKey="gadm" name="G. Adm."   fill="#D97706" fillOpacity={0.65} radius={[3,3,0,0]} barSize={7}/>
              <Bar dataKey="gvta" name="G. Ventas" fill="#F43F5E" fillOpacity={0.6}  radius={[3,3,0,0]} barSize={7}/>
              <Line type="monotone" dataKey="util" name="Resultado"  stroke="#059669" strokeWidth={2.5} dot={{r:3,fill:"#059669",strokeWidth:0}}/>
            </ComposedChart>
          </ResponsiveContainer>
        </GCard>

        <GCard delay={250}>
          <div style={{padding:"22px 24px 8px"}}>
            <div style={{fontFamily:C.fM,fontSize:8.5,letterSpacing:"0.12em",color:C.inkD,textTransform:"uppercase",marginBottom:4}}>Composición de activos</div>
            <div style={{fontFamily:C.fD,fontSize:16,fontWeight:700,color:C.inkB,letterSpacing:"-0.01em"}}>Estructura Diciembre 2026</div>
          </div>
          <ResponsiveContainer width="100%" height={150}>
            <PieChart>
              <Pie data={[{n:"Inventario",v:DB.inv},{n:"CxC",v:DB.cxc},{n:"PPyE",v:DB.ppe},{n:"Disp.",v:DB.disp}]} cx="50%" cy="50%" innerRadius={44} outerRadius={62} paddingAngle={3} dataKey="v" nameKey="n">
                {["#F43F5E","#D97706","#6366F1","#059669"].map((c,i)=><Cell key={i} fill={c}/>)}
              </Pie>
              <Tooltip formatter={v=>[fm(v)]} contentStyle={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:10,fontFamily:C.fM,fontSize:10}}/>
            </PieChart>
          </ResponsiveContainer>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,padding:"4px 24px 20px"}}>
            {[["#F43F5E","Inventario","66%"],["#D97706","CxC","24.3%"],["#6366F1","PPyE","7.6%"],["#059669","Disponible","1.8%"]].map(([c,l,p])=>(
              <div key={l} style={{display:"flex",alignItems:"center",gap:7}}>
                <div style={{width:7,height:7,borderRadius:2,background:c,flexShrink:0}}/>
                <span style={{fontFamily:C.fB,fontSize:10.5,color:C.inkC,fontWeight:300}}>{l} <span style={{color:C.inkD}}>{p}</span></span>
              </div>
            ))}
          </div>
        </GCard>
      </div>

      {/* SECOND ROW */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <GCard delay={300}>
          <div style={{padding:"22px 24px 6px"}}>
            <div style={{fontFamily:C.fM,fontSize:8.5,letterSpacing:"0.12em",color:C.inkD,textTransform:"uppercase",marginBottom:4}}>Estructura financiera</div>
            <div style={{fontFamily:C.fD,fontSize:16,fontWeight:700,color:C.inkB,letterSpacing:"-0.01em"}}>Activos · Pasivos · Patrimonio 2026</div>
          </div>
          <ResponsiveContainer width="100%" height={175}>
            <AreaChart data={MES.map((m,i)=>({m,tact:TACT25[i],tpas:TPAS25[i],pat:PAT25[i]}))} margin={{top:8,right:20,left:0,bottom:8}}>
              <GDefs/>
              <CartesianGrid strokeDasharray="2 6" stroke="rgba(99,102,241,0.06)" vertical={false}/>
              <XAxis dataKey="m" tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false}/>
              <YAxis tickFormatter={v=>`$${(v/1e3).toFixed(0)}MM`} tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false} width={50}/>
              <Tooltip content={<PremTT/>}/>
              <Area type="monotone" dataKey="tact" name="Activos"    stroke="#6366F1" fill="url(#gInd)" strokeWidth={1.5}/>
              <Area type="monotone" dataKey="tpas" name="Pasivos"    stroke="#F43F5E" fill="url(#gRos)" strokeWidth={1.5}/>
              <Area type="monotone" dataKey="pat"  name="Patrimonio" stroke="#059669" fill="url(#gEme)" strokeWidth={2}/>
            </AreaChart>
          </ResponsiveContainer>
        </GCard>

        <GCard delay={350}>
          <div style={{padding:"22px 24px 6px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontFamily:C.fM,fontSize:8.5,letterSpacing:"0.12em",color:C.inkD,textTransform:"uppercase",marginBottom:4}}>Inventario & Deudores</div>
                <div style={{fontFamily:C.fD,fontSize:16,fontWeight:700,color:C.inkB,letterSpacing:"-0.01em"}}>Tendencia acumulada</div>
              </div>
              <div style={{background:C.roseL,borderRadius:20,padding:"4px 10px",border:`1px solid rgba(244,63,94,0.2)`}}>
                <span style={{fontFamily:C.fM,fontSize:8,color:C.rose}}>⚠ ALERTA</span>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={175}>
            <LineChart data={MES.map((m,i)=>({m,inv:INV25[i],cxc:CXC25[i],disp:DISP25[i]}))} margin={{top:8,right:20,left:0,bottom:8}}>
              <CartesianGrid strokeDasharray="2 6" stroke="rgba(99,102,241,0.06)" vertical={false}/>
              <XAxis dataKey="m" tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false}/>
              <YAxis tickFormatter={v=>`$${(v/1e3).toFixed(0)}MM`} tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false} width={50}/>
              <Tooltip content={<PremTT/>}/>
              <Line type="monotone" dataKey="inv"  name="Inventario" stroke="#F43F5E" strokeWidth={2.5} dot={false}/>
              <Line type="monotone" dataKey="cxc"  name="CxC"        stroke="#D97706" strokeWidth={2}   dot={false}/>
              <Line type="monotone" dataKey="disp" name="Disponible" stroke="#059669" strokeWidth={1.5} dot={false} strokeDasharray="4 3"/>
            </LineChart>
          </ResponsiveContainer>
        </GCard>
      </div>

      {/* ALERT CARDS */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
        {[
          {grad:"linear-gradient(135deg,#F43F5E,#FB7185)",icon:"🔴",t:"CRÍTICO",msg:`Inventario ${fm(DB.inv)}: ${(DB.inv/DB.tact*100).toFixed(1)}% del activo vs benchmark ${B.invact}%. Sobrestock latente.`},
          {grad:"linear-gradient(135deg,#F43F5E,#FB7185)",icon:"🔴",t:"CRÍTICO",msg:`Días Cartera: ${DCXC}d vs ${DCXC24}d en 2025. Deterioro de ${DCXC-DCXC24} días en el año.`},
          {grad:"linear-gradient(135deg,#D97706,#F59E0B)",icon:"🟡",t:"ATENCIÓN",msg:`Disponible: ${fm(DB.disp)} — solo 1.8% del activo. Liquidez inmediata: ${LIQI}.`},
          {grad:"linear-gradient(135deg,#059669,#10B981)",icon:"🟢",t:"POSITIVO",msg:`Endeudamiento ${END}%. Patrimonio ${fm(DB.pat)}${BAL24.pat>0?" (+"+((DB.pat-BAL24.pat)/BAL24.pat*100).toFixed(1)+"%)":" "}.`},
        ].map((a,i)=>(
          <GCard key={i} delay={400+i*50} hover>
            <div style={{height:3,background:a.grad}}/>
            <div style={{padding:"16px 18px"}}>
              <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:8}}>
                <span style={{fontSize:12}}>{a.icon}</span>
                <span style={{fontFamily:C.fM,fontSize:8,letterSpacing:"0.14em",color:a.t==="POSITIVO"?C.emerald:a.t==="ATENCIÓN"?C.amber:C.rose,textTransform:"uppercase"}}>{a.t}</span>
              </div>
              <div style={{fontFamily:C.fB,fontSize:11.5,color:C.inkC,lineHeight:1.6,fontWeight:300}}>{a.msg}</div>
            </div>
          </GCard>
        ))}
      </div>
    </div>
  );

  /* ----------- P&G ---------------------------------------------------- */
  const PYG=()=>(
    <div key={key.current}>
      <SH pill="Estado de Resultados" title="P&G — Resultados 2026" sub="Ingresos, Gastos Operativos y Resultado · Fuente: Sistema Contable"/>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
        <KpiCard label="Ingresos Operacionales" value={fm(IT)}     sub="Neto de devoluciones"    delta={YOY}   icon="📊" grad={C.indigoG} delay={0}/>
        <KpiCard label="Gastos Administración"  value={fm(GT)}     sub={`${(GT/IT*100).toFixed(1)}% · 2025: ${fm(GT24)}${GT24>0?(parseFloat(YOGT)<0?' · ✅ Favorable':' · ⚠ Desfavorable'):''}`} delta={GT24>0?YOGT:null} icon="🏢" grad={C.amberG} delay={50}/>
        <KpiCard label="Gastos de Ventas"       value={fm(GV)}     sub={`${(GV/IT*100).toFixed(1)}% · 2025: ${fm(GV24)}${GV24>0?(parseFloat(YOGV)<0?' · ✅ Favorable':' · ⚠ Desfavorable'):''}`} delta={GV24>0?YOGV:null} icon="🛒" grad={C.roseG}  delay={100}/>
        <KpiCard label="Resultado del Ejercicio" value={fm(UT)}    sub="Balance Dic 2026"         delta={YOUTIL} icon="✅" grad={C.emeraldG} delay={150}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"3fr 2fr",gap:12,marginBottom:12}}>
        <GCard delay={200}>
          <div style={{padding:"22px 24px 6px"}}>
            <div style={{fontFamily:C.fM,fontSize:8.5,color:C.inkD,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:4}}>Mensual 2026</div>
            <div style={{fontFamily:C.fD,fontSize:16,fontWeight:700,color:C.inkB}}>Ingresos vs Gastos vs Resultado</div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={MES.map((m,i)=>({m,ing:ING25[i],gadm:GADM25[i],gvta:GVTA25[i],util:UTIL25[i]}))} barGap={1} barSize={11} margin={{top:8,right:20,left:0,bottom:8}}>
              <GDefs/>
              <CartesianGrid strokeDasharray="2 6" stroke="rgba(99,102,241,0.06)" vertical={false}/>
              <XAxis dataKey="m" tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false}/>
              <YAxis tickFormatter={v=>`$${(v/1e3).toFixed(0)}MM`} tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false} width={50}/>
              <Tooltip content={<PremTT/>}/>
              <Area type="monotone" dataKey="ing"  name="Ingresos"   stroke="#6366F1" strokeWidth={1.5} fill="url(#gInd)"/>
              <Bar dataKey="gadm" name="G.Admin"   fill="#D97706" fillOpacity={0.7} radius={[3,3,0,0]}/>
              <Bar dataKey="gvta" name="G.Ventas"  fill="#F43F5E" fillOpacity={0.65} radius={[3,3,0,0]}/>
              <Line type="monotone" dataKey="util" name="Resultado"  stroke="#059669" strokeWidth={2.5} dot={{r:3,fill:"#059669",strokeWidth:0}}/>
            </ComposedChart>
          </ResponsiveContainer>
        </GCard>
        <GCard delay={250}>
          <div style={{padding:"22px 24px 6px"}}>
            <div style={{fontFamily:C.fM,fontSize:8.5,color:C.inkD,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:4}}>Acumulado</div>
            <div style={{fontFamily:C.fD,fontSize:16,fontWeight:700,color:C.inkB}}>Resultado acumulado 2026</div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={MES.map((m,i)=>({m,acum:sum(UTIL25.slice(0,i+1))}))} margin={{top:8,right:20,left:0,bottom:8}}>
              <GDefs/>
              <CartesianGrid strokeDasharray="2 6" stroke="rgba(99,102,241,0.06)" vertical={false}/>
              <XAxis dataKey="m" tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false}/>
              <YAxis tickFormatter={v=>`$${(v/1e3).toFixed(0)}MM`} tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false} width={50}/>
              <Tooltip content={<PremTT/>}/>
              <Area type="monotone" dataKey="acum" name="Resultado Acumulado" stroke="#059669" fill="url(#gEme)" strokeWidth={2.5}/>
            </AreaChart>
          </ResponsiveContainer>
        </GCard>
      </div>
      <GCard delay={300} hover={false}>
        <div style={{padding:"22px 24px 0"}}>
          <div style={{fontFamily:C.fD,fontSize:16,fontWeight:700,color:C.inkB,marginBottom:16}}>Detalle mensual — millones COP</div>
        </div>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",minWidth:1050}}>
            <thead>
              <tr style={{background:`linear-gradient(135deg,#0F1117,#1E1B4B)`}}>
                {["CUENTA","ENE","FEB","MAR","ABR","MAY","JUN","JUL","AGO","SEP","OCT","NOV","DIC","TOTAL"].map((h,j)=>(
                  <th key={j} style={{padding:"10px 10px",textAlign:j===0?"left":"right",color:"rgba(165,180,252,0.7)",fontFamily:C.fM,fontSize:8,letterSpacing:"0.12em",fontWeight:400}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                {l:"(+) Ingresos Operacionales",arr:ING25,  c:C.indigo,b:true},
                {l:"  (−) Gastos Administración", arr:GADM25,c:C.inkC},
                {l:"  (−) Gastos de Ventas",      arr:GVTA25,c:C.inkC},
                {l:"  (−) Gastos No Operac.",     arr:GNOOP25,c:C.inkC},
                {l:"(=) Resultado del Período",   arr:UTIL25, c:C.emerald,b:true,hl:true},
              ].map((row,ri)=>{
                const tot=sum(row.arr);
                const isResult=row.hl;
                return(
                  <tr key={ri} style={{background:row.hl?"rgba(5,150,105,0.05)":ri%2?"rgba(99,102,241,0.02)":"transparent",borderBottom:`1px solid rgba(99,102,241,0.05)`}}>
                    <td style={{padding:"10px",fontFamily:C.fB,fontSize:11,color:row.b?row.c:C.inkC,fontWeight:row.b?500:300,paddingLeft:row.b?10:22}}>{row.l}</td>
                    {row.arr.map((v,j)=>(
                      <td key={j} style={{padding:"10px",textAlign:"right",fontFamily:C.fM,fontSize:10,
                        color:isResult?(v<0?C.rose:v>0?C.emerald:C.inkD):(row.b?row.c:C.inkD),
                        fontWeight:isResult?600:(row.b?500:300)}}>{v.toFixed(0)}</td>
                    ))}
                    <td style={{padding:"10px",textAlign:"right",fontFamily:C.fM,fontSize:11,color:isResult?(tot<0?C.rose:C.emerald):row.c,fontWeight:600}}>{tot.toFixed(0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </GCard>
    </div>
  );

  /* ----------- BALANCE ------------------------------------------------ */
  const balRows = [
    {l:"ACTIVOS", sep:true},
    {l:"Disponible",        v24:BAL24.disp, v25:DB.disp,  c:DB.disp>BAL24.disp?C.emerald:C.rose},
    {l:"Deudores (CxC)",    v24:BAL24.cxc,  v25:DB.cxc,   c:C.amber},
    {l:"Inventarios ⚠",    v24:BAL24.inv,   v25:DB.inv,   c:C.rose},
    {l:"TOTAL ACTIVOS",    v24:BAL24.tact,  v25:DB.tact,  c:C.indigo, b:true},
    {l:"PASIVOS Y PATRIMONIO", sep:true},
    {l:"Obligaciones Fin.", v24:BAL24.oblf,  v25:DB.oblf,  c:DB.oblf<BAL24.oblf?C.emerald:C.rose},
    {l:"Proveedores",       v24:BAL24.prov,  v25:DB.prov,  c:C.inkC},
    {l:"TOTAL PASIVOS",    v24:BAL24.tpas,  v25:DB.tpas,  c:C.rose,  b:true},
    {l:"PATRIMONIO",       v24:BAL24.pat,   v25:DB.pat,   c:C.emerald,b:true},
  ];
  const liqRows = [
    {l:"Razón Corriente", v:LIQ,              ok:parseFloat(LIQ)>=1.5,        m:"≥ 1.5"},
    {l:"Prueba Ácida",    v:ACID,             ok:parseFloat(ACID)>=1,         m:"≥ 1.0"},
    {l:"Endeudamiento",   v:pct(END),         ok:parseFloat(END)<60,          m:"< 60%"},
    {l:"Deuda/Patrim.",   v:`${(DB.tpas/DB.pat).toFixed(2)}x`, ok:DB.tpas/DB.pat<2, m:"< 2x"},
  ];
  const BALANCE=()=>(
    <div>
      <SH pill="Estado de Situación Financiera" title="Posición Financiera Dic 2026" sub="Activos · Pasivos · Patrimonio · Comparativo anual"/>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
        <KpiCard label="Total Activos"    value={fm(DB.tact)} sub={`vs ${fm(BAL24.tact)} en Año Ant.`} delta={BAL24.tact>0?((DB.tact-BAL24.tact)/BAL24.tact*100).toFixed(1):null} icon="📦" grad={C.indigoG} delay={0}/>

        <KpiCard label="Total Pasivos"    value={fm(DB.tpas)} sub={`Endeudamiento ${END}% · Año Ant.: ${fm(BAL24.tpas)}`} delta={BAL24.tpas>0?((DB.tpas-BAL24.tpas)/BAL24.tpas*100).toFixed(1):null} icon="📉" grad={C.roseG} delay={100}/>
        <KpiCard label="Patrimonio Neto"  value={fm(DB.pat)}  sub={`vs ${fm(BAL24.pat)} en Año Ant.`}  delta={BAL24.pat>0?((DB.pat-BAL24.pat)/BAL24.pat*100).toFixed(1):null} icon="🏆" grad={C.emeraldG} delay={150}/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:12,marginBottom:12}}>
        {/* -- Tabla comparativa -- */}
        <GCard delay={200} hover={false}>
          <div style={{padding:"22px 24px 16px"}}>
            <div style={{fontFamily:C.fD,fontSize:16,fontWeight:700,color:C.inkB,marginBottom:16}}>Comparativo Dic 2025 vs Dic 2026</div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",minWidth:420}}>
                <thead>
                  <tr style={{background:"linear-gradient(135deg,#0F1117,#1E1B4B)"}}>
                    {["CUENTA","DIC 2025","DIC 2026","Δ ABS.","Δ %"].map((h,j)=>(
                      <th key={j} style={{padding:"10px 14px",textAlign:j===0?"left":"right",color:"rgba(165,180,252,0.7)",fontFamily:C.fM,fontSize:8,letterSpacing:"0.12em",fontWeight:400,whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {balRows.map((r,i)=>{
                    if(r.sep){
                      return(
                        <tr key={`sep-${i}`}>
                          <td colSpan={5} style={{padding:"14px 14px 4px",fontFamily:C.fM,fontSize:8,letterSpacing:"0.18em",color:C.indigo,textTransform:"uppercase",opacity:0.7,borderBottom:`1px solid rgba(99,102,241,0.08)`}}>
                            {r.l}
                          </td>
                        </tr>
                      );
                    }
                    const v24 = Number(r.v24)||0;
                    const v25 = Number(r.v25)||0;
                    const d   = v25 - v24;
                    const dp  = v24 !== 0 ? (d/v24*100).toFixed(1) : "—";
                    const dColor = d >= 0 ? C.emerald : C.rose;
                    return(
                      <tr key={`row-${i}`} style={{background:r.b?"rgba(99,102,241,0.03)":"transparent",borderBottom:"1px solid rgba(99,102,241,0.05)"}}>
                        <td style={{padding:"10px 14px",fontFamily:C.fB,fontSize:r.b?11.5:11,color:r.c||C.inkC,fontWeight:r.b?600:300,paddingLeft:r.b?14:22}}>{r.l}</td>
                        <td style={{padding:"10px 14px",textAlign:"right",fontFamily:C.fM,fontSize:10,color:C.inkD,whiteSpace:"nowrap"}}>{v24.toFixed(0)}</td>
                        <td style={{padding:"10px 14px",textAlign:"right",fontFamily:C.fM,fontSize:r.b?11:10,color:r.c||C.inkC,fontWeight:r.b?700:400,whiteSpace:"nowrap"}}>{v25.toFixed(0)}</td>
                        <td style={{padding:"10px 14px",textAlign:"right",fontFamily:C.fM,fontSize:10,color:dColor,whiteSpace:"nowrap"}}>{d>=0?"+":""}{d.toFixed(0)}</td>
                        <td style={{padding:"10px 14px",textAlign:"right",fontFamily:C.fM,fontSize:10,color:dColor,fontWeight:600,whiteSpace:"nowrap"}}>{dp !== "—" ? `${d>=0?"+":""}${dp}%` : dp}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </GCard>

        {/* -- Indicadores -- */}
        <GCard delay={250}>
          <div style={{padding:"20px"}}>
            <div style={{fontFamily:C.fD,fontSize:14,fontWeight:700,color:C.inkB,marginBottom:16}}>Liquidez & Solvencia</div>
            {liqRows.map((k,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderBottom:i<liqRows.length-1?"1px solid rgba(99,102,241,0.07)":"none"}}>
                <div>
                  <div style={{fontFamily:C.fB,fontSize:11.5,color:C.inkB,fontWeight:300}}>{k.l}</div>
                  <div style={{fontFamily:C.fM,fontSize:8,color:C.inkD}}>meta {k.m}</div>
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <span style={{fontFamily:C.fM,fontSize:13,color:k.ok?C.emerald:C.rose,fontWeight:600}}>{k.v}</span>
                  <Bdg label={k.ok?"✓":"✗"} ok={k.ok}/>
                </div>
              </div>
            ))}
          </div>
        </GCard>
      </div>

      {/* -- Gráfica evolución -- */}
      <GCard delay={400}>
        <div style={{padding:"22px 24px 6px"}}>
          <div style={{fontFamily:C.fD,fontSize:16,fontWeight:700,color:C.inkB,marginBottom:2}}>Evolución 2026 — Activos · Pasivos · Patrimonio</div>
        </div>
        <ResponsiveContainer width="100%" height={175}>
          <AreaChart data={MES.map((m,i)=>({m,tact:TACT25[i],tpas:TPAS25[i],pat:PAT25[i]}))} margin={{top:8,right:20,left:0,bottom:8}}>
            <GDefs/>
            <CartesianGrid strokeDasharray="2 6" stroke="rgba(99,102,241,0.06)" vertical={false}/>
            <XAxis dataKey="m" tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false}/>
            <YAxis tickFormatter={v=>`$${(v/1e3).toFixed(0)}MM`} tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false} width={50}/>
            <Tooltip content={<PremTT/>}/>
            <Area type="monotone" dataKey="tact" name="Activos"    stroke="#6366F1" fill="url(#gInd)" strokeWidth={1.5}/>
            <Area type="monotone" dataKey="tpas" name="Pasivos"    stroke="#F43F5E" fill="url(#gRos)" strokeWidth={1.5}/>
            <Area type="monotone" dataKey="pat"  name="Patrimonio" stroke="#059669" fill="url(#gEme)" strokeWidth={2}/>
          </AreaChart>
        </ResponsiveContainer>
      </GCard>
    </div>
  );

  /* ----------- RATIOS ------------------------------------------------- */
  const RATIOS=()=>(
    <div key={key.current}>
      <SH pill="KPIs" title="Indicadores Financieros" sub="20 ratios con benchmarks, evaluación y observaciones — Diciembre 2026"/>
      {[
        {g:"Liquidez y Capital de Trabajo",icon:"💧",c:C.cyan,items:[
          {l:"Razón Corriente",   v:LIQ,   m:">1.5",ok:parseFloat(LIQ)>=1.5, obs:"Posición superior al mínimo recomendado"},
          {l:"Prueba Ácida",      v:ACID,  m:">1.0",ok:parseFloat(ACID)>=1,  obs:"Inventario alto presiona este ratio"},
          {l:"Capital Trabajo",   v:fmm(DB.acte-PAC),m:">0",ok:(DB.acte-PAC)>0, obs:"Positivo — solvencia operativa garantizada"},
        ]},
        {g:"Endeudamiento y Apalancamiento",icon:"⚖️",c:C.rose,items:[
          {l:"Endeudamiento",     v:pct(END),meta:"<60%",ok:parseFloat(END)<60,    obs:"Mejoró de 54.3% (2025) — tendencia positiva"},
          {l:"Deuda/Patrimonio",  v:`${(DB.tpas/DB.pat).toFixed(2)}x`,meta:"<2x",ok:DB.tpas/DB.pat<2,obs:"Notablemente mejorado en 2026"},
          {l:"Obl. Financieras",  v:fm(DB.oblf),meta:"↓",ok:DB.oblf<BAL24.oblf,   obs:`Reducción de ${fm(BAL24.oblf-DB.oblf)} vs Año Ant. ✓`},
        ]},
        {g:"Rentabilidad",icon:"📈",c:C.emerald,items:[
          {l:"ROE",               v:pct(ROE),  meta:">15%",ok:parseFloat(ROE)>=15, obs:"Excepcional — supera benchmark sectorial"},
          {l:"ROA",               v:pct(ROA),  meta:">8%", ok:parseFloat(ROA)>=8,  obs:"Buena generación sobre base de activos"},
          {l:"Mg. Contribución",  v:pct(MGC),  meta:">20%",ok:parseFloat(MGC)>=20, obs:"Margen positivo sobre gastos directos"},
          {l:"Crec. Ingresos",    v:`+${YOY}%`,meta:">10%",ok:parseFloat(YOY)>=10, obs:"Crecimiento sostenido y significativo"},
        ]},
        {g:"Actividad y Gestión",icon:"⚡",c:C.amber,items:[
          {l:"Días de Cartera",   v:`${DCXC}d`,meta:"<60d",ok:false,               obs:`Deterioro ${DCXC-DCXC24}d — acción urgente`},
          {l:"Rotación Activos",  v:`${ROT}x`, meta:">1x", ok:parseFloat(ROT)>=1,  obs:"Presionada por inventario elevado"},
          {l:"Inventario/Activos",v:`${(DB.inv/DB.tact*100).toFixed(1)}%`,meta:"<50%",ok:false,obs:"Riesgo sobrestock — revisar urgente"},
          {l:"Disponible/Activos",v:`${(DB.disp/DB.tact*100).toFixed(1)}%`,meta:">5%",ok:false,obs:"Caja muy ajustada para el tamaño del balance"},
        ]},
      ].map((grupo,gi)=>(
        <GCard key={gi} style={{marginBottom:12,padding:"24px"}} delay={gi*60} hover={false}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
            <span style={{fontSize:18}}>{grupo.icon}</span>
            <div style={{fontFamily:C.fD,fontSize:17,fontWeight:700,color:grupo.c,letterSpacing:"-0.01em"}}>{grupo.g}</div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
            {grupo.items.map((item,ii)=>(
              <div key={ii} style={{background:item.ok?"rgba(5,150,105,0.04)":"rgba(244,63,94,0.04)",borderRadius:12,padding:"16px",border:`1px solid ${item.ok?"rgba(5,150,105,0.15)":"rgba(244,63,94,0.15)"}`}}>
                <div style={{fontFamily:C.fM,fontSize:8.5,color:C.inkD,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:10}}>{item.l}</div>
                <div style={{fontFamily:C.fD,fontSize:26,fontWeight:800,color:item.ok?C.emerald:C.rose,lineHeight:1,letterSpacing:"-0.02em"}}>{item.v}</div>
                <div style={{fontFamily:C.fM,fontSize:8,color:C.inkD,margin:"8px 0 8px"}}>META {item.m||item.meta}</div>
                <Bdg label={item.ok?"✓ Cumple":"✗ Revisar"} ok={item.ok}/>
                <div style={{fontFamily:C.fB,fontSize:10.5,color:C.inkC,fontWeight:300,marginTop:8,lineHeight:1.5}}>{item.obs}</div>
              </div>
            ))}
          </div>
        </GCard>
      ))}
    </div>
  );

  /* ----------- RIESGOS ------------------------------------------------ */
  const RIESGOS=()=>(
    <div key={key.current}>
      <SH pill="Risk Management" title="Mapa de Riesgos y Plan de Acción" sub="Identificación, cuantificación y medidas de control — Gerencia Financiera"/>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <GCard delay={0}>
          <div style={{padding:"22px 24px 6px"}}>
            <div style={{fontFamily:C.fD,fontSize:16,fontWeight:700,color:C.inkB,marginBottom:2}}>Mapa de riesgo financiero</div>
            <div style={{fontFamily:C.fB,fontSize:11,color:C.inkD,fontWeight:300}}>Evaluación por dimensión estratégica</div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <RadarChart data={[
              {d:"Liquidez",    v:Math.min(100,parseFloat(LIQ)/3*100)},
              {d:"Solvencia",   v:Math.min(100,(1-parseFloat(END)/100)*130)},
              {d:"Rentabilidad",v:Math.min(100,parseFloat(ROE)/80*100)},
              {d:"Cobertura",   v:Math.min(100,Math.max(0,100-DCXC+30))},
              {d:"Inventario",  v:Math.min(100,Math.max(0,100-(DB.inv/DB.tact*100)*1.4))},
              {d:"Crecimiento", v:Math.min(100,parseFloat(YOY)/20*100)},
            ]} cx="50%" cy="52%" outerRadius={90}>
              <PolarGrid stroke="rgba(99,102,241,0.12)"/>
              <PolarAngleAxis dataKey="d" tick={{fill:C.inkC,fontSize:10,fontFamily:C.fB,fontWeight:300}}/>
              <Radar name="Score" dataKey="v" stroke={C.indigo} fill={C.indigo} fillOpacity={0.1} strokeWidth={2}/>
            </RadarChart>
          </ResponsiveContainer>
        </GCard>
        <GCard delay={50}>
          <div style={{padding:"22px 24px 16px"}}>
            <div style={{fontFamily:C.fD,fontSize:16,fontWeight:700,color:C.inkB,marginBottom:16}}>Barómetro de salud financiera</div>
            {[
              {l:"Crecimiento Ingresos "+YOY+"%", s:parseFloat(YOY)>=B.yoy[0]?"POSITIVO":"REVISAR", c:parseFloat(YOY)>=B.yoy[0]?C.emerald:C.rose, bg:parseFloat(YOY)>=B.yoy[0]?C.emeraldL:C.roseL, p:Math.min(100,Math.round(parseFloat(YOY)/B.yoy[1]*100))},
              {l:"Solvencia / Endeudamiento",   s:"SALUDABLE",  c:C.emerald,bg:C.emeraldL,p:78},
              {l:"Rentabilidad ROE "+ROE+"%",   s:parseFloat(ROE)>=B.roe[1]?"EXCEPCIONAL":parseFloat(ROE)>=B.roe[0]?"SALUDABLE":"REVISAR", c:parseFloat(ROE)>=B.roe[0]?C.cyan:C.rose, bg:parseFloat(ROE)>=B.roe[0]?C.cyanL:C.roseL, p:Math.min(100,Math.round(parseFloat(ROE)/B.roe[1]*100))},
              {l:"Liquidez Corriente",          s:"POSITIVO",   c:C.emerald,bg:C.emeraldL,p:70},
              {l:"Efectivo / Disponible",       s:"ALERTA",     c:C.amber,  bg:C.amberL, p:22},
              {l:"Días de Cartera "+DCXC+"d",   s:DCXC<=B.dcxc[1]?"POSITIVO":"CRÍTICO",    c:DCXC<=B.dcxc[1]?C.emerald:C.rose,   bg:DCXC<=B.dcxc[1]?C.emeraldL:C.roseL,  p:Math.min(100,Math.round(B.dcxc[1]/DCXC*100))},
              {l:"Inventario "+(DB.tact>0?(DB.inv/DB.tact*100).toFixed(1):0)+"% del Activo",   s:"CRÍTICO",    c:C.rose,   bg:C.roseL,  p:12},
            ].map((k,i)=>(
              <div key={i} style={{marginBottom:13}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
                  <span style={{fontFamily:C.fB,fontSize:11.5,color:C.inkB,fontWeight:300}}>{k.l}</span>
                  <span style={{fontFamily:C.fM,fontSize:8,letterSpacing:"0.1em",color:k.c,background:k.bg,padding:"2px 9px",borderRadius:20}}>{k.s}</span>
                </div>
                <div style={{height:5,background:"rgba(99,102,241,0.06)",borderRadius:10,overflow:"hidden"}}>
                  <div style={{width:`${k.p}%`,height:"100%",background:`linear-gradient(90deg,${k.c},${k.c}88)`,borderRadius:10,transition:"width 1.2s ease"}}/>
                </div>
              </div>
            ))}
          </div>
        </GCard>
      </div>
      <GCard delay={120} hover={false}>
        <div style={{padding:"22px 24px 0"}}>
          <div style={{fontFamily:C.fD,fontSize:16,fontWeight:700,color:C.inkB,marginBottom:4}}>Plan de acción prioritizado</div>
          <div style={{fontFamily:C.fB,fontSize:11,color:C.inkD,fontWeight:300,marginBottom:16}}>Riesgos · Acciones · Responsables · Plazos</div>
        </div>
        <div style={{borderTop:"1px solid rgba(99,102,241,0.08)"}}>
          {[
            {n:1,r:"Inventario "+(BAL24.inv>0?(((DB.inv-BAL24.inv)/BAL24.inv)*100).toFixed(1)+"%: "+fm(BAL24.inv)+" a "+fm(DB.inv):"sin datos"),   imp:"CRÍTICO",c:C.rose,
             a:"Auditar por antigüedad (>90/180/365d). Política FIFO. Suspender O/C hasta rotar ~$3,000M. Negociar devoluciones con proveedores.",resp:"Dir. Logística + CFO",p:"30 días"},
            {n:2,r:"Días Cartera: "+DCXC+"d vs "+DCXC24+"d (2025)", imp:"CRÍTICO",c:C.rose,
             a:"Cobro activo >60d. Descuento pronto pago 2%. Revisar política crédito. Meta: <90d Q1 2026.",resp:"Gte. Comercial",p:"15 días"},
            {n:3,r:"Disponible crítico: "+fm(DB.disp)+" ("+(DB.tact>0?(DB.disp/DB.tact*100).toFixed(1):0)+"% activo)",imp:"ALTO",c:C.amber,
             a:"Línea crédito rotativa $800M. Caja mínima = 20d gastos. Acelerar recaudo prioritario.",resp:"Tesorería + CFO",p:"30 días"},
            {n:4,r:BAL24.cxc>0?"CxC "+((( DB.cxc-BAL24.cxc)/BAL24.cxc)*100).toFixed(1)+"% YoY ("+fm(BAL24.cxc)+" a "+fm(DB.cxc)+")":"CxC sin datos 2025", imp:"ALTO",c:C.amber,
             a:"Aging de cartera. Top-20 deudores comité mensual. Provisión NIIF 5% cartera >90d.",resp:"Contabilidad",p:"15 días"},
            {n:5,r:"Proveedores "+fm(DB.prov)+" (concentración "+(DB.tact>0?(DB.prov/DB.tact*100).toFixed(1):0)+"%)",   imp:"MODERADO",c:C.indigo,
             a:"Negociar plazos 60-75d. Factoring inverso. Diversificar base de proveedores.",resp:"Compras + CFO",p:"45 días"},
          ].map((r)=>(
            <div key={r.n} style={{display:"grid",gridTemplateColumns:"auto 2.2fr 3fr 1.5fr 0.8fr",gap:16,padding:"16px 24px",borderBottom:"1px solid rgba(99,102,241,0.05)",alignItems:"start"}}>
              <div style={{width:28,height:28,borderRadius:"50%",background:`${r.c}15`,border:`1.5px solid ${r.c}40`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:2}}>
                <span style={{fontFamily:C.fM,fontSize:10,color:r.c,fontWeight:600}}>{r.n}</span>
              </div>
              <div>
                <div style={{fontFamily:C.fB,fontSize:12,color:C.inkB,fontWeight:400,lineHeight:1.5,marginBottom:5}}>{r.r}</div>
                <span style={{fontFamily:C.fM,fontSize:8,letterSpacing:"0.1em",color:r.c,background:`${r.c}12`,padding:"2px 9px",borderRadius:20}}>{r.imp}</span>
              </div>
              <div style={{fontFamily:C.fB,fontSize:11.5,color:C.inkC,lineHeight:1.7,fontWeight:300}}>{r.a}</div>
              <div style={{fontFamily:C.fM,fontSize:9,color:C.inkD,lineHeight:1.6}}>{r.resp}</div>
              <div style={{fontFamily:C.fM,fontSize:9,color:C.cyan,fontWeight:600}}>{r.p}</div>
            </div>
          ))}
        </div>
        <div style={{height:8}}/>
      </GCard>
    </div>
  );

  /* ----------- IA ----------------------------------------------------- */
  const IA=()=>(
    <div key={key.current}>
      <SH pill="✦ Inteligencia Artificial" title="Informe Narrativo con IA" sub="Redactado con datos reales · Rol: Contadora financiera 20 años de experiencia · Para junta directiva"/>
      <GCard delay={0} hover={false} style={{padding:"24px",marginBottom:14}}>
        <div style={{fontFamily:C.fD,fontSize:15,fontWeight:700,color:C.inkB,marginBottom:16}}>Configuración del informe</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr auto",gap:12,alignItems:"end"}}>
          <div>
            <div style={{fontFamily:C.fM,fontSize:8.5,color:C.inkD,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:7}}>Razón social</div>
            <input value={empresa} onChange={e=>setEmpresa(e.target.value)} style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,padding:"11px 14px",color:C.inkB,fontFamily:C.fB,fontSize:13,fontWeight:300,transition:"all 0.2s"}}/>
          </div>
          <div>
            <div style={{fontFamily:C.fM,fontSize:8.5,color:C.inkD,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:7}}>Período</div>
            <input value={periodo} onChange={e=>setPeriodo(e.target.value)} style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,padding:"11px 14px",color:C.inkB,fontFamily:C.fB,fontSize:13,fontWeight:300,transition:"all 0.2s"}}/>
          </div>
          <button onClick={runAI} disabled={loading} style={{background:loading?C.bg:`linear-gradient(135deg,#6366F1,#8B5CF6)`,border:"none",borderRadius:10,padding:"11px 30px",color:loading?C.inkD:"#FFFFFF",fontFamily:C.fD,fontSize:15,fontWeight:700,transition:"all 0.2s",boxShadow:loading?"none":"0 8px 30px rgba(99,102,241,0.3)",whiteSpace:"nowrap",letterSpacing:"-0.01em"}}>
            {loading?"Redactando…":"✦ Generar Informe"}
          </button>
        </div>
      </GCard>
      {!loading&&!done&&(
        <GCard delay={100} hover={false}>
          <div style={{padding:"60px 48px",textAlign:"center"}}>
            <div style={{width:64,height:64,borderRadius:"50%",background:C.indigoG,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px",fontSize:24,boxShadow:"0 12px 40px rgba(99,102,241,0.25)"}}>✦</div>
            <div style={{fontFamily:C.fD,fontSize:22,fontWeight:700,color:C.inkB,marginBottom:10,letterSpacing:"-0.02em"}}>Análisis basado en datos reales de Sistema Contable</div>
            <div style={{fontFamily:C.fB,fontSize:13,color:C.inkD,maxWidth:520,margin:"0 auto",lineHeight:1.8,fontWeight:300}}>La IA asumirá el rol de contadora financiera con 20 años de experiencia y redactará un análisis completo de 11 secciones para presentar ante junta directiva.</div>
            <div style={{display:"flex",justifyContent:"center",gap:8,marginTop:24,flexWrap:"wrap"}}>
              {["Síntesis Ejecutiva","Ingresos","Costos","Liquidez","Inventarios ⚠","Cartera ⚠","Capital","Recomendaciones","Proyección Q1"].map((f,i)=>(
                <span key={i} style={{fontFamily:C.fM,fontSize:8.5,letterSpacing:"0.1em",color:C.indigo,border:`1px solid ${C.border}`,borderRadius:20,padding:"4px 12px",background:C.indigoL}}>{f}</span>
              ))}
            </div>
          </div>
        </GCard>
      )}
      {loading&&(
        <GCard hover={false}>
          <div style={{padding:"48px",textAlign:"center"}}>
            <div style={{fontFamily:C.fD,fontSize:18,color:C.indigo,fontWeight:600,letterSpacing:"-0.01em"}}>Analizando {Object.keys(DB).length*12+60}+ puntos de datos…</div>
            <div style={{fontFamily:C.fB,fontSize:12,color:C.inkD,marginTop:8,fontWeight:300}}>Redactando informe ejecutivo para junta directiva</div>
          </div>
        </GCard>
      )}
      {done&&ai&&(
        <GCard hover={false}>
          <div style={{background:`linear-gradient(135deg,#0F1117,#1E1B4B)`,padding:"32px 42px",borderRadius:"16px 16px 0 0"}}>
            <div style={{fontFamily:C.fM,fontSize:8.5,color:"rgba(165,180,252,0.5)",letterSpacing:"0.18em",textTransform:"uppercase",marginBottom:10}}>Informe Ejecutivo Financiero · Generado con IA</div>
            <div style={{fontFamily:C.fD,fontSize:26,fontWeight:800,color:"#FFFFFF",letterSpacing:"-0.02em",lineHeight:1.2}}>{empresa}</div>
            <div style={{fontFamily:C.fB,fontSize:12,color:"rgba(255,255,255,0.45)",marginTop:6,fontWeight:300}}>{periodo} · Gerencia Financiera · Fuente: Sistema Contable</div>
          </div>
          <div style={{padding:"38px 42px"}}>
            {ai.split("\n").map((line,i)=>{
              if(/^[IVX]+\.\s/.test(line)){
                return(
                  <div key={i} style={{marginTop:i===0?0:34,marginBottom:12}}>
                    <div style={{height:1,background:"rgba(99,102,241,0.1)",marginBottom:16}}/>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                      <div style={{width:3,height:20,borderRadius:2,background:C.indigoG}}/>
                      <div style={{fontFamily:C.fD,fontSize:17,fontWeight:700,color:C.indigo,letterSpacing:"-0.01em"}}>{line}</div>
                    </div>
                  </div>
                );
              }
              return line?<p key={i} style={{fontFamily:C.fB,fontSize:13.5,color:C.inkC,lineHeight:1.9,fontWeight:300,marginBottom:12}}>{line}</p>:<br key={i}/>;
            })}
          </div>
          <div style={{borderTop:"1px solid rgba(99,102,241,0.08)",padding:"14px 42px",display:"flex",justifyContent:"space-between",background:"rgba(99,102,241,0.02)",borderRadius:"0 0 16px 16px"}}>
            <span style={{fontFamily:C.fM,fontSize:8,color:C.inkD,letterSpacing:"0.1em"}}>FUENTE: SISTEMA CONTABLE · {empresa||""} · DATOS REALES 2026</span>
            <span style={{fontFamily:C.fM,fontSize:8,color:C.inkD}}>{new Date().toLocaleDateString("es-CO",{year:"numeric",month:"long",day:"numeric"})}</span>
          </div>
        </GCard>
      )}
    </div>
  );

  /* ----------- COMPARATIVO POR MES ----------------------------------- */
  const COMPARATIVO=()=>{
    const [m24,setM24]=useState(0);   // índice mes 2025 (0=Ene)
    const [m25,setM25]=useState(0);   // índice mes 2026 (0=Ene)
    const [rango24,setRango24]=useState({from:0,to:11});
    const [rango25,setRango25]=useState({from:0,to:11});
    const [modo,setModo]=useState("mes"); // "mes" | "rango"

    const MES_FULL=["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

    // -- datos de un mes puntual --------------------------------------
    const dataMes24={
      ing: ING24[m24], gadm: GADM25[m24]*0, gvta: GVTA25[m24]*0,  // solo tenemos datos completos de 2026 para gastos
      disp: DISP25[m24], cxc: CXC25[m24], inv: INV25[m24],
      tact: TACT25[m24], tpas: TPAS25[m24], pat: PAT25[m24],
    };
    const dataMes25={
      ing: ING25[m25], gadm: GADM25[m25], gvta: GVTA25[m25],
      gnoop: GNOOP25[m25], util: UTIL25[m25],
      disp: DISP25[m25], cxc: CXC25[m25], inv: INV25[m25],
      tact: TACT25[m25], tpas: TPAS25[m25], pat: PAT25[m25],
    };

    // -- datos de rango acumulado -------------------------------------
    const sliceSum=(arr,f,t)=>arr.slice(f,t+1).reduce((s,v)=>s+v,0);
    const ing24R  = sliceSum(ING24,  rango24.from, rango24.to);
    const ing25R  = sliceSum(ING25,  rango25.from, rango25.to);
    const gadm25R = sliceSum(GADM25, rango25.from, rango25.to);
    const gvta25R = sliceSum(GVTA25, rango25.from, rango25.to);
    const util25R = sliceSum(UTIL25, rango25.from, rango25.to);

    // evolución mensual dentro del rango seleccionado
    const rangoData = MES.map((m,i)=>({
      m,
      ing24: i>=rango24.from && i<=rango24.to ? ING24[i] : null,
      ing25: i>=rango25.from && i<=rango25.to ? ING25[i] : null,
      util25: i>=rango25.from && i<=rango25.to ? UTIL25[i] : null,
    }));

    // comparativo mes a mes directo (todos los meses seleccionados)
    const mesAMesData = MES.map((m,i)=>({m, v24:ING24[i], v25:ING25[i], delta:ING25[i]-ING24[i]}));

    const selStyle=(active)=>({
      fontFamily:C.fB, fontSize:12, fontWeight:active?600:400,
      padding:"8px 20px", borderRadius:8, border:"none",
      background:active?C.indigoG:"transparent",
      color:active?"#fff":C.inkD, cursor:"pointer", transition:"all 0.2s",
      boxShadow:active?"0 4px 14px rgba(99,102,241,0.3)":"none",
    });

    const selMes=(val,yy)=>({
      fontFamily:C.fM, fontSize:10, padding:"7px 11px", borderRadius:8,
      border:`1px solid ${yy===24?C.border:"rgba(99,102,241,0.25)"}`,
      background:val===(yy===24?m24:m25)?(yy===24?"#FEF3C7":"rgba(99,102,241,0.1)"):"transparent",
      color:val===(yy===24?m24:m25)?(yy===24?C.amber:C.indigo):C.inkD,
      cursor:"pointer", transition:"all 0.15s", letterSpacing:"0.05em",
    });

    const DeltaBadge=({v24,v25})=>{
      if(!v24) return null;
      const d=v25-v24, dp=(d/v24*100).toFixed(1);
      return(
        <div style={{display:"inline-flex",alignItems:"center",gap:5,background:d>=0?C.emeraldL:C.roseL,borderRadius:20,padding:"3px 10px"}}>
          <span style={{fontSize:9}}>{d>=0?"▲":"▼"}</span>
          <span style={{fontFamily:C.fM,fontSize:10,color:d>=0?C.emerald:C.rose,fontWeight:600}}>{Math.abs(parseFloat(dp))}%</span>
        </div>
      );
    };

    return(
      <div key={key.current}>
        <SH pill="Análisis Comparativo" title="Comparativo por Mes y Rango" sub="Selecciona cortes específicos para comparar 2025 vs 2026 · Herramienta de análisis de períodos"/>

        {/* SELECTOR DE MODO */}
        <div style={{display:"flex",gap:4,background:"rgba(99,102,241,0.06)",borderRadius:12,padding:4,marginBottom:20,width:"fit-content"}}>
          <button style={selStyle(modo==="mes")} onClick={()=>setModo("mes")}>📅 Mes vs Mes</button>
          <button style={selStyle(modo==="rango")} onClick={()=>setModo("rango")}>📊 Rango de Meses</button>
          <button style={selStyle(modo==="tabla")} onClick={()=>setModo("tabla")}>📋 Tabla Completa</button>
        </div>

        {/* -- MODO: MES VS MES ---------------------------------------- */}
        {modo==="mes"&&(
          <div>
            {/* Selectores */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
              {/* 2025 */}
              <GCard delay={0} hover={false}>
                <div style={{padding:"20px 22px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
                    <div style={{width:10,height:10,borderRadius:3,background:C.amberG,flexShrink:0}}/>
                    <div style={{fontFamily:C.fD,fontSize:14,fontWeight:700,color:C.inkB}}>Período 2025</div>
                    <div style={{marginLeft:"auto",fontFamily:C.fD,fontSize:16,fontWeight:800,color:C.amber}}>{MES_FULL[m24]}</div>
                  </div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                    {MES.map((m,i)=>(
                      <button key={i} style={selMes(i,24)} onClick={()=>setM24(i)}>{m}</button>
                    ))}
                  </div>
                </div>
              </GCard>
              {/* 2026 */}
              <GCard delay={50} hover={false}>
                <div style={{padding:"20px 22px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
                    <div style={{width:10,height:10,borderRadius:3,background:C.indigoG,flexShrink:0}}/>
                    <div style={{fontFamily:C.fD,fontSize:14,fontWeight:700,color:C.inkB}}>Período 2026</div>
                    <div style={{marginLeft:"auto",fontFamily:C.fD,fontSize:16,fontWeight:800,color:C.indigo}}>{MES_FULL[m25]}</div>
                  </div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                    {MES.map((m,i)=>(
                      <button key={i} style={selMes(i,25)} onClick={()=>setM25(i)}>{m}</button>
                    ))}
                  </div>
                </div>
              </GCard>
            </div>

            {/* KPIs comparativos */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
              {[
                {l:"Ingresos",         v24:ING24[m24],  v25:ING25[m25],  icon:"💰"},
                {l:"G. Administración",v24:null,         v25:GADM25[m25], icon:"🏢"},
                {l:"G. Ventas",        v24:null,         v25:GVTA25[m25], icon:"🛒"},
                {l:"Resultado Mes",    v24:null,         v25:UTIL25[m25], icon:"📈"},
                {l:"Disponible",       v24:DISP25[m24], v25:DISP25[m25], icon:"💵"},
                {l:"CxC Deudores",     v24:CXC25[m24],  v25:CXC25[m25],  icon:"📋"},
                {l:"Inventario",       v24:INV25[m24],  v25:INV25[m25],  icon:"📦"},
                {l:"Patrimonio",       v24:PAT25[m24],  v25:PAT25[m25],  icon:"🏆"},
              ].map((k,i)=>{
                const d=k.v24?((k.v25-k.v24)/k.v24*100).toFixed(1):null;
                return(
                  <GCard key={i} delay={i*30} style={{padding:"18px 20px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                      <div style={{fontFamily:C.fM,fontSize:8,color:C.inkD,letterSpacing:"0.12em",textTransform:"uppercase"}}>{k.l}</div>
                      <span style={{fontSize:14}}>{k.icon}</span>
                    </div>
                    {k.v24&&(
                      <div style={{fontFamily:C.fM,fontSize:10,color:C.amber,marginBottom:2}}>
                        {MES_FULL[m24]} 2025: <span style={{fontWeight:600}}>{fm(k.v24)}</span>
                      </div>
                    )}
                    <div style={{fontFamily:C.fD,fontSize:22,fontWeight:700,color:C.indigo,letterSpacing:"-0.02em"}}>{fm(k.v25)}</div>
                    <div style={{fontFamily:C.fM,fontSize:9,color:C.inkD,marginBottom:8}}>{MES_FULL[m25]} 2026</div>
                    {d&&<DeltaBadge v24={k.v24} v25={k.v25}/>}
                  </GCard>
                );
              })}
            </div>

            {/* Gráfica comparativa ingresos mes */}
            <div style={{display:"grid",gridTemplateColumns:"3fr 2fr",gap:12}}>
              <GCard delay={200} hover={false}>
                <div style={{padding:"22px 24px 6px"}}>
                  <div style={{fontFamily:C.fD,fontSize:15,fontWeight:700,color:C.inkB}}>Ingresos — Todos los meses 2025 vs 2026</div>
                  <div style={{fontFamily:C.fB,fontSize:11,color:C.inkD,fontWeight:300,marginTop:3}}>
                    Marcados: <span style={{color:C.amber,fontWeight:500}}>{MES_FULL[m24]} 2025</span> · <span style={{color:C.indigo,fontWeight:500}}>{MES_FULL[m25]} 2026</span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart data={mesAMesData} margin={{top:8,right:20,left:0,bottom:8}}>
                    <GDefs/>
                    <CartesianGrid strokeDasharray="2 6" stroke="rgba(99,102,241,0.06)" vertical={false}/>
                    <XAxis dataKey="m" tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false}/>
                    <YAxis tickFormatter={v=>`$${(v/1e3).toFixed(0)}MM`} tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false} width={50}/>
                    <Tooltip content={<PremTT/>}/>
                    <Bar dataKey="v24" name="Ingresos 2025" fill={C.amber}    fillOpacity={0.5} radius={[4,4,0,0]} barSize={14}/>
                    <Bar dataKey="v25" name="Ingresos 2026" fill={C.indigo}   fillOpacity={0.75} radius={[4,4,0,0]} barSize={14}/>
                    <Line type="monotone" dataKey="delta" name="Variación Δ" stroke={C.emerald} strokeWidth={1.5} dot={false} strokeDasharray="4 3"/>
                  </ComposedChart>
                </ResponsiveContainer>
              </GCard>
              <GCard delay={250} hover={false}>
                <div style={{padding:"22px 22px"}}>
                  <div style={{fontFamily:C.fD,fontSize:15,fontWeight:700,color:C.inkB,marginBottom:16}}>
                    Resumen — {MES_FULL[m25]} 2026
                  </div>
                  {[
                    {l:"Ingresos del mes",  v:ING25[m25],  c:C.indigo, icon:"💰"},
                    {l:"G. Admin",          v:GADM25[m25], c:C.amber,  icon:"🏢"},
                    {l:"G. Ventas",         v:GVTA25[m25], c:C.rose,   icon:"🛒"},
                    {l:"G. No Operac.",     v:GNOOP25[m25],c:C.inkD,   icon:"📎"},
                    {l:"Resultado",         v:UTIL25[m25], c:UTIL25[m25]>=0?C.emerald:C.rose, icon:"📈"},
                    {l:"Inventario",        v:INV25[m25],  c:C.rose,   icon:"📦"},
                    {l:"Patrimonio",        v:PAT25[m25],  c:C.emerald,icon:"🏆"},
                  ].map((k,i)=>(
                    <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderBottom:i<6?"1px solid rgba(99,102,241,0.06)":"none"}}>
                      <div style={{display:"flex",alignItems:"center",gap:7}}>
                        <span style={{fontSize:11}}>{k.icon}</span>
                        <span style={{fontFamily:C.fB,fontSize:11.5,color:C.inkC,fontWeight:300}}>{k.l}</span>
                      </div>
                      <span style={{fontFamily:C.fM,fontSize:12,color:k.c,fontWeight:600}}>{fm(k.v)}</span>
                    </div>
                  ))}
                </div>
              </GCard>
            </div>
          </div>
        )}

        {/* -- MODO: RANGO DE MESES ------------------------------------ */}
        {modo==="rango"&&(
          <div>
            {/* Selectores de rango */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
              <GCard delay={0} hover={false}>
                <div style={{padding:"20px 22px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                    <div style={{width:10,height:10,borderRadius:3,background:C.amberG,flexShrink:0}}/>
                    <div style={{fontFamily:C.fD,fontSize:14,fontWeight:700,color:C.inkB}}>Rango 2025</div>
                    <div style={{marginLeft:"auto",fontFamily:C.fM,fontSize:11,color:C.amber,fontWeight:500}}>{MES[rango24.from]} → {MES[rango24.to]}</div>
                  </div>
                  <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:10}}>
                    <span style={{fontFamily:C.fM,fontSize:9,color:C.inkD,width:32}}>Desde</span>
                    <div style={{display:"flex",flexWrap:"wrap",gap:4,flex:1}}>
                      {MES.map((m,i)=>(
                        <button key={i} style={{...selMes(i,24),background:i===rango24.from?"#FEF3C7":"transparent",fontWeight:i===rango24.from?700:400}} onClick={()=>setRango24(r=>({...r,from:Math.min(i,r.to)}))}>{m}</button>
                      ))}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:10,alignItems:"center"}}>
                    <span style={{fontFamily:C.fM,fontSize:9,color:C.inkD,width:32}}>Hasta</span>
                    <div style={{display:"flex",flexWrap:"wrap",gap:4,flex:1}}>
                      {MES.map((m,i)=>(
                        <button key={i} style={{...selMes(i,24),background:i===rango24.to?"#FEF3C7":"transparent",fontWeight:i===rango24.to?700:400,opacity:i<rango24.from?0.3:1}} onClick={()=>setRango24(r=>({...r,to:Math.max(i,r.from)}))}>{m}</button>
                      ))}
                    </div>
                  </div>
                  {/* Highlight strip */}
                  <div style={{display:"flex",marginTop:10,gap:2}}>
                    {MES.map((_,i)=>(
                      <div key={i} style={{flex:1,height:4,borderRadius:2,background:i>=rango24.from&&i<=rango24.to?C.amber:"rgba(99,102,241,0.08)",transition:"background 0.15s"}}/>
                    ))}
                  </div>
                </div>
              </GCard>
              <GCard delay={50} hover={false}>
                <div style={{padding:"20px 22px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
                    <div style={{width:10,height:10,borderRadius:3,background:C.indigoG,flexShrink:0}}/>
                    <div style={{fontFamily:C.fD,fontSize:14,fontWeight:700,color:C.inkB}}>Rango 2026</div>
                    <div style={{marginLeft:"auto",fontFamily:C.fM,fontSize:11,color:C.indigo,fontWeight:500}}>{MES[rango25.from]} → {MES[rango25.to]}</div>
                  </div>
                  <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:10}}>
                    <span style={{fontFamily:C.fM,fontSize:9,color:C.inkD,width:32}}>Desde</span>
                    <div style={{display:"flex",flexWrap:"wrap",gap:4,flex:1}}>
                      {MES.map((m,i)=>(
                        <button key={i} style={{...selMes(i,25),background:i===rango25.from?"rgba(99,102,241,0.15)":"transparent",fontWeight:i===rango25.from?700:400}} onClick={()=>setRango25(r=>({...r,from:Math.min(i,r.to)}))}>{m}</button>
                      ))}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:10,alignItems:"center"}}>
                    <span style={{fontFamily:C.fM,fontSize:9,color:C.inkD,width:32}}>Hasta</span>
                    <div style={{display:"flex",flexWrap:"wrap",gap:4,flex:1}}>
                      {MES.map((m,i)=>(
                        <button key={i} style={{...selMes(i,25),background:i===rango25.to?"rgba(99,102,241,0.15)":"transparent",fontWeight:i===rango25.to?700:400,opacity:i<rango25.from?0.3:1}} onClick={()=>setRango25(r=>({...r,to:Math.max(i,r.from)}))}>{m}</button>
                      ))}
                    </div>
                  </div>
                  <div style={{display:"flex",marginTop:10,gap:2}}>
                    {MES.map((_,i)=>(
                      <div key={i} style={{flex:1,height:4,borderRadius:2,background:i>=rango25.from&&i<=rango25.to?C.indigo:"rgba(99,102,241,0.08)",transition:"background 0.15s"}}/>
                    ))}
                  </div>
                </div>
              </GCard>
            </div>

            {/* KPIs de rango */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
              {[
                {l:"Ingresos 2025",   v:ing24R, color:C.amber,  sub:`${MES[rango24.from]}–${MES[rango24.to]} 2025`, icon:"📅"},
                {l:"Ingresos 2026",   v:ing25R, color:C.indigo, sub:`${MES[rango25.from]}–${MES[rango25.to]} 2026`, icon:"📈", comp:ing24R},
                {l:"G. Operativos 25",v:gadm25R+gvta25R, color:C.rose, sub:`${MES[rango25.from]}–${MES[rango25.to]} 2026`, icon:"🏢"},
                {l:"Resultado 2026",  v:util25R, color:C.emerald,sub:`${MES[rango25.from]}–${MES[rango25.to]} 2026`, icon:"✅"},
              ].map((k,i)=>{
                const delta=k.comp?((k.v-k.comp)/k.comp*100).toFixed(1):null;
                return(
                  <GCard key={i} delay={i*40} style={{padding:"20px 22px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                      <div style={{fontFamily:C.fM,fontSize:8,color:C.inkD,letterSpacing:"0.12em",textTransform:"uppercase",lineHeight:1.4}}>{k.l}</div>
                      <span style={{fontSize:16}}>{k.icon}</span>
                    </div>
                    <div style={{fontFamily:C.fD,fontSize:26,fontWeight:800,color:k.color,letterSpacing:"-0.02em"}}>{fm(k.v)}</div>
                    <div style={{fontFamily:C.fB,fontSize:10,color:C.inkD,fontWeight:300,marginTop:6,marginBottom:delta?8:0}}>{k.sub}</div>
                    {delta&&(
                      <div style={{display:"inline-flex",alignItems:"center",gap:5,background:parseFloat(delta)>=0?C.emeraldL:C.roseL,borderRadius:20,padding:"3px 10px"}}>
                        <span style={{fontSize:9}}>{parseFloat(delta)>=0?"▲":"▼"}</span>
                        <span style={{fontFamily:C.fM,fontSize:10,color:parseFloat(delta)>=0?C.emerald:C.rose,fontWeight:600}}>{Math.abs(parseFloat(delta))}% vs 2025</span>
                      </div>
                    )}
                  </GCard>
                );
              })}
            </div>

            {/* Gráfica de rango */}
            <GCard delay={200} hover={false}>
              <div style={{padding:"22px 24px 6px"}}>
                <div style={{fontFamily:C.fD,fontSize:15,fontWeight:700,color:C.inkB}}>
                  Ingresos mes a mes dentro del rango seleccionado
                </div>
                <div style={{fontFamily:C.fB,fontSize:11,color:C.inkD,fontWeight:300,marginTop:3}}>
                  <span style={{color:C.amber,fontWeight:500}}>■ 2025: {MES[rango24.from]}–{MES[rango24.to]}</span>
                  <span style={{margin:"0 12px",color:C.inkD}}>·</span>
                  <span style={{color:C.indigo,fontWeight:500}}>■ 2026: {MES[rango25.from]}–{MES[rango25.to]}</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={rangoData} margin={{top:8,right:20,left:0,bottom:8}} barGap={2} barSize={16}>
                  <GDefs/>
                  <CartesianGrid strokeDasharray="2 6" stroke="rgba(99,102,241,0.06)" vertical={false}/>
                  <XAxis dataKey="m" tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false}/>
                  <YAxis tickFormatter={v=>`$${(v/1e3).toFixed(0)}MM`} tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false} width={50}/>
                  <Tooltip content={<PremTT/>}/>
                  <Bar dataKey="ing24"  name="Ingresos 2025" fill={C.amber}  fillOpacity={0.65} radius={[4,4,0,0]}/>
                  <Bar dataKey="ing25"  name="Ingresos 2026" fill={C.indigo} fillOpacity={0.8}  radius={[4,4,0,0]}/>
                  <Line type="monotone" dataKey="util25" name="Resultado 25" stroke={C.emerald} strokeWidth={2.5} dot={{r:3,fill:C.emerald}} connectNulls={false}/>
                </ComposedChart>
              </ResponsiveContainer>
            </GCard>
          </div>
        )}

        {/* -- MODO: TABLA COMPLETA ------------------------------------ */}
        {modo==="tabla"&&(
          <div>
            <GCard delay={0} hover={false}>
              <div style={{padding:"22px 24px 0"}}>
                <div style={{fontFamily:C.fD,fontSize:15,fontWeight:700,color:C.inkB,marginBottom:4}}>Ingresos comparados — Todos los meses 2025 vs 2026</div>
                <div style={{fontFamily:C.fB,fontSize:11,color:C.inkD,fontWeight:300,marginBottom:16}}>Haz clic en cualquier fila para resaltarla · Variación absoluta y porcentual mes a mes</div>
              </div>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead>
                    <tr style={{background:"linear-gradient(135deg,#0F1117,#1E1B4B)"}}>
                      {["MES","INGRESOS 2025","INGRESOS 2026","Δ ABSOLUTA","Δ %","G. ADM 2026","G. VTA 2026","G. NO OP. 2026","RESULTADO 2026"].map((h,j)=>(
                        <th key={j} style={{padding:"11px 14px",textAlign:j===0?"left":"right",color:"rgba(165,180,252,0.7)",fontFamily:C.fM,fontSize:8,letterSpacing:"0.12em",fontWeight:400,whiteSpace:"nowrap"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {MES.map((m,i)=>{
                      const d=ING25[i]-ING24[i], dp=(d/ING24[i]*100).toFixed(1);
                      const totGasto=GADM25[i]+GVTA25[i]+GNOOP25[i];
                      return(
                        <tr key={i} style={{borderBottom:"1px solid rgba(99,102,241,0.05)",background:i%2?"rgba(99,102,241,0.015)":"transparent",cursor:"pointer",transition:"background 0.15s"}}
                          onMouseEnter={e=>e.currentTarget.style.background="rgba(99,102,241,0.07)"}
                          onMouseLeave={e=>e.currentTarget.style.background=i%2?"rgba(99,102,241,0.015)":"transparent"}>
                          <td style={{padding:"11px 14px",fontFamily:C.fD,fontSize:12,fontWeight:700,color:C.inkB}}>{m}</td>
                          <td style={{padding:"11px 14px",textAlign:"right",fontFamily:C.fM,fontSize:10.5,color:C.amber,fontWeight:500}}>{fm(ING24[i])}</td>
                          <td style={{padding:"11px 14px",textAlign:"right",fontFamily:C.fM,fontSize:10.5,color:C.indigo,fontWeight:600}}>{fm(ING25[i])}</td>
                          <td style={{padding:"11px 14px",textAlign:"right",fontFamily:C.fM,fontSize:10.5,color:d>=0?C.emerald:C.rose,fontWeight:500}}>{d>=0?"+":""}{fm(d)}</td>
                          <td style={{padding:"11px 14px",textAlign:"right"}}>
                            <span style={{fontFamily:C.fM,fontSize:10,color:parseFloat(dp)>=0?C.emerald:C.rose,fontWeight:600,background:parseFloat(dp)>=0?C.emeraldL:C.roseL,padding:"2px 9px",borderRadius:20}}>{parseFloat(dp)>=0?"+":""}{dp}%</span>
                          </td>
                          <td style={{padding:"11px 14px",textAlign:"right",fontFamily:C.fM,fontSize:10,color:C.amber}}>{fm(GADM25[i])}</td>
                          <td style={{padding:"11px 14px",textAlign:"right",fontFamily:C.fM,fontSize:10,color:C.rose}}>{fm(GVTA25[i])}</td>
                          <td style={{padding:"11px 14px",textAlign:"right",fontFamily:C.fM,fontSize:10,color:C.inkD}}>{fm(GNOOP25[i])}</td>
                          <td style={{padding:"11px 14px",textAlign:"right",fontFamily:C.fM,fontSize:10.5,color:UTIL25[i]>=0?C.emerald:C.rose,fontWeight:600}}>{fm(UTIL25[i])}</td>
                        </tr>
                      );
                    })}
                    {/* TOTALES */}
                    <tr style={{background:"linear-gradient(135deg,rgba(99,102,241,0.08),rgba(99,102,241,0.03))",borderTop:`2px solid ${C.indigo}30`}}>
                      <td style={{padding:"13px 14px",fontFamily:C.fD,fontSize:13,fontWeight:800,color:C.indigo}}>TOTAL AÑO</td>
                      <td style={{padding:"13px 14px",textAlign:"right",fontFamily:C.fM,fontSize:11,color:C.amber,fontWeight:700}}>{fm(IT24)}</td>
                      <td style={{padding:"13px 14px",textAlign:"right",fontFamily:C.fM,fontSize:11,color:C.indigo,fontWeight:700}}>{fm(IT)}</td>
                      <td style={{padding:"13px 14px",textAlign:"right",fontFamily:C.fM,fontSize:11,color:C.emerald,fontWeight:700}}>+{fm(IT-IT24)}</td>
                      <td style={{padding:"13px 14px",textAlign:"right"}}>
                        <span style={{fontFamily:C.fM,fontSize:11,color:C.emerald,fontWeight:700,background:C.emeraldL,padding:"3px 10px",borderRadius:20}}>+{YOY}%</span>
                      </td>
                      <td style={{padding:"13px 14px",textAlign:"right",fontFamily:C.fM,fontSize:11,color:C.amber,fontWeight:700}}>{fm(GT)}</td>
                      <td style={{padding:"13px 14px",textAlign:"right",fontFamily:C.fM,fontSize:11,color:C.rose,fontWeight:700}}>{fm(GV)}</td>
                      <td style={{padding:"13px 14px",textAlign:"right",fontFamily:C.fM,fontSize:11,color:C.inkC,fontWeight:700}}>{fm(GN)}</td>
                      <td style={{padding:"13px 14px",textAlign:"right",fontFamily:C.fM,fontSize:11,color:C.emerald,fontWeight:700}}>{fm(UT)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div style={{padding:"14px 24px 18px"}}>
                <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>
                  {[[C.amber,"■ 2025"],[C.indigo,"■ 2026"],[C.emerald,"■ Positivo"],[C.rose,"■ Negativo / Alerta"]].map(([c,l])=>(
                    <div key={l} style={{display:"flex",alignItems:"center",gap:5}}>
                      <span style={{fontFamily:C.fM,fontSize:10,color:c}}>{l}</span>
                    </div>
                  ))}
                </div>
              </div>
            </GCard>

            {/* Gráfica waterfall-style de variación mensual */}
            <GCard delay={100} hover={false} style={{marginTop:12}}>
              <div style={{padding:"22px 24px 6px"}}>
                <div style={{fontFamily:C.fD,fontSize:15,fontWeight:700,color:C.inkB}}>Variación de ingresos mes a mes (Δ 2026 vs 2025)</div>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={MES.map((m,i)=>({m,delta:ING25[i]-ING24[i]}))} margin={{top:8,right:20,left:0,bottom:8}}>
                  <CartesianGrid strokeDasharray="2 6" stroke="rgba(99,102,241,0.06)" vertical={false}/>
                  <XAxis dataKey="m" tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false}/>
                  <YAxis tickFormatter={v=>`$${(v/1e3).toFixed(0)}MM`} tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false} width={50}/>
                  <Tooltip content={<PremTT/>}/>
                  <Bar dataKey="delta" name="Δ Ingresos" radius={[4,4,0,0]} barSize={28}>
                    {MES.map((_,i)=><Cell key={i} fill={ING25[i]-ING24[i]>=0?C.emerald:C.rose} fillOpacity={0.75}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </GCard>
          </div>
        )}
      </div>
    );
  };

  /* ----------- INFORME EJECUTIVO -------------------------------------- */
  const INFORME_EJ=()=>{
    const [sec,setSec]=useState("portada");

    const NAV=[
      {id:"portada",  label:"Portada"},
      {id:"highlights",label:"Highlights"},
      {id:"cfo",      label:"Análisis Ejecutivo"},
      {id:"financiero",label:"Revisión Financiera"},
      {id:"balance",  label:"Balance & Solvencia"},
      {id:"alertas",  label:"Alertas & Oportunidades"},
      {id:"perspectivas",label:"Perspectivas 2026"},
    ];

    const Divider=({color})=>(
      <div style={{height:1,background:`linear-gradient(90deg,${color},transparent)`,margin:"28px 0",opacity:0.18}}/>
    );

    const PullQuote=({text,author})=>(
      <div style={{borderLeft:`3px solid ${C.indigo}`,paddingLeft:24,margin:"28px 0"}}>
        <p style={{fontFamily:C.fD,fontSize:18,fontWeight:600,color:C.inkB,lineHeight:1.55,letterSpacing:"-0.01em",fontStyle:"italic"}}>"{text}"</p>
        {author&&<p style={{fontFamily:C.fM,fontSize:10,color:C.inkD,letterSpacing:"0.12em",marginTop:8,textTransform:"uppercase"}}>{author}</p>}
      </div>
    );

    const StatBox=({label,value,sub,color,delta,icon})=>(
      <div style={{padding:"24px 28px",border:`1px solid rgba(99,102,241,0.1)`,borderRadius:12,background:"#fff",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:0,left:0,width:4,height:"100%",background:color||C.indigo}}/>
        <div style={{fontFamily:C.fM,fontSize:8.5,color:C.inkD,letterSpacing:"0.14em",textTransform:"uppercase",marginBottom:10}}>{label}</div>
        <div style={{fontFamily:C.fD,fontSize:30,fontWeight:800,color,letterSpacing:"-0.03em",lineHeight:1}}>{value}</div>
        {sub&&<div style={{fontFamily:C.fB,fontSize:11,color:C.inkD,marginTop:8,fontWeight:300}}>{sub}</div>}
        {delta&&(
          <div style={{display:"inline-flex",alignItems:"center",gap:4,marginTop:10,background:parseFloat(delta)>=0?C.emeraldL:C.roseL,borderRadius:20,padding:"3px 10px"}}>
            <span style={{fontSize:9}}>{parseFloat(delta)>=0?"▲":"▼"}</span>
            <span style={{fontFamily:C.fM,fontSize:10,color:parseFloat(delta)>=0?C.emerald:C.rose,fontWeight:600}}>{Math.abs(parseFloat(delta))}% vs 2025</span>
          </div>
        )}
        {icon&&<div style={{position:"absolute",right:20,top:"50%",transform:"translateY(-50%)",fontSize:28,opacity:0.08}}>{icon}</div>}
      </div>
    );

    /* -- PORTADA ------------------------------------------------------- */
    const SecPortada=()=>(
      <div>
        {/* Hero */}
        <div style={{background:"linear-gradient(135deg,#0A0F1E 0%,#1E1B4B 45%,#0F2027 100%)",borderRadius:16,padding:"64px 56px",marginBottom:20,position:"relative",overflow:"hidden"}}>
          {/* Orbs */}
          <div style={{position:"absolute",top:-80,right:-80,width:320,height:320,borderRadius:"50%",background:"radial-gradient(circle,rgba(99,102,241,0.2) 0%,transparent 70%)"}}/>
          <div style={{position:"absolute",bottom:-60,left:100,width:240,height:240,borderRadius:"50%",background:"radial-gradient(circle,rgba(5,150,105,0.12) 0%,transparent 70%)"}}/>
          {/* Content */}
          <div style={{position:"relative",zIndex:2}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:40}}>
              <div style={{width:36,height:36,borderRadius:10,background:"linear-gradient(135deg,#6366F1,#8B5CF6)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,boxShadow:"0 6px 20px rgba(99,102,241,0.5)"}}>⬡</div>
              <div>
                <div style={{fontFamily:C.fM,fontSize:9,color:"rgba(165,180,252,0.7)",letterSpacing:"0.2em"}}>CFO INTELLIGENCE PLATFORM</div>
                <div style={{fontFamily:C.fD,fontSize:13,fontWeight:700,color:"rgba(255,255,255,0.9)",letterSpacing:"-0.01em"}}>{empresa||""}</div>
              </div>
              <div style={{marginLeft:"auto",fontFamily:C.fM,fontSize:9,color:"rgba(165,180,252,0.5)",letterSpacing:"0.1em"}}>NIT: 901.XXX.XXX–X</div>
            </div>
            <div style={{fontFamily:C.fD,fontSize:48,fontWeight:800,color:"#fff",lineHeight:1.1,letterSpacing:"-0.03em",marginBottom:20,maxWidth:600}}>
              INFORME EJECUTIVO<br/>
              <span style={{background:"linear-gradient(90deg,#818CF8,#34D399)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>FINANCIERO CONTABLE</span>
            </div>
            <div style={{fontFamily:C.fB,fontSize:15,color:"rgba(165,180,252,0.75)",fontWeight:300,lineHeight:1.6,maxWidth:520,marginBottom:40}}>
              Análisis integral de la posición financiera, resultados del ejercicio, indicadores de gestión y perspectivas estratégicas para el período enero–diciembre 2026.
            </div>
            <div style={{display:"flex",gap:40,flexWrap:"wrap"}}>
              {[
                {l:"Periodo",  v:periodo},
                {l:"Preparado por",v:"Doly Mayeli Blanco G."},
                {l:"Cargo",   v:"Contadora · TP 199.692-T"},
                {l:"Fuente",  v:"Sistema Contable · "+empresa},
              ].map((k,i)=>(
                <div key={i}>
                  <div style={{fontFamily:C.fM,fontSize:8,color:"rgba(165,180,252,0.45)",letterSpacing:"0.16em",textTransform:"uppercase",marginBottom:4}}>{k.l}</div>
                  <div style={{fontFamily:C.fD,fontSize:13,fontWeight:600,color:"rgba(255,255,255,0.85)"}}>{k.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Snapshot de cifras clave */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:20}}>
          {[
            {label:"Ingresos Operacionales 2026", value:fm(IT),     sub:IT24>0?"Crecimiento del "+((IT-IT24)/IT24*100).toFixed(1)+"% frente a 2025":"Sin datos", color:C.indigo, delta:IT24>0?((IT-IT24)/IT24*100).toFixed(1):null, icon:"💰"},
            {label:"Resultado del Ejercicio",      value:fm(UT),     sub:IT>0?"Margen neto: "+MG_UT.toFixed(1)+"%":"Sin datos", color:C.emerald, delta:UT24>0?((UT-UT24)/Math.abs(UT24)*100).toFixed(0):null, icon:"📈"},
            {label:"Total Activos",                value:fm(DB.tact),sub:BAL24.tact>0?"Crecimiento del "+((DB.tact-BAL24.tact)/BAL24.tact*100).toFixed(1)+"% vs dic 2025":"Sin datos", color:C.cyan, delta:BAL24.tact>0?((DB.tact-BAL24.tact)/BAL24.tact*100).toFixed(1):null, icon:"📦"},
            {label:"Patrimonio Neto",              value:fm(DB.pat), sub:BAL24.pat>0?"vs "+fm(BAL24.pat)+" en 2025":"Sin datos", color:C.emerald, delta:BAL24.pat>0?((DB.pat-BAL24.pat)/BAL24.pat*100).toFixed(1):null, icon:"🏆"},
            {label:"Índice de Endeudamiento",      value:pct(END),   sub:"Reducción desde 54.3% en 2025",        color:C.rose,    delta:"-20.1",icon:"📉"},
            {label:"Rentabilidad sobre Patrimonio",value:`${ROE}%`,  sub:"Return on Equity — Dic 2026",          color:C.indigo,  delta:null,   icon:"⭐"},
          ].map((k,i)=>(
            <StatBox key={i} {...k}/>
          ))}
        </div>

        {/* Mensaje del informe */}
        <div style={{background:"linear-gradient(135deg,rgba(99,102,241,0.04),rgba(5,150,105,0.03))",border:`1px solid rgba(99,102,241,0.1)`,borderRadius:12,padding:"32px 36px"}}>
          <div style={{fontFamily:C.fM,fontSize:8.5,color:C.indigo,letterSpacing:"0.18em",textTransform:"uppercase",marginBottom:14}}>Sobre este informe</div>
          <p style={{fontFamily:C.fB,fontSize:13,color:C.inkC,lineHeight:1.85,fontWeight:300,maxWidth:860}}>
            El presente informe ejecutivo ha sido elaborado a partir de los datos del sistema Sistema Contable de {empresa||""} para el ejercicio contable enero–diciembre 2026. Incluye el análisis del Estado de Resultados (P&G), Estado de Situación Financiera, indicadores financieros clave, identificación de riesgos materiales y recomendaciones estratégicas orientadas a la toma de decisiones por parte de la alta dirección. Las cifras se expresan en millones de pesos colombianos (COP M) salvo indicación contraria.
          </p>
          <div style={{marginTop:20,display:"flex",alignItems:"center",gap:16}}>
            <div style={{width:40,height:40,borderRadius:"50%",background:C.indigoG,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontFamily:C.fD,fontSize:16,fontWeight:700,flexShrink:0}}>D</div>
            <div>
              <div style={{fontFamily:C.fD,fontSize:14,fontWeight:700,color:C.inkB}}>Doly Mayeli Blanco G.</div>
              <div style={{fontFamily:C.fM,fontSize:9,color:C.inkD,letterSpacing:"0.1em"}}>CONTADORA · TP 199.692-T · {empresa||""} · 2026</div>
            </div>
          </div>
        </div>
      </div>
    );

    /* -- HIGHLIGHTS ---------------------------------------------------- */
    const SecHighlights=()=>(
      <div>
        <div style={{fontFamily:C.fM,fontSize:8.5,color:C.inkD,letterSpacing:"0.18em",textTransform:"uppercase",marginBottom:4}}>Informe Ejecutivo · Desempeño 2026</div>
        <div style={{fontFamily:C.fD,fontSize:36,fontWeight:800,color:C.inkB,letterSpacing:"-0.03em",lineHeight:1.1,marginBottom:6}}>Highlights Financieros</div>
        <div style={{fontFamily:C.fB,fontSize:13,color:C.inkD,fontWeight:300,marginBottom:32}}>Entregamos un sólido desempeño financiero con ingresos récord y fuerte expansión patrimonial.</div>

        {/* Big number strip - Halma style */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:0,border:`1px solid rgba(99,102,241,0.1)`,borderRadius:12,overflow:"hidden",marginBottom:20,background:"#fff"}}>
          {[
            {label:"Ingresos",       value:fm(IT),       delta:IT24>0?"+"+((IT-IT24)/IT24*100).toFixed(1)+"%":"", note:IT24>0?"vs "+fm(IT24)+" en 2025":"", c:C.indigo, good:true},
            {label:"Resultado Neto", value:fm(UT),       delta:UT24>0?((UT-UT24)/Math.abs(UT24)*100).toFixed(0)+"%":"", note:UT24>0?"vs "+fm(UT24)+" en 2025":"", c:C.emerald, good:UT>0},
            {label:"Total Activos",  value:fm(DB.tact),  delta:BAL24.tact>0?"+"+((DB.tact-BAL24.tact)/BAL24.tact*100).toFixed(1)+"%":"", note:BAL24.tact>0?"vs "+fm(BAL24.tact)+" en 2025":"", c:C.cyan, good:true},
            {label:"Patrimonio",     value:fm(DB.pat),   delta:BAL24.pat>0?"+"+((DB.pat-BAL24.pat)/BAL24.pat*100).toFixed(1)+"%":"", note:BAL24.pat>0?"vs "+fm(BAL24.pat)+" en 2025":"", c:C.emerald, good:true},
          ].map((k,i)=>(
            <div key={i} style={{padding:"36px 40px",borderRight:i%2===0?`1px solid rgba(99,102,241,0.08)`:"none",borderBottom:i<2?`1px solid rgba(99,102,241,0.08)`:"none",position:"relative",overflow:"hidden"}}>
              <div style={{position:"absolute",bottom:-20,right:-10,fontFamily:C.fD,fontSize:80,fontWeight:900,color:k.c,opacity:0.04,letterSpacing:"-0.04em"}}>{i+1}</div>
              <div style={{fontFamily:C.fM,fontSize:8.5,color:C.inkD,letterSpacing:"0.16em",textTransform:"uppercase",marginBottom:12}}>{k.label}</div>
              <div style={{fontFamily:C.fD,fontSize:38,fontWeight:900,color:k.c,letterSpacing:"-0.03em",lineHeight:1,marginBottom:10}}>{k.value}</div>
              <div style={{display:"inline-flex",alignItems:"center",gap:5,background:k.good?C.emeraldL:C.roseL,borderRadius:20,padding:"4px 12px",marginBottom:8}}>
                <span style={{fontFamily:C.fM,fontSize:10,color:k.good?C.emerald:C.rose,fontWeight:700}}>{k.delta}</span>
              </div>
              <div style={{fontFamily:C.fB,fontSize:11,color:C.inkD,fontWeight:300}}>{k.note}</div>
            </div>
          ))}
        </div>

        {/* Indicadores secundarios */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:20}}>
          {[
            {l:"Margen Neto",       v:`${(UT/IT*100).toFixed(1)}%`, note:"Resultado/Ingresos",    c:C.emerald},
            {l:"Endeudamiento",     v:pct(END),                     note:"Solidez financiera",    c:C.rose},
            {l:"ROE",               v:`${ROE}%`,                    note:"Rentabilidad patrim.",  c:C.indigo},
            {l:"Razón Corriente",   v:LIQ,                          note:"Capacidad de pago",     c:C.cyan},
            {l:"Inventario/Activo", v:`${(DB.inv/DB.tact*100).toFixed(1)}%`,note:"⚠ Alta concentración",c:C.rose},
            {l:"Días Cartera",      v:DCXC+"d",  note:BAL24.cxc>0?"vs "+DCXC24+"d en 2025":"",c:parseFloat(DCXC)>B.dcxc[1]?C.rose:C.emerald},
            {l:"Disponible",        v:fm(DB.disp),                  note:"⚠ Liquidez inmediata",  c:C.amber},
            {l:"Deuda/EBITDA",      v:`${(DB.tpas/UT).toFixed(2)}x`,note:"Solvencia de largo plazo",c:C.emerald},
          ].map((k,i)=>(
            <div key={i} style={{padding:"18px 20px",background:"#fff",border:`1px solid rgba(99,102,241,0.08)`,borderRadius:10,borderTop:`2px solid ${k.c}`}}>
              <div style={{fontFamily:C.fM,fontSize:8,color:C.inkD,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:8}}>{k.l}</div>
              <div style={{fontFamily:C.fD,fontSize:20,fontWeight:800,color:k.c,letterSpacing:"-0.02em"}}>{k.v}</div>
              <div style={{fontFamily:C.fB,fontSize:10,color:C.inkD,fontWeight:300,marginTop:6}}>{k.note}</div>
            </div>
          ))}
        </div>

        {/* Charts row */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <GCard hover={false}>
            <div style={{padding:"20px 22px 6px"}}>
              <div style={{fontFamily:C.fD,fontSize:14,fontWeight:700,color:C.inkB}}>Evolución de Ingresos 2025 → 2026</div>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <ComposedChart data={MES.map((m,i)=>({m,v24:ING24[i],v25:ING25[i]}))} barSize={12} margin={{top:6,right:16,left:0,bottom:6}}>
                <GDefs/>
                <CartesianGrid strokeDasharray="2 6" stroke="rgba(99,102,241,0.06)" vertical={false}/>
                <XAxis dataKey="m" tick={{fill:C.inkD,fontSize:8,fontFamily:C.fM}} axisLine={false} tickLine={false}/>
                <YAxis tickFormatter={v=>`$${(v/1e3).toFixed(0)}MM`} tick={{fill:C.inkD,fontSize:8,fontFamily:C.fM}} axisLine={false} tickLine={false} width={46}/>
                <Tooltip content={<PremTT/>}/>
                <Bar dataKey="v24" name="2025" fill={C.amber} fillOpacity={0.5} radius={[3,3,0,0]}/>
                <Bar dataKey="v25" name="2026" fill={C.indigo} fillOpacity={0.85} radius={[3,3,0,0]}/>
              </ComposedChart>
            </ResponsiveContainer>
          </GCard>
          <GCard hover={false}>
            <div style={{padding:"20px 22px 6px"}}>
              <div style={{fontFamily:C.fD,fontSize:14,fontWeight:700,color:C.inkB}}>Resultado acumulado 2026</div>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={MES.map((m,i)=>({m,acum:sum(UTIL25.slice(0,i+1)),ing:sum(ING25.slice(0,i+1))}))} margin={{top:6,right:16,left:0,bottom:6}}>
                <GDefs/>
                <CartesianGrid strokeDasharray="2 6" stroke="rgba(99,102,241,0.06)" vertical={false}/>
                <XAxis dataKey="m" tick={{fill:C.inkD,fontSize:8,fontFamily:C.fM}} axisLine={false} tickLine={false}/>
                <YAxis tickFormatter={v=>`$${(v/1e3).toFixed(0)}MM`} tick={{fill:C.inkD,fontSize:8,fontFamily:C.fM}} axisLine={false} tickLine={false} width={46}/>
                <Tooltip content={<PremTT/>}/>
                <Area type="monotone" dataKey="ing"  name="Ingresos Acum."  stroke={C.indigo}  fill="url(#gInd)" strokeWidth={1.5} fillOpacity={0.3}/>
                <Area type="monotone" dataKey="acum" name="Resultado Acum." stroke={C.emerald} fill="url(#gEme)" strokeWidth={2.5}/>
              </AreaChart>
            </ResponsiveContainer>
          </GCard>
        </div>
      </div>
    );

    /* -- CARTA CFO ------------------------------------------------------ */
    const SecCFO=()=>(
      <div style={{maxWidth:820}}>
        <div style={{fontFamily:C.fM,fontSize:8.5,color:C.inkD,letterSpacing:"0.18em",textTransform:"uppercase",marginBottom:4}}>Informe Ejecutivo · Análisis de la Dirección</div>
        <div style={{fontFamily:C.fD,fontSize:32,fontWeight:800,color:C.inkB,letterSpacing:"-0.02em",lineHeight:1.15,marginBottom:24}}>Análisis Ejecutivo Financiero</div>

        <Divider/>

        <p style={{fontFamily:C.fB,fontSize:14,color:C.inkC,lineHeight:1.9,fontWeight:300,marginBottom:18}}>
          Es para mí un honor presentar el informe ejecutivo financiero de <strong style={{fontWeight:600,color:C.inkB}}>{empresa||""}</strong> correspondiente al ejercicio contable enero–diciembre de 2026. Este reporte refleja un año de transformación significativa para la compañía, marcado por un crecimiento sostenido en ingresos, una notable expansión del patrimonio y un fortalecimiento estructural del balance general.
        </p>

        <PullQuote
          text="MEQ logró incrementar sus ingresos en un 18%, consolidar un patrimonio 2.3 veces mayor al del año anterior y reducir su endeudamiento al 34.2%, sentando bases sólidas para el crecimiento sostenible."
          author="Análisis Financiero Ejecutivo · {empresa||''}"
        />

        <p style={{fontFamily:C.fB,fontSize:14,color:C.inkC,lineHeight:1.9,fontWeight:300,marginBottom:18}}>
          Durante 2026, la compañía registró <strong style={{color:C.indigo,fontWeight:600}}>ingresos totales de </strong><strong style={{color:C.indigo,fontWeight:600}}>{fm(IT)} COP</strong>, frente a {fm(IT24)} en 2025, evidenciando una expansión del {YOY}%. El resultado del ejercicio ascendió a <strong style={{color:C.emerald,fontWeight:600}}>{fm(UT)}</strong>, calculado conforme PUC D.2649/93.
        </p>

        <p style={{fontFamily:C.fB,fontSize:14,color:C.inkC,lineHeight:1.9,fontWeight:300,marginBottom:18}}>
          En el frente del balance general, el activo total varió un <strong style={{color:C.cyan,fontWeight:600}}>{Math.abs(parseFloat(BAL24.tact>0?((DB.tact-BAL24.tact)/BAL24.tact*100).toFixed(1):0))}%</strong> para alcanzar {fm(DB.tact)}. El patrimonio neto {BAL24.pat>0?"pasó de "+fm(BAL24.pat)+" a "+fm(DB.pat):"registró "+fm(DB.pat)}, fortaleciendo la estructura financiera de largo plazo.
        </p>

        <Divider/>

        <div style={{fontFamily:C.fD,fontSize:18,fontWeight:700,color:C.inkB,marginBottom:14}}>Áreas de atención prioritaria</div>

        <p style={{fontFamily:C.fB,fontSize:14,color:C.inkC,lineHeight:1.9,fontWeight:300,marginBottom:18}}>
          Sin perjuicio del desempeño positivo general, el análisis financiero evidencia dos alertas que requieren atención inmediata de la alta dirección. En primer lugar, el <strong style={{color:C.rose,fontWeight:600}}>inventario acumuló $14,518 millones</strong>, representando el 66% del activo total y creciendo un 84.5% frente a 2025, con una velocidad de expansión muy superior al crecimiento de ingresos. Esta dinámica sugiere la necesidad urgente de revisar la política de abastecimiento y rotación de mercancías.
        </p>

        <p style={{fontFamily:C.fB,fontSize:14,color:C.inkC,lineHeight:1.9,fontWeight:300,marginBottom:18}}>
          En segundo lugar, los <strong style={{color:C.rose,fontWeight:600}}>días de cartera ascendieron a {DCXC} días</strong> al cierre de 2026{BAL24.cxc>0?", frente a "+DCXC24+" días en 2025":""}. Este nivel de cartera, combinado con el bajo nivel de disponible ({fm(DB.disp)}, {DB.tact>0?(DB.disp/DB.tact*100).toFixed(1):0}% del activo), representa el principal riesgo de liquidez operativa a gestionar en el corto plazo.
        </p>

        <PullQuote
          text="La fortaleza de nuestro balance y la solvencia demostrada nos posicionan bien para abordar los desafíos de liquidez y rotación de activos que serán prioritarios en 2026."
          author="Doly Mayeli Blanco G. · Contadora · MEQ Tecnología Médica SAS"
        />

        <p style={{fontFamily:C.fB,fontSize:14,color:C.inkC,lineHeight:1.9,fontWeight:300,marginBottom:18}}>
          En términos de solvencia, el índice de endeudamiento se redujo significativamente al <strong style={{color:C.emerald,fontWeight:600}}>34.2%</strong> desde el 54.3% de 2025, lo que otorga a MEQ un amplio margen de maniobra financiera y capacidad de apalancamiento para inversiones estratégicas. La razón corriente de 4.93x confirma una posición de liquidez estructural sólida, aunque la concentración en inventarios modera la liquidez inmediata disponible.
        </p>

      </div>
    );

    /* -- REVISIÓN FINANCIERA ------------------------------------------- */
    const SecFinanciero=()=>(
      <div>
        <div style={{fontFamily:C.fM,fontSize:8.5,color:C.inkD,letterSpacing:"0.18em",textTransform:"uppercase",marginBottom:4}}>Informe Ejecutivo · Estado de Resultados</div>
        <div style={{fontFamily:C.fD,fontSize:32,fontWeight:800,color:C.inkB,letterSpacing:"-0.02em",lineHeight:1.15,marginBottom:24}}>Revisión Financiera — P&G 2026</div>

        {/* Puente de ingresos - Halma style bridge */}
        <div style={{background:"#fff",border:`1px solid rgba(99,102,241,0.1)`,borderRadius:12,padding:"28px 32px",marginBottom:16}}>
          <div style={{fontFamily:C.fD,fontSize:16,fontWeight:700,color:C.inkB,marginBottom:4}}>Composición del crecimiento de ingresos 2025 → 2026</div>
          <div style={{fontFamily:C.fB,fontSize:11,color:C.inkD,fontWeight:300,marginBottom:24}}>{IT24>0?("Variación de "+fm(IT24)+" a "+fm(IT)+" · Incremento: +"+fm(IT-IT24)+" (+"+YOY+"%)") :"Carga los archivos Excel para ver la evolución"}</div>
          <div style={{position:"relative",height:200,marginBottom:8}}>
            <div style={{position:"absolute",bottom:36,left:0,right:0,height:1,background:"rgba(99,102,241,0.1)"}}/>
            {[
              {l:"2025",     v:11980, base:0,    c:C.amber,   delta:null},
              {l:"+H1 2026", v:2000,  base:11980, c:C.emerald, delta:"+$2,000M"},
              {l:"+H2 2026", v:155,   base:13980, c:C.cyan,    delta:"+$155M"},
              {l:"2026",     v:Math.round(IT*1000), base:0, c:C.indigo, delta:null},
            ].map((k,i,arr)=>{
              const maxV=14500;
              const barH = Math.max((k.v/maxV)*130, k.delta?6:0);
              const barBottom = 36 + (k.base/maxV)*130;
              const colW = 100/arr.length;
              return(
                <div key={i} style={{position:"absolute",left:`${i*colW+colW*0.15}%`,width:`${colW*0.7}%`,bottom:0,top:0}}>
                  {k.delta&&(
                    <div style={{position:"absolute",bottom:barBottom+barH+8,left:"50%",transform:"translateX(-50%)",
                      fontFamily:C.fM,fontSize:9,color:k.c,fontWeight:700,whiteSpace:"nowrap",
                      background:"rgba(255,255,255,0.95)",padding:"2px 8px",borderRadius:4,
                      border:`1px solid ${k.c}40`,boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
                      {k.delta}
                    </div>
                  )}
                  <div style={{position:"absolute",bottom:barBottom,left:0,right:0,height:barH,
                    background:k.c,borderRadius:"4px 4px 0 0",opacity:0.85}}/>
                  <div style={{position:"absolute",bottom:16,left:0,right:0,textAlign:"center",
                    fontFamily:C.fM,fontSize:8,color:C.inkD,letterSpacing:"0.06em"}}>{k.l}</div>
                  <div style={{position:"absolute",bottom:0,left:0,right:0,textAlign:"center",
                    fontFamily:C.fM,fontSize:10,fontWeight:600,color:k.c}}>${(k.v/1e3).toFixed(1)}MM</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tabla P&G ejecutiva */}
        <div style={{background:"#fff",border:`1px solid rgba(99,102,241,0.1)`,borderRadius:12,marginBottom:16,overflow:"hidden"}}>
          <div style={{padding:"22px 28px 0"}}>
            <div style={{fontFamily:C.fD,fontSize:16,fontWeight:700,color:C.inkB,marginBottom:16}}>Estado de Resultados Comparativo — Cifras en millones COP</div>
          </div>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr style={{background:"linear-gradient(135deg,#0F1117,#1E1B4B)"}}>
                {["CONCEPTO","2025","2026","Δ ABSOLUTA","Δ %","PARTICIPACIÓN 2026"].map((h,j)=>(
                  <th key={j} style={{padding:"12px 20px",textAlign:j===0?"left":"right",color:"rgba(165,180,252,0.7)",fontFamily:C.fM,fontSize:8,letterSpacing:"0.12em",fontWeight:400,whiteSpace:"nowrap"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                {l:"Ingresos Operacionales",  v24:IT24,  v25:IT,  c:C.indigo,  b:true},
                {l:"Gastos de Administración",v24:978,   v25:GT,  c:C.inkC,  sign:-1},
                {l:"Gastos de Ventas",         v24:2522,  v25:GV,  c:C.inkC,  sign:-1},
                {l:"Gastos No Operacionales",  v24:null,  v25:GN,  c:C.inkC,  sign:-1},
                {l:"RESULTADO DEL EJERCICIO",  v24:UT24, v25:UT, c:C.emerald,b:true},
              ].map((r,i)=>{
                const d = r.v24 ? r.v25 - r.v24 : null;
                const dp = r.v24 && r.v24!==0 ? (d/Math.abs(r.v24)*100).toFixed(1) : null;
                const pct2026 = (r.v25/IT*100).toFixed(1);
                return(
                  <tr key={i} style={{background:r.b?"rgba(99,102,241,0.03)":i%2?"rgba(99,102,241,0.01)":"transparent",borderBottom:`1px solid rgba(99,102,241,0.05)`}}>
                    <td style={{padding:"13px 20px",fontFamily:C.fB,fontSize:r.b?12:11.5,color:r.b?r.c:C.inkC,fontWeight:r.b?600:300,paddingLeft:r.b?20:32}}>{r.l}</td>
                    <td style={{padding:"13px 20px",textAlign:"right",fontFamily:C.fM,fontSize:10,color:C.inkD}}>{r.v24?r.v24.toFixed(0):"—"}</td>
                    <td style={{padding:"13px 20px",textAlign:"right",fontFamily:C.fM,fontSize:r.b?12:10,color:r.c||C.inkC,fontWeight:r.b?700:400}}>{r.v25.toFixed(0)}</td>
                    <td style={{padding:"13px 20px",textAlign:"right",fontFamily:C.fM,fontSize:10,color:d>=0?C.emerald:C.rose}}>{d!=null?(d>=0?"+":"")+d.toFixed(0):"—"}</td>
                    <td style={{padding:"13px 20px",textAlign:"right"}}>
                      {dp&&<span style={{fontFamily:C.fM,fontSize:10,color:parseFloat(dp)>=0?C.emerald:C.rose,background:parseFloat(dp)>=0?C.emeraldL:C.roseL,padding:"2px 9px",borderRadius:20,fontWeight:600}}>{parseFloat(dp)>=0?"+":""}{dp}%</span>}
                      {!dp&&<span style={{color:C.inkD,fontFamily:C.fM,fontSize:10}}>—</span>}
                    </td>
                    <td style={{padding:"13px 20px",textAlign:"right"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,justifyContent:"flex-end"}}>
                        <div style={{height:6,width:60,background:"rgba(99,102,241,0.08)",borderRadius:3,overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${Math.min(parseFloat(pct2026),100)}%`,background:r.c||C.inkD,borderRadius:3}}/>
                        </div>
                        <span style={{fontFamily:C.fM,fontSize:10,color:r.c||C.inkD,fontWeight:r.b?600:400}}>{pct2026}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{padding:"14px 24px 18px"}}>
            <span style={{fontFamily:C.fB,fontSize:10.5,color:C.inkD,fontWeight:300}}>Fuente: Sistema Contable · P&G enero–diciembre 2026 · Cifras en millones COP</span>
          </div>
        </div>

        {/* Análisis mensual */}
        <GCard hover={false}>
          <div style={{padding:"22px 24px 6px"}}>
            <div style={{fontFamily:C.fD,fontSize:15,fontWeight:700,color:C.inkB}}>Comportamiento mensual de ingresos y resultado — 2026</div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={MES.map((m,i)=>({m,ing:ING25[i],gadm:GADM25[i],gvta:GVTA25[i],util:UTIL25[i]}))} barGap={1} barSize={12} margin={{top:8,right:20,left:0,bottom:8}}>
              <GDefs/>
              <CartesianGrid strokeDasharray="2 6" stroke="rgba(99,102,241,0.06)" vertical={false}/>
              <XAxis dataKey="m" tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false}/>
              <YAxis tickFormatter={v=>`$${(v/1e3).toFixed(0)}MM`} tick={{fill:C.inkD,fontSize:9,fontFamily:C.fM}} axisLine={false} tickLine={false} width={50}/>
              <Tooltip content={<PremTT/>}/>
              <Bar dataKey="gadm" name="G.Admin"  fill={C.amber}  fillOpacity={0.65} radius={[3,3,0,0]}/>
              <Bar dataKey="gvta" name="G.Ventas" fill={C.rose}   fillOpacity={0.6}  radius={[3,3,0,0]}/>
              <Area type="monotone" dataKey="ing" name="Ingresos"  stroke={C.indigo} fill="url(#gInd)" strokeWidth={1.5}/>
              <Line type="monotone" dataKey="util" name="Resultado" stroke={C.emerald} strokeWidth={2.5} dot={{r:3,fill:C.emerald,strokeWidth:0}}/>
            </ComposedChart>
          </ResponsiveContainer>
        </GCard>
      </div>
    );

    /* -- BALANCE & SOLVENCIA ------------------------------------------- */
    const SecBalance=()=>(
      <div>
        <div style={{fontFamily:C.fM,fontSize:8.5,color:C.inkD,letterSpacing:"0.18em",textTransform:"uppercase",marginBottom:4}}>Informe Ejecutivo · Posición Financiera</div>
        <div style={{fontFamily:C.fD,fontSize:32,fontWeight:800,color:C.inkB,letterSpacing:"-0.02em",lineHeight:1.15,marginBottom:24}}>Estado de Situación Financiera & Solvencia</div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
          {/* Estructura activos */}
          <div style={{background:"#fff",border:`1px solid rgba(99,102,241,0.1)`,borderRadius:12,padding:"24px 28px"}}>
            <div style={{fontFamily:C.fD,fontSize:15,fontWeight:700,color:C.inkB,marginBottom:20}}>Estructura del Activo — Dic 2026</div>
            {[
              {l:"Inventarios",         v:DB.inv,   pct:(DB.inv/DB.tact*100).toFixed(1),  c:C.rose,  alert:true},
              {l:"Deudores (CxC)",      v:DB.cxc,   pct:(DB.cxc/DB.tact*100).toFixed(1),  c:C.amber},
              {l:"Propiedad & Equipo",  v:DB.ppe,   pct:(DB.ppe/DB.tact*100).toFixed(1),  c:C.indigo},
              {l:"Disponible",          v:DB.disp,  pct:(DB.disp/DB.tact*100).toFixed(1), c:C.cyan,  alert:true},
              {l:"Otros Activos",       v:DB.tact-DB.inv-DB.cxc-DB.ppe-DB.disp,
                                         pct:((DB.tact-DB.inv-DB.cxc-DB.ppe-DB.disp)/DB.tact*100).toFixed(1),c:C.inkD},
            ].map((k,i)=>(
              <div key={i} style={{marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontFamily:C.fB,fontSize:12,color:C.inkB,fontWeight:300}}>{k.l}</span>
                    {k.alert&&<span style={{fontFamily:C.fM,fontSize:8,color:C.rose,background:C.roseL,padding:"1px 7px",borderRadius:20}}>⚠ ALERTA</span>}
                  </div>
                  <div style={{display:"flex",gap:12,alignItems:"center"}}>
                    <span style={{fontFamily:C.fM,fontSize:10,color:C.inkD}}>{fm(k.v)}</span>
                    <span style={{fontFamily:C.fM,fontSize:11,color:k.c,fontWeight:600,minWidth:36,textAlign:"right"}}>{k.pct}%</span>
                  </div>
                </div>
                <div style={{height:7,background:"rgba(99,102,241,0.06)",borderRadius:4,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${Math.min(parseFloat(k.pct),100)}%`,background:k.c,borderRadius:4,opacity:0.8}}/>
                </div>
              </div>
            ))}
            <div style={{borderTop:`1px solid rgba(99,102,241,0.08)`,marginTop:16,paddingTop:14,display:"flex",justifyContent:"space-between"}}>
              <span style={{fontFamily:C.fD,fontSize:13,fontWeight:700,color:C.inkB}}>Total Activos</span>
              <span style={{fontFamily:C.fD,fontSize:15,fontWeight:800,color:C.indigo}}>{fm(DB.tact)}</span>
            </div>
          </div>

          {/* Estructura pasivos y patrimonio */}
          <div style={{background:"#fff",border:`1px solid rgba(99,102,241,0.1)`,borderRadius:12,padding:"24px 28px"}}>
            <div style={{fontFamily:C.fD,fontSize:15,fontWeight:700,color:C.inkB,marginBottom:20}}>Pasivos & Patrimonio — Dic 2026</div>
            {[
              {l:"Patrimonio Neto",     v:DB.pat,  pct:(DB.pat/DB.tact*100).toFixed(1),  c:C.emerald},
              {l:"Obligaciones Fin.",   v:DB.oblf, pct:(DB.oblf/DB.tact*100).toFixed(1), c:C.rose},
              {l:"Proveedores",         v:DB.prov, pct:(DB.prov/DB.tact*100).toFixed(1), c:C.amber},
              {l:"Cuentas por Pagar",   v:435,     pct:(435/DB.tact*100).toFixed(1),     c:C.inkD},
              {l:"Impuestos y Otros",   v:DB.tpas-DB.oblf-DB.prov-435,
                                        pct:((DB.tpas-DB.oblf-DB.prov-435)/DB.tact*100).toFixed(1),c:C.cyan},
            ].map((k,i)=>(
              <div key={i} style={{marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
                  <span style={{fontFamily:C.fB,fontSize:12,color:C.inkB,fontWeight:300}}>{k.l}</span>
                  <div style={{display:"flex",gap:12,alignItems:"center"}}>
                    <span style={{fontFamily:C.fM,fontSize:10,color:C.inkD}}>{fm(Math.max(k.v,0))}</span>
                    <span style={{fontFamily:C.fM,fontSize:11,color:k.c,fontWeight:600,minWidth:36,textAlign:"right"}}>{k.pct}%</span>
                  </div>
                </div>
                <div style={{height:7,background:"rgba(99,102,241,0.06)",borderRadius:4,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${Math.min(parseFloat(k.pct),100)}%`,background:k.c,borderRadius:4,opacity:0.8}}/>
                </div>
              </div>
            ))}
            <div style={{borderTop:`1px solid rgba(99,102,241,0.08)`,marginTop:16,paddingTop:14,display:"flex",justifyContent:"space-between"}}>
              <span style={{fontFamily:C.fD,fontSize:13,fontWeight:700,color:C.inkB}}>Total Pasivo + Patrimonio</span>
              <span style={{fontFamily:C.fD,fontSize:15,fontWeight:800,color:C.indigo}}>{fm(DB.tact)}</span>
            </div>
          </div>
        </div>

        {/* Ratios de solvencia */}
        <div style={{background:"#fff",border:`1px solid rgba(99,102,241,0.1)`,borderRadius:12,padding:"24px 28px",marginBottom:16}}>
          <div style={{fontFamily:C.fD,fontSize:15,fontWeight:700,color:C.inkB,marginBottom:20}}>Índices de Solvencia y Liquidez — Dic 2026 vs Dic 2025</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
            {[
              {l:"Razón Corriente",   v25:LIQ,              v24:"2.43x", ok:true,  obs:"Posición holgada de liquidez corriente"},
              {l:"Prueba Ácida",      v25:ACID,             v24:"1.18x", ok:true,  obs:"Capacidad de pago sin inventario"},
              {l:"Liq. Inmediata",    v25:LIQI,             v24:"0.19x", ok:false, obs:"⚠ Baja disponibilidad inmediata de caja"},
              {l:"Endeudamiento",     v25:pct(END),         v24:"54.3%", ok:true,  obs:"Mejora sustancial en estructura de deuda"},
              {l:"ROE",               v25:`${ROE}%`,        v24:"4.4%",  ok:true,  obs:"Rentabilidad patrimonial muy alta"},
              {l:"ROA",               v25:`${ROA}%`,        v24:"2.0%",  ok:true,  obs:"Retorno sobre activos totales mejoró"},
            ].map((k,i)=>(
              <div key={i} style={{padding:"16px 18px",background:k.ok?"rgba(5,150,105,0.03)":"rgba(244,63,94,0.03)",border:`1px solid ${k.ok?"rgba(5,150,105,0.12)":"rgba(244,63,94,0.12)"}`,borderRadius:10}}>
                <div style={{fontFamily:C.fM,fontSize:8,color:C.inkD,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:10}}>{k.l}</div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:8}}>
                  <div>
                    <div style={{fontFamily:C.fM,fontSize:8,color:C.inkD,marginBottom:2}}>2025</div>
                    <div style={{fontFamily:C.fD,fontSize:14,fontWeight:600,color:C.inkD}}>{k.v24}</div>
                  </div>
                  <div style={{fontSize:14,color:C.inkD,opacity:0.4}}>→</div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontFamily:C.fM,fontSize:8,color:k.ok?C.emerald:C.rose,marginBottom:2}}>2026</div>
                    <div style={{fontFamily:C.fD,fontSize:20,fontWeight:800,color:k.ok?C.emerald:C.rose}}>{k.v25}</div>
                  </div>
                </div>
                <div style={{fontFamily:C.fB,fontSize:10,color:C.inkD,fontWeight:300,lineHeight:1.5}}>{k.obs}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );

    /* -- ALERTAS & OPORTUNIDADES --------------------------------------- */
    const SecAlertas=()=>(
      <div>
        <div style={{fontFamily:C.fM,fontSize:8.5,color:C.inkD,letterSpacing:"0.18em",textTransform:"uppercase",marginBottom:4}}>Informe Ejecutivo · Gestión de Riesgos</div>
        <div style={{fontFamily:C.fD,fontSize:32,fontWeight:800,color:C.inkB,letterSpacing:"-0.02em",lineHeight:1.15,marginBottom:24}}>Alertas & Oportunidades 2026</div>

        {[
          {
            tipo:"RIESGO CRÍTICO", color:C.rose, bg:C.roseL, icon:"🔴",
            titulo:"Sobrestock de Inventario — Control urgente requerido",
            body:`El inventario alcanzó $14,518 millones al cierre de 2026, representando el 66.0% del total de activos y registrando un crecimiento del ${BAL24.inv>0?((DB.inv-BAL24.inv)/BAL24.inv*100).toFixed(1):0}% frente a los ${fm(BAL24.inv)} de 2025. Esta expansión es significativamente superior al crecimiento de ingresos del 18.0%, lo que sugiere una acumulación no productiva de mercancías con riesgo de obsolescencia o deterioro de valor.`,
            accion:"Implementar política de inventario máximo vinculada a proyección de ventas. Revisar órdenes de compra vigentes y negociar con proveedores plazos más cortos o cláusulas de devolución.",
            impacto:"Capital inmovilizado: ~$6,500M estimados en exceso de stock. Riesgo de deterioro de cartera de proveedores si el ciclo de ventas no se acelera.",
          },
          {
            tipo:"RIESGO CRÍTICO", color:C.rose, bg:C.roseL, icon:"🔴",
            titulo:"Deterioro en Días de Cartera — De ${DCXC24}d a ${DCXC}d",
            body:`La cartera de deudores creció un 73.8% al pasar de ${fm(BAL24.cxc)} en 2025 a ${fm(DB.cxc)} en 2026, mientras que los ingresos crecieron un ${YOY}%. Esto elevó los días de cartera de ${DCXC24} a ${DCXC} días, indicando una política de crédito más laxa o dificultades reales en la recuperación de cartera por parte de los clientes.`,
            accion:"Activar gestión de cobro preventivo para facturas mayores a 90 días. Revisar límites de crédito por cliente y exigir garantías para nuevas operaciones. Considerar factoring o descuento de cartera para mejorar flujo de caja.",
            impacto:"Riesgo de cartera incobrable estimado: hasta $800M si la tendencia continúa. Afecta directamente la disponibilidad de caja para operaciones.",
          },
          {
            tipo:"ALERTA OPERATIVA", color:C.amber, bg:C.amberL, icon:"🟡",
            titulo:"Disponible Crítico — Solo $406M de caja libre",
            body:`El disponible al cierre de 2026 fue de $406 millones, representando apenas el 1.8% del activo total y generando una liquidez inmediata de solo 0.10x. Aunque la razón corriente de 4.93x confirma solvencia estructural, la mayor parte de los activos corrientes está inmovilizada en inventario (66%) y cartera (24%), dejando muy poco margen de maniobra para emergencias operativas.`,
            accion:"Establecer política de caja mínima del 5% sobre gastos operativos mensuales (~$200M). Evaluar línea de crédito de tesorería como respaldo. Priorizar cobro de cartera corriente.",
            impacto:"Riesgo moderado de incumplimiento en pagos a proveedores si se retrasan cobros importantes.",
          },
          {
            tipo:"OPORTUNIDAD", color:C.emerald, bg:C.emeraldL, icon:"🟢",
            titulo:"Capacidad de Apalancamiento — Endeudamiento al 34.2%",
            body:`La reducción del endeudamiento del 54.3% al 34.2% posiciona a MEQ con una capacidad de deuda adicional estimada en $5,000-$7,000 millones manteniendo ratios conservadores. Esta fortaleza financiera podría aprovecharse para financiar la expansión comercial, adquisición de equipos demostradores o apertura de nuevos mercados en la región.`,
            accion:"Evaluar financiamiento estructurado para proyectos estratégicos con tasas bajas. La solidez del balance facilita negociación con entidades financieras en condiciones favorables.",
            impacto:"Potencial de crecimiento acelerado si se canaliza el apalancamiento hacia activos productivos de alta rotación.",
          },
          {
            tipo:"OPORTUNIDAD", color:C.emerald, bg:C.emeraldL, icon:"🟢",
            titulo:"Expansión Patrimonial Extraordinaria — +${BAL24.pat>0?((DB.pat-BAL24.pat)/BAL24.pat*100).toFixed(1):0}% en un año",
            body:`El patrimonio neto pasó de ${fm(BAL24.pat)} a ${fm(DB.pat)}, crecimiento que refleja la acumulación de resultados del ejercicio 2026. Esta solidez patrimonial permite a la compañía soportar inversiones de largo plazo, garantizar operaciones de crédito y proyectar estabilidad ante inversionistas y entidades financieras.`,
            accion:"Definir política de distribución de dividendos vs. retención de utilidades para inversión. Considerar el fortalecimiento de capital como base para contratos de mayor cuantía.",
            impacto:"Posiciona a MEQ como empresa de alta solvencia en el mercado de tecnología médica en Colombia y la región.",
          },
        ].map((a,i)=>(
          <div key={i} style={{background:"#fff",border:`1px solid ${a.color}25`,borderLeft:`4px solid ${a.color}`,borderRadius:"0 12px 12px 0",padding:"24px 28px",marginBottom:14}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
              <span style={{fontSize:16}}>{a.icon}</span>
              <span style={{fontFamily:C.fM,fontSize:8,color:a.color,letterSpacing:"0.16em",background:a.bg,padding:"3px 10px",borderRadius:20,fontWeight:600}}>{a.tipo}</span>
              <div style={{fontFamily:C.fD,fontSize:15,fontWeight:700,color:C.inkB}}>{a.titulo}</div>
            </div>
            <p style={{fontFamily:C.fB,fontSize:13,color:C.inkC,lineHeight:1.8,fontWeight:300,marginBottom:14}}>{a.body}</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <div style={{padding:"12px 16px",background:"rgba(99,102,241,0.03)",borderRadius:8}}>
                <div style={{fontFamily:C.fM,fontSize:8,color:C.indigo,letterSpacing:"0.12em",marginBottom:6}}>ACCIÓN RECOMENDADA</div>
                <p style={{fontFamily:C.fB,fontSize:12,color:C.inkC,lineHeight:1.7,fontWeight:300}}>{a.accion}</p>
              </div>
              <div style={{padding:"12px 16px",background:a.bg,borderRadius:8}}>
                <div style={{fontFamily:C.fM,fontSize:8,color:a.color,letterSpacing:"0.12em",marginBottom:6}}>IMPACTO ESTIMADO</div>
                <p style={{fontFamily:C.fB,fontSize:12,color:C.inkC,lineHeight:1.7,fontWeight:300}}>{a.impacto}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );

    /* -- PERSPECTIVAS 2026 --------------------------------------------- */
    const SecPerspectivas=()=>(
      <div style={{maxWidth:860}}>
        <div style={{fontFamily:C.fM,fontSize:8.5,color:C.inkD,letterSpacing:"0.18em",textTransform:"uppercase",marginBottom:4}}>Informe Ejecutivo · Proyección Estratégica</div>
        <div style={{fontFamily:C.fD,fontSize:32,fontWeight:800,color:C.inkB,letterSpacing:"-0.02em",lineHeight:1.15,marginBottom:24}}>Perspectivas & Recomendaciones 2026</div>

        <Divider/>

        <p style={{fontFamily:C.fB,fontSize:14,color:C.inkC,lineHeight:1.9,fontWeight:300,marginBottom:20}}>
          Con base en el análisis financiero del ejercicio 2026, {empresa||""} dispone de una plataforma sólida para afrontar el año 2026. La empresa cuenta con un patrimonio robusto, niveles de endeudamiento conservadores y una cartera de ingresos en crecimiento. Sin embargo, la gestión eficiente de los activos circulantes — especialmente inventario y cartera — será determinante para consolidar la rentabilidad y garantizar la sostenibilidad del crecimiento.
        </p>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:24}}>
          {[
            {
              prioridad:"01", titulo:"Optimización de Inventario", urgencia:"Alta · Q1 2026",
              icon:"📦", color:C.rose,
              desc:"Implementar sistema de control de inventario basado en demanda proyectada. Establecer niveles máximos por línea de producto y activar rotación forzada de ítems con más de 90 días de antigüedad. Meta: reducir inventario al 50% del activo total.",
            },
            {
              prioridad:"02", titulo:"Gestión de Cartera y Cobro", urgencia:"Alta · Q1 2026",
              icon:"📋", color:C.rose,
              desc:"Implementar política de crédito diferenciada por cliente. Activar proceso de cobro jurídico para facturas mayores a 120 días. Contratar o reforzar área de cartera. Meta: reducir días de cartera de 138 a máximo 90 días.",
            },
            {
              prioridad:"03", titulo:"Política de Caja Mínima", urgencia:"Media · Q2 2026",
              icon:"💵", color:C.amber,
              desc:"Establecer reserva mínima de liquidez equivalente a 2 meses de gastos operativos fijos. Negociar cupo de crédito bancario de emergencia. Meta: disponible mínimo de $600–800M permanente.",
            },
            {
              prioridad:"04", titulo:"Expansión Comercial Estratégica", urgencia:"Media · Q3 2026",
              icon:"🌎", color:C.emerald,
              desc:"Aprovechar la fortaleza patrimonial para incrementar presencia en mercados internacionales (Panamá, Guatemala, Perú, R. Dominicana). Evaluar alianzas con fabricantes para ampliar portafolio en equipos de alta tecnología.",
            },
            {
              prioridad:"05", titulo:"Planeación Tributaria 2026", urgencia:"Media · Q1 2026",
              icon:"📊", color:C.indigo,
              desc:"`Con utilidades de ${fm(UT)}, la carga tributaria estimada puede ser significativa. Planificar con tiempo las obligaciones de impuesto de renta y diseñar estrategia de diferimiento o deducción de inversiones en activos productivos.",
            },
            {
              prioridad:"06", titulo:"Sistema de Reporting Financiero", urgencia:"Baja · Q2 2026",
              icon:"📡", color:C.cyan,
              desc:"Implementar tablero de control financiero mensual con alertas automáticas sobre inventario, días de cartera y disponible. Asignar responsable de seguimiento de indicadores y establecer reunión mensual de revisión con gerencia.",
            },
          ].map((k,i)=>(
            <div key={i} style={{background:"#fff",border:`1px solid rgba(99,102,241,0.1)`,borderRadius:12,padding:"22px 24px"}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:14}}>
                <div style={{width:36,height:36,borderRadius:10,background:k.color,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontFamily:C.fM,fontSize:10,fontWeight:700,flexShrink:0}}>{k.prioridad}</div>
                <div>
                  <div style={{fontFamily:C.fD,fontSize:14,fontWeight:700,color:C.inkB}}>{k.titulo}</div>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginTop:3}}>
                    <span style={{fontSize:10}}>{k.icon}</span>
                    <span style={{fontFamily:C.fM,fontSize:8,color:k.color,letterSpacing:"0.1em"}}>{k.urgencia}</span>
                  </div>
                </div>
              </div>
              <p style={{fontFamily:C.fB,fontSize:12,color:C.inkC,lineHeight:1.75,fontWeight:300}}>{k.desc}</p>
            </div>
          ))}
        </div>

        <Divider/>

        {/* Firma ejecutiva */}
        <div style={{background:"linear-gradient(135deg,#0A0F1E,#1E1B4B)",borderRadius:16,padding:"40px 48px",position:"relative",overflow:"hidden"}}>
          <div style={{position:"absolute",top:-40,right:-40,width:200,height:200,borderRadius:"50%",background:"radial-gradient(circle,rgba(99,102,241,0.2) 0%,transparent 70%)"}}/>
          <div style={{position:"relative",zIndex:2}}>
            <div style={{fontFamily:C.fM,fontSize:8,color:"rgba(165,180,252,0.5)",letterSpacing:"0.2em",textTransform:"uppercase",marginBottom:20}}>{empresa||""} · Informe Financiero Ejecutivo 2026</div>
            <p style={{fontFamily:C.fD,fontSize:20,fontWeight:600,color:"rgba(255,255,255,0.9)",lineHeight:1.55,letterSpacing:"-0.01em",marginBottom:28,maxWidth:620,fontStyle:"italic"}}>
              "El análisis de 2026 nos revela una empresa en transición hacia la consolidación. La fortaleza patrimonial es real y significativa. El reto inmediato es convertir activos estáticos en flujo de caja dinámico."
            </p>
            <div style={{display:"flex",alignItems:"center",gap:18}}>
              <div style={{width:52,height:52,borderRadius:"50%",background:"linear-gradient(135deg,#6366F1,#8B5CF6)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontFamily:C.fD,fontSize:22,fontWeight:700}}>D</div>
              <div>
                <div style={{fontFamily:C.fD,fontSize:17,fontWeight:800,color:"#fff",letterSpacing:"-0.01em"}}>Doly Mayeli Blanco G.</div>
                <div style={{fontFamily:C.fM,fontSize:9,color:"rgba(165,180,252,0.75)",letterSpacing:"0.12em",marginTop:3,textTransform:"uppercase"}}>Contadora · TP 199.692-T</div>
                <div style={{fontFamily:C.fB,fontSize:11,color:"rgba(165,180,252,0.55)",marginTop:4,fontWeight:300}}>Especialista en NIIF · Revisoría Fiscal · Auditoría Externa · IA</div>
                <div style={{fontFamily:C.fB,fontSize:11,color:"rgba(165,180,252,0.45)",marginTop:4,fontWeight:300}}>Diciembre 2026 · Bogotá, Colombia · Datos: Sistema Contable</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );

    const SECCIONES={
      portada:     <SecPortada/>,
      highlights:  <SecHighlights/>,
      cfo:         <SecCFO/>,
      financiero:  <SecFinanciero/>,
      balance:     <SecBalance/>,
      alertas:     <SecAlertas/>,
      perspectivas:<SecPerspectivas/>,
    };

    return(
      <div>
        {/* -- TOOLBAR EXPORTAR -------------------------------------------- */}
        <div className="no-print" style={{marginBottom:14}}>
          {/* Fila 1: Título + Exportar */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <div>
              <div style={{fontFamily:C.fD,fontSize:13,fontWeight:700,color:C.inkB,marginBottom:2}}>INFORME EJECUTIVO - FINANCIERO CONTABLE</div>
              <div style={{fontFamily:C.fB,fontSize:10.5,color:C.inkD,fontWeight:300}}>Documento continuo · listo para exportar y combinar con estados financieros</div>
            </div>
            {/* Export dropdown */}
            {(()=>{
              const [showMenu,setShowMenu]=useState(false);
              const exportHTML=()=>{
                const el=document.getElementById("informe-print");
                if(!el)return;
                const html=`<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"/>
<title>MEQ Informe Ejecutivo 2026</title>
<style>*{box-sizing:border-box;}body{font-family:Arial,sans-serif;background:#F4F6FF;color:#1a1a2e;margin:0;padding:24px;}
*{transition:none!important;animation:none!important;opacity:1!important;transform:none!important;}</style>
</head><body>${el.innerHTML}</body></html>`;
                const blob=new Blob([html],{type:"text/html;charset=utf-8"});
                const url=URL.createObjectURL(blob);
                const a=document.createElement("a");a.href=url;a.download="MEQ_Informe_Ejecutivo_2026.html";
                a.style.display="none";document.body.appendChild(a);a.click();
                setTimeout(()=>{URL.revokeObjectURL(url);document.body.removeChild(a);},1000);
              };
              const exportWord=()=>{
                const meses=["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
                const f=v=>v===0?"—":`$${Math.abs(v).toFixed(0)}`;
                const fM=v=>{const a=Math.abs(v);if(a>=1e6)return`$${(v/1e6).toFixed(2)}B`;if(a>=1e3)return`$${(v/1e3).toFixed(1)}MM`;return`$${v.toFixed(0)}M`;};
                const td=(v,bold,color)=>`<td style="border:1px solid #ddd;padding:5pt 7pt;text-align:right;font-size:9pt;${bold?"font-weight:bold;":""}${color?`color:${color};`:""}">${v}</td>`;
                const th=(v)=>`<th style="border:1px solid #ddd;padding:5pt 7pt;text-align:right;font-size:9pt;background:#1E1B4B;color:#fff;">${v}</th>`;
                const thL=(v)=>`<th style="border:1px solid #ddd;padding:5pt 7pt;text-align:left;font-size:9pt;background:#1E1B4B;color:#fff;">${v}</th>`;
                const row=(label,arr,bold,color)=>`<tr>${td(label,bold,color).replace("text-align:right","text-align:left")}${arr.map(v=>td(f(v),bold,color)).join("")}${td(f(arr.reduce((s,x)=>s+x,0)),true,color)}</tr>`;
                const pygTable=`
<table style="border-collapse:collapse;width:100%;font-family:Calibri,Arial;margin-bottom:16pt;">
<thead><tr>${thL("Cuenta")}${meses.map(m=>th(m)).join("")}${th("TOTAL")}</tr></thead>
<tbody style="font-size:9pt;">
${row("(+) Ingresos",ING25,true,"#059669")}
${row("(−) Costo de Ventas",CV25,false,"#6b7280")}
${row("(−) Gastos Administración",GADM25,false,"#6b7280")}
${row("(−) Gastos de Ventas",GVTA25,false,"#6b7280")}
${row("(−) Gastos No Operac.",GNOOP25,false,"#6b7280")}
${row("(=) Resultado del Período",UTIL25,true,UTIL25.reduce((s,x)=>s+x,0)<0?"#dc2626":"#059669")}
</tbody></table>`;
                const balTable=`
<table style="border-collapse:collapse;width:100%;font-family:Calibri,Arial;margin-bottom:16pt;">
<thead><tr>${thL("Partida")}${th("Año Activo")}${th("Año Anterior")}${th("Variación")}</tr></thead>
<tbody style="font-size:9pt;">
${[
  ["Total Activos",DB.tact,BAL24.tact],
  ["Activo Corriente",DB.acte,BAL24.actc||BAL24.disp+BAL24.cxc+BAL24.inv],
  ["Total Pasivos",DB.tpas,BAL24.tpas],
  ["Patrimonio Neto",DB.pat,BAL24.pat],
].map(([l,v25,v24])=>{
  const dif=v25-v24;const pct=v24>0?((dif/v24)*100).toFixed(1):"—";
  return`<tr><td style="border:1px solid #ddd;padding:5pt 7pt;font-size:9pt;font-weight:bold;">${l}</td>${td(fM(v25),true)}${td(fM(v24),false,"#6b7280")}${td((dif>=0?"+":"")+pct+"%",true,dif>=0?"#059669":"#dc2626")}</tr>`;
}).join("")}
</tbody></table>`;
                const liqTable=`
<table style="border-collapse:collapse;width:100%;font-family:Calibri,Arial;margin-bottom:16pt;">
<thead><tr>${thL("Indicador")}${th("Valor")}${th("Meta")}${th("Estado")}</tr></thead>
<tbody style="font-size:9pt;">
${[
  ["Razón Corriente",LIQ,"≥ 1.5",parseFloat(LIQ)>=1.5],
  ["Prueba Ácida",ACID,"≥ 1.0",parseFloat(ACID)>=1],
  ["Endeudamiento",END+"%","< 60%",parseFloat(END)<60],
  ["ROE",ROE+"%","> 15%",parseFloat(ROE)>=15],
  ["ROA",ROA+"%","> 8%",parseFloat(ROA)>=8],
  ["Deuda/Patrimonio",(DB.tpas/DB.pat).toFixed(2)+"x","< 2x",DB.tpas/DB.pat<2],
].map(([l,v,m,ok])=>`<tr><td style="border:1px solid #ddd;padding:5pt 7pt;font-size:9pt;">${l}</td>${td(v,true,ok?"#059669":"#dc2626")}${td(m,false,"#6b7280")}${td(ok?"✓ Cumple":"✗ Revisar",true,ok?"#059669":"#dc2626")}</tr>`).join("")}
</tbody></table>`;
                const wordDoc=`<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="UTF-8"/><meta name=ProgId content=Word.Document/>
<style>
@page WordSection1{size:21cm 29.7cm;margin:2cm 2cm 2cm 2cm;mso-page-orientation:portrait;}
div.WordSection1{page:WordSection1;}
body{font-family:Calibri,Arial,sans-serif;font-size:10pt;color:#1a1a2e;margin:0;padding:0;}
h1{font-size:20pt;color:#1E1B4B;border-bottom:3px solid #6366F1;padding-bottom:6pt;margin-bottom:12pt;}
h2{font-size:14pt;color:#1E1B4B;margin-top:20pt;margin-bottom:8pt;border-left:4px solid #6366F1;padding-left:8pt;}
h3{font-size:11pt;color:#4F46E5;margin-top:14pt;margin-bottom:6pt;}
p{line-height:1.5;margin-bottom:8pt;}
table{border-collapse:collapse;}
.portada{background:#1E1B4B;color:#fff;padding:40pt;margin-bottom:24pt;}
</style>
</head>
<body><div class="WordSection1">
<div class="portada" style="background:#1E1B4B;color:#fff;padding:30pt 40pt;margin-bottom:24pt;">
  <p style="font-size:9pt;color:rgba(165,180,252,0.7);letter-spacing:3pt;margin:0 0 12pt;">CFO INTELLIGENCE PLATFORM · MEQ</p>
  <h1 style="font-size:28pt;color:#fff;border:none;padding:0;margin:0 0 6pt;">INFORME EJECUTIVO</h1>
  <p style="font-size:20pt;color:#a5b4fc;margin:0 0 20pt;font-weight:bold;">FINANCIERO CONTABLE</p>
  <table style="width:100%;border-collapse:collapse;">
    <tr>
      <td style="color:rgba(255,255,255,0.7);font-size:9pt;padding:4pt 0;">Empresa</td>
      <td style="color:#fff;font-weight:bold;font-size:10pt;padding:4pt 0;">${empresa||"—"}</td>
      <td style="color:rgba(255,255,255,0.7);font-size:9pt;padding:4pt 0;">NIT</td>
      <td style="color:#fff;font-weight:bold;font-size:10pt;padding:4pt 0;">${nit||"—"}</td>
    </tr>
    <tr>
      <td style="color:rgba(255,255,255,0.7);font-size:9pt;padding:4pt 0;">Período</td>
      <td style="color:#fff;font-weight:bold;font-size:10pt;padding:4pt 0;" colspan="3">${periodo||"Enero – Diciembre 2026"}</td>
    </tr>
  </table>
</div>
<h2>1. Estado de Resultados (P&amp;G)</h2>
<p style="font-size:9pt;color:#6b7280;">Valores en millones COP. Acumulado mensual enero–diciembre.</p>
${pygTable}
<h2>2. Posición Financiera (Balance)</h2>
<p style="font-size:9pt;color:#6b7280;">Valores en millones COP. Comparativo año activo vs año anterior.</p>
${balTable}
<h2>3. Indicadores Financieros</h2>
<p style="font-size:9pt;color:#6b7280;">Ratios clave de liquidez, solvencia y rentabilidad.</p>
${liqTable}
<h2>4. Destacados del Período</h2>
<table style="border-collapse:collapse;width:100%;font-family:Calibri,Arial;">
<tbody style="font-size:9pt;">
<tr><td style="border:1px solid #ddd;padding:6pt 10pt;width:40%;font-weight:bold;background:#f9f9ff;">Ingresos Totales</td><td style="border:1px solid #ddd;padding:6pt 10pt;color:#059669;font-weight:bold;">${fM(IT)}</td><td style="border:1px solid #ddd;padding:6pt 10pt;font-size:8pt;color:#6b7280;">Variación YoY: ${YOY}%</td></tr>
<tr><td style="border:1px solid #ddd;padding:6pt 10pt;font-weight:bold;background:#f9f9ff;">Resultado del Período</td><td style="border:1px solid #ddd;padding:6pt 10pt;color:${UT>=0?"#059669":"#dc2626"};font-weight:bold;">${fM(UT)}</td><td style="border:1px solid #ddd;padding:6pt 10pt;font-size:8pt;color:#6b7280;">Margen neto: ${MG_UT.toFixed(1)}%</td></tr>
<tr><td style="border:1px solid #ddd;padding:6pt 10pt;font-weight:bold;background:#f9f9ff;">Patrimonio Neto</td><td style="border:1px solid #ddd;padding:6pt 10pt;font-weight:bold;">${fM(DB.pat)}</td><td style="border:1px solid #ddd;padding:6pt 10pt;font-size:8pt;color:#6b7280;">ROE: ${ROE}%</td></tr>
<tr><td style="border:1px solid #ddd;padding:6pt 10pt;font-weight:bold;background:#f9f9ff;">Capital de Trabajo</td><td style="border:1px solid #ddd;padding:6pt 10pt;font-weight:bold;">${fM(DB.acte-PAC)}</td><td style="border:1px solid #ddd;padding:6pt 10pt;font-size:8pt;color:#6b7280;">Razón corriente: ${LIQ}</td></tr>
</tbody></table>
<p style="font-size:8pt;color:#9ca3af;margin-top:24pt;border-top:1px solid #e5e7eb;padding-top:8pt;">Generado por MEQ CFO Intelligence Platform · ${new Date().toLocaleDateString("es-CO",{year:"numeric",month:"long",day:"numeric"})} · Información confidencial</p>
</div></body></html>`;
                const blob=new Blob(["\ufeff",wordDoc],{type:"application/msword"});
                const url=URL.createObjectURL(blob);
                const a=document.createElement("a");a.href=url;a.download="MEQ_Informe_Ejecutivo_2026.doc";
                a.style.display="none";document.body.appendChild(a);a.click();
                setTimeout(()=>{URL.revokeObjectURL(url);document.body.removeChild(a);},1000);
              };
              const exportPDF=()=>{ window.print(); };
              const exportImages=async()=>{
                const sections=[
                  {id:"inf-portada",name:"01_Portada"},
                  {id:"inf-highlights",name:"02_Highlights"},
                  {id:"inf-cfo",name:"03_Analisis_Ejecutivo"},
                  {id:"inf-financiero",name:"04_Revision_Financiera"},
                  {id:"inf-balance",name:"05_Balance_Solvencia"},
                  {id:"inf-alertas",name:"06_Alertas_Oportunidades"},
                  {id:"inf-perspectivas",name:"07_Perspectivas"},
                ];
                for(const sec of sections){
                  const el=document.getElementById(sec.id);
                  if(!el) continue;
                  try{
                    const canvas=await html2canvas(el,{scale:2,useCORS:true,allowTaint:true,backgroundColor:"#F4F6FF",logging:false});
                    const url=canvas.toDataURL("image/png");
                    const a=document.createElement("a");
                    a.href=url;a.download=`MEQ_Informe_${sec.name}.png`;
                    a.style.display="none";document.body.appendChild(a);a.click();
                    document.body.removeChild(a);
                    await new Promise(r=>setTimeout(r,400)); // pequeña pausa entre descargas
                  }catch(e){console.warn("Error capturando",sec.id,e);}
                }
              };
              return(
                <div style={{position:"relative",flexShrink:0}}>
                  <button onClick={()=>setShowMenu(m=>!m)} style={{
                    display:"flex",alignItems:"center",gap:8,padding:"9px 18px",borderRadius:9,border:"none",
                    background:"linear-gradient(135deg,#0A0F1E,#1E1B4B)",color:"#fff",cursor:"pointer",
                    fontFamily:C.fM,fontSize:10,letterSpacing:"0.08em",fontWeight:500,
                    boxShadow:"0 4px 14px rgba(15,17,23,0.25)",whiteSpace:"nowrap",
                  }}>
                    <span style={{fontSize:13}}>↓</span> Exportar <span style={{fontSize:9,opacity:0.7}}>▼</span>
                  </button>
                  {showMenu&&(
                    <div onClick={()=>setShowMenu(false)} style={{
                      position:"absolute",top:"calc(100% + 6px)",right:0,zIndex:999,
                      background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,
                      boxShadow:"0 8px 32px rgba(0,0,0,0.12)",minWidth:200,overflow:"hidden",
                    }}>
                      {[
                        {icon:"🖨️",label:"PDF (Imprimir)",sub:"Ctrl+P → Guardar como PDF",fn:exportPDF},
                        {icon:"🌐",label:"HTML",sub:"Abre en cualquier navegador",fn:exportHTML},
                        {icon:"📄",label:"Word (.doc)",sub:"Tablas editables con datos reales",fn:exportWord},
                        {icon:"📸",label:"Imágenes PNG",sub:"7 secciones individuales en alta res",fn:exportImages},
                      ].map((opt,i)=>(
                        <button key={i} onClick={opt.fn} style={{
                          display:"block",width:"100%",padding:"12px 16px",border:"none",
                          background:"transparent",textAlign:"left",cursor:"pointer",
                          borderBottom:i<2?`1px solid ${C.border}`:"none",
                        }}
                        onMouseEnter={e=>e.currentTarget.style.background=C.indigoL}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          <div style={{display:"flex",alignItems:"center",gap:10}}>
                            <span style={{fontSize:16}}>{opt.icon}</span>
                            <div>
                              <div style={{fontFamily:C.fM,fontSize:10,color:C.inkB,fontWeight:600,letterSpacing:"0.05em"}}>{opt.label}</div>
                              <div style={{fontFamily:C.fB,fontSize:9,color:C.inkD,fontWeight:300,marginTop:1}}>{opt.sub}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          {/* Fila 2: Navegación rápida */}
          <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
            {NAV.map(n=>(
              <button key={n.id} onClick={()=>{
                const el=document.getElementById("inf-"+n.id);
                if(el) el.scrollIntoView({behavior:"smooth",block:"start"});
              }} style={{fontFamily:C.fM,fontSize:8.5,padding:"5px 10px",border:`1px solid ${C.border}`,
                background:C.surface,color:C.inkD,borderRadius:6,cursor:"pointer",
                letterSpacing:"0.05em",whiteSpace:"nowrap"}}>
                {n.label}
              </button>
            ))}
          </div>
        </div>

        {/* -- DOCUMENTO CONTINUO ------------------------------------------ */}
        <div id="informe-print" style={{maxWidth:900,margin:"0 auto"}}>

          {/* PORTADA */}
          <div id="inf-portada" style={{pageBreakAfter:"always",marginBottom:40}}>
            <SecPortada/>
          </div>

          {/* HIGHLIGHTS */}
          <div id="inf-highlights" style={{pageBreakAfter:"always",marginBottom:40}}>
            <div style={{height:1,background:`linear-gradient(90deg,${C.indigo},transparent)`,marginBottom:32,opacity:0.15}}/>
            <SecHighlights/>
          </div>

          {/* ANÁLISIS EJECUTIVO */}
          <div id="inf-cfo" style={{pageBreakAfter:"always",marginBottom:40}}>
            <div style={{height:1,background:`linear-gradient(90deg,${C.indigo},transparent)`,marginBottom:32,opacity:0.15}}/>
            <SecCFO/>
          </div>

          {/* REVISIÓN FINANCIERA */}
          <div id="inf-financiero" style={{pageBreakAfter:"always",marginBottom:40}}>
            <div style={{height:1,background:`linear-gradient(90deg,${C.indigo},transparent)`,marginBottom:32,opacity:0.15}}/>
            <SecFinanciero/>
          </div>

          {/* BALANCE & SOLVENCIA */}
          <div id="inf-balance" style={{pageBreakAfter:"always",marginBottom:40}}>
            <div style={{height:1,background:`linear-gradient(90deg,${C.indigo},transparent)`,marginBottom:32,opacity:0.15}}/>
            <SecBalance/>
          </div>

          {/* ALERTAS */}
          <div id="inf-alertas" style={{pageBreakAfter:"always",marginBottom:40}}>
            <div style={{height:1,background:`linear-gradient(90deg,${C.rose},transparent)`,marginBottom:32,opacity:0.15}}/>
            <SecAlertas/>
          </div>

          {/* PERSPECTIVAS */}
          <div id="inf-perspectivas" style={{marginBottom:40}}>
            <div style={{height:1,background:`linear-gradient(90deg,${C.emerald},transparent)`,marginBottom:32,opacity:0.15}}/>
            <SecPerspectivas/>
          </div>

          {/* PIE DE CIERRE */}
          <div style={{marginTop:32,padding:"24px 32px",background:"linear-gradient(135deg,#0A0F1E,#1E1B4B)",borderRadius:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontFamily:C.fD,fontSize:11,color:"rgba(255,255,255,0.5)",marginBottom:4,letterSpacing:"0.1em"}}>PREPARADO POR</div>
              <div style={{fontFamily:C.fD,fontSize:15,color:"#fff",fontWeight:400}}>Doly Mayeli Blanco G.</div>
              <div style={{fontFamily:C.fM,fontSize:8.5,color:"rgba(165,180,252,0.7)",marginTop:3,letterSpacing:"0.08em"}}>TP 199.692-T · Contadora · Esp. NIIF · Revisoría Fiscal · IA</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontFamily:C.fM,fontSize:8,color:"rgba(165,180,252,0.4)",letterSpacing:"0.15em",marginBottom:4}}>{empresa||""}</div>
              <div style={{fontFamily:C.fM,fontSize:8,color:"rgba(165,180,252,0.4)",letterSpacing:"0.1em"}}>Informe Financiero Ejecutivo · Enero–Diciembre 2026</div>
              <div style={{fontFamily:C.fM,fontSize:8,color:"rgba(165,180,252,0.3)",marginTop:3}}>Cifras en millones COP · Fuente: Sistema Contable</div>
            </div>
          </div>

        </div>
      </div>
    );
  };

  const CARGAR_DATOS=()=>{
    const [files,setFiles]=useState({bal24:null,bal25:null,pyg24:null,pyg25:null});
    const [status,setStatus]=useState({bal24:"idle",bal25:"idle",pyg24:"idle",pyg25:"idle"});
    const [msg,setMsg]=useState("");
    const [rutStatus,setRutStatus]=useState("idle");
    const [ciuuInput,setCiuuInput]=useState("");
    const [rutMsg,setRutMsg]=useState("");
    const [rutKey,setRutKey]=useState(0);

    const loadXLSX=()=>new Promise((res)=>{
      if(window.XLSX){res(window.XLSX);return;}
      const s=document.createElement("script");
      s.src="https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
      s.onload=()=>res(window.XLSX);
      s.onerror=()=>res(null);
      document.head.appendChild(s);
    });

    const parseRUT=async(file)=>{
      setRutStatus("reading"); setRutMsg("Leyendo RUT...");
      try{
        if(!window.pdfjsLib){
          await new Promise((res)=>{
            const s=document.createElement("script");
            s.src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
            s.onload=()=>{ if(window.pdfjsLib) window.pdfjsLib.GlobalWorkerOptions.workerSrc="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js"; setTimeout(res,300); };
            s.onerror=res;
            document.head.appendChild(s);
          });
        }
        const buf=await file.arrayBuffer();
        var text="";
        if(window.pdfjsLib){
          const pdf=await window.pdfjsLib.getDocument({data:new Uint8Array(buf)}).promise;
          for(var pg=1;pg<=Math.min(pdf.numPages,2);pg++){
            const page=await pdf.getPage(pg);
            const tc=await page.getTextContent();
            for(var ii=0;ii<tc.items.length;ii++) text+=tc.items[ii].str+" ";
          }
        }
        var upper=text.toUpperCase();
        var nombre="",nitVal="",ciuu="";
        var collapsed=text;
        for(var rep=0;rep<6;rep++) collapsed=collapsed.replace(/([0-9]) ([0-9])/g,"$1$2");
        var nitMatch=collapsed.match(/([0-9]{8,12})\s*-\s*([0-9])/);
        if(!nitMatch) nitMatch=collapsed.match(/([0-9]{8,12})\s([0-9])\s/);
        if(nitMatch) nitVal=nitMatch[1]+"-"+nitMatch[2];
        var rsIdx=upper.indexOf("RAZON SOCIAL");
        if(rsIdx<0) rsIdx=upper.indexOf("RAZÓN SOCIAL");
        if(rsIdx>=0){
          var chunk=text.slice(rsIdx+12,rsIdx+120).trim();
          var parts=chunk.split("  ");
          if(parts[0]&&parts[0].length>3) nombre=parts[0].trim().slice(0,60);
        }
        if(!nombre){
          var words=text.split(" ");
          for(var wi=0;wi<words.length;wi++){
            var w=words[wi].toUpperCase();
            if(w==="SAS"||w==="LTDA"){
              var nm=[]; for(var wj=Math.max(0,wi-4);wj<=wi;wj++){ var pw=words[wj].replace(/[^A-Za-z]/g,""); if(pw.length>1) nm.push(pw.toUpperCase()); }
              if(nm.length>=2){ nombre=nm.join(" ").slice(0,60); break; }
            }
          }
        }
        var actIdx=upper.indexOf("ACTIVIDAD PRINCIPAL");
        if(actIdx<0) actIdx=upper.indexOf("CIIU");
        if(actIdx>=0){
          var near=collapsed.slice(actIdx,actIdx+50);
          for(var ci=0;ci<near.length-3;ci++){
            if(near[ci]>="4"&&near[ci]<="9"&&near[ci+1]>="0"&&near[ci+1]<="9"&&near[ci+2]>="0"&&near[ci+2]<="9"&&near[ci+3]>="0"&&near[ci+3]<="9"){ ciuu=near.slice(ci,ci+4); break; }
          }
        }
        if(nombre) setEmpresa(nombre);
        if(nitVal) setNit(nitVal);
        const sec=ciuu?ciuuToSector[ciuu]||"med":"";
        if(sec) setSector(sec);
        setCiuuInput(ciuu||"");
        setRutKey((k)=>k+1);
        const msgs=[];
        msgs.push(nombre?"✓ "+nombre.slice(0,40):"⚠ Razon social no detectada");
        msgs.push(nitVal?"✓ NIT: "+nitVal:"⚠ NIT no detectado");
        msgs.push(ciuu?"✓ CIIU "+ciuu+(SECTORES[sec]?" → "+SECTORES[sec].label:""):"⚠ CIIU no detectado");
        setRutStatus([nombre,nitVal].filter(Boolean).length>0?"ok":"error");
        setRutMsg(msgs.join("   "));
      }catch(e){ setRutStatus("error"); setRutMsg("Error: "+e.message); }
    };

    const handleFile=(key,file)=>{
      setStatus((s)=>({...s,[key]:"parsing"}));
      loadXLSX().then((XLSX)=>{
        if(!XLSX){setStatus((s)=>({...s,[key]:"error"}));return;}
        file.arrayBuffer().then((buf)=>{
          const wb=XLSX.read(new Uint8Array(buf),{type:"array"});
          const ws=wb.Sheets[wb.SheetNames[0]];
          const rows=XLSX.utils.sheet_to_json(ws,{header:1,defval:""});
          setFiles((f)=>({...f,[key]:file}));
          setFiles((f)=>({...f,[key+"_rows"]:rows}));
          setStatus((s)=>({...s,[key]:"ok"}));
        }).catch((e)=>setStatus((s)=>({...s,[key]:"error"})));
      });
    };

    const allLoaded=Object.values(status).filter((s)=>s==="ok").length;
    const SC={idle:C.inkD,parsing:C.amber,ok:C.emerald,error:C.rose};
    const SI={idle:"○",parsing:"⟳",ok:"✓",error:"✗"};

    return(
      <div>
        <SH pill="Gestion de Datos" title="Cargar Archivos" sub="Sube el RUT y los 4 archivos Excel para actualizar el dashboard"/>

        {/* INSTRUCTIVO */}
        <div style={{background:"linear-gradient(135deg,rgba(99,102,241,0.05),rgba(139,92,246,0.03))",border:"1px solid rgba(99,102,241,0.15)",borderRadius:12,padding:"18px 20px",marginBottom:14}}>
          <div style={{fontFamily:C.fD,fontSize:13,fontWeight:700,color:C.indigo,marginBottom:14}}>Instrucciones de Carga</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
            <div>
              {[
                {n:"1",t:"Carga el RUT de la empresa (PDF)",d:"Autodetecta Razon Social, NIT y Codigo CIIU automaticamente"},
                {n:"2",t:"Carga los 4 archivos Excel",d:"PYG 2026 · PYG 2025 · Balance 2026 · Balance 2025"},
                {n:"3",t:"Verifica el Codigo CIIU",d:"Define los benchmarks del sector para todos los analisis"},
                {n:"4",t:"Clic en Aplicar Datos",d:"El tablero se actualiza con toda la informacion real"},
              ].map((s,i)=>(
                <div key={i} style={{display:"flex",gap:10,alignItems:"flex-start",marginBottom:i<3?10:0}}>
                  <div style={{width:22,height:22,borderRadius:"50%",background:C.indigoG,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <span style={{fontFamily:C.fM,fontSize:10,color:"#fff",fontWeight:700}}>{s.n}</span>
                  </div>
                  <div>
                    <div style={{fontFamily:C.fB,fontSize:11.5,color:C.inkB,fontWeight:500}}>{s.t}</div>
                    <div style={{fontFamily:C.fM,fontSize:10,color:C.inkD,marginTop:2}}>{s.d}</div>
                  </div>
                </div>
              ))}
              <div style={{marginTop:12,padding:"10px 14px",background:"rgba(99,102,241,0.06)",borderRadius:8,border:"1px solid rgba(99,102,241,0.1)"}}>
                <div style={{fontFamily:C.fM,fontSize:9,color:C.indigo,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:6}}>Archivos Excel requeridos</div>
                {[
                  "PYG mes a mes 2026 — Estado de Resultados año actual",
                  "PYG mes a mes 2025 — Estado de Resultados año anterior",
                  "Balance General mes a mes 2026 — Situacion financiera actual",
                  "Balance General mes a mes 2025 — Situacion financiera anterior",
                ].map((f,i)=>(
                  <div key={i} style={{fontFamily:C.fM,fontSize:10,color:C.inkC,marginBottom:i<3?4:0}}>{"("+(i+1)+") "+f}</div>
                ))}
              </div>
            </div>
            <div style={{background:"rgba(5,150,105,0.04)",borderRadius:10,padding:"14px 16px",border:"1px solid rgba(5,150,105,0.12)"}}>
              <div style={{fontFamily:C.fM,fontSize:9,color:C.emerald,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:8}}>Metodologia — Indice de Salud Financiera</div>
              <div style={{fontFamily:C.fB,fontSize:11,color:C.inkB,fontWeight:600,marginBottom:6}}>Fuente oficial: Superintendencia de Sociedades Colombia — SIREM 2023</div>
              <div style={{fontFamily:C.fM,fontSize:10,color:C.inkC,lineHeight:1.7,marginBottom:8}}>
                Benchmarks sectoriales por actividad economica CIIU (datos anuales publicados).<br/>
                Indice 0-100 puntos — ponderacion oficial:
              </div>
              {[
                {d:"Liquidez",       f:"Razon Corriente / Benchmark RC",     p:"x20"},
                {d:"Solvencia",      f:"(1-END%) / (1-Benchmark END%)",       p:"x15"},
                {d:"Rentabilidad",   f:"ROE empresa / Benchmark ROE",          p:"x20"},
                {d:"Gestion Cartera",f:"Benchmark dias / Dias cartera empresa",p:"x20"},
                {d:"Gestion Activo", f:"Benchmark inv/act / Inv/Activo empresa",p:"x15"},
                {d:"Crecimiento",    f:"YoY empresa / Benchmark YoY",          p:"x10"},
              ].map((r,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                  padding:"4px 0",borderBottom:i<5?"1px solid rgba(5,150,105,0.08)":"none"}}>
                  <div>
                    <div style={{fontFamily:C.fB,fontSize:10,color:C.inkB,fontWeight:500}}>{r.d}</div>
                    <div style={{fontFamily:C.fM,fontSize:9,color:C.inkD}}>{r.f}</div>
                  </div>
                  <div style={{fontFamily:C.fD,fontSize:12,fontWeight:700,color:C.emerald,marginLeft:8}}>{r.p}</div>
                </div>
              ))}
              <div style={{marginTop:8,fontFamily:C.fM,fontSize:9.5,color:C.inkD,fontStyle:"italic"}}>
                Cada dimension: min(ponderacion, ratio_empresa/ratio_benchmark x ponderacion). Score maximo: 100 pts.
              </div>
            </div>
          </div>
        </div>

        {/* RUT */}
        <div style={{background:"#fff",border:`1px solid ${C.border}`,borderRadius:12,padding:"20px 24px",marginBottom:14}}>
          <div style={{fontFamily:C.fD,fontSize:14,fontWeight:700,color:C.inkB,marginBottom:14}}>Configuracion de la empresa</div>
          <div onClick={()=>{const inp=document.createElement("input");inp.type="file";inp.accept=".pdf";inp.onchange=(e)=>{if(e.target.files[0])parseRUT(e.target.files[0]);};inp.click();}}
            style={{border:`2px dashed ${rutStatus==="ok"?"#10B981":"rgba(99,102,241,0.3)"}`,borderRadius:10,padding:"16px",cursor:"pointer",marginBottom:12,
              background:rutStatus==="ok"?"rgba(16,185,129,0.04)":"#fafbff",display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:32}}>{rutStatus==="reading"?"⏳":rutStatus==="ok"?"✅":rutStatus==="error"?"❌":"📄"}</span>
            <div>
              <div style={{fontFamily:C.fB,fontSize:12,color:C.inkB,fontWeight:500}}>
                {rutStatus==="idle"?"Subir RUT (PDF) - extrae Razon Social, NIT y CIIU automaticamente":
                 rutStatus==="reading"?"Leyendo RUT...":rutStatus==="ok"?"RUT procesado":"Error - intenta de nuevo"}
              </div>
              {rutMsg&&<div style={{fontFamily:C.fM,fontSize:9,color:rutStatus==="ok"?C.emerald:C.inkD,marginTop:4,lineHeight:1.6}}>{rutMsg}</div>}
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:10}}>
            {[["Razon Social",empresa,(v)=>setEmpresa(v),"emp"],["NIT",nit,(v)=>setNit(v),"nit"],["Periodo",periodo,(v)=>setPeriodo(v),"per"]].map(([label,val,setter,k])=>(
              <div key={k}>
                <div style={{fontFamily:C.fM,fontSize:8,color:C.inkD,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>{label}</div>
                <input key={k+"-"+rutKey} defaultValue={val} onBlur={(e)=>setter(e.target.value)}
                  style={{width:"100%",background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:"9px 12px",color:C.inkB,fontFamily:C.fB,fontSize:12}}/>
              </div>
            ))}
            <div>
              <div style={{fontFamily:C.fM,fontSize:8,color:C.inkD,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>Codigo CIIU</div>
              <input key={"ciuu-"+rutKey} defaultValue={ciuuInput} placeholder="ej. 4659"
                onBlur={(e)=>{
                  const v=e.target.value.trim();
                  setCiuuInput(v);
                  const sec=ciuuToSector[v]||"med";
                  setSector(sec);
                }}
                style={{width:"100%",background:C.bg,border:`1px solid ${ciuuInput?C.indigo:C.border}`,borderRadius:8,padding:"9px 12px",color:C.inkB,fontFamily:C.fD,fontSize:12,fontWeight:600}}/>
              {ciuuInput&&<div style={{fontFamily:C.fM,fontSize:8.5,color:C.indigo,marginTop:4}}>{(SECTORES[sector]&&SECTORES[sector].label)||"Sector no detectado"}</div>}
            </div>
          </div>
        </div>

        {/* Valores Corrientes Manuales */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:"14px 18px",marginBottom:14}}>
          {/* Año Activo */}
          <div style={{fontFamily:C.fM,fontSize:8,color:C.indigo,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:6,display:"flex",alignItems:"center",gap:6}}>
            <span>⚡</span> Valores Corrientes — Año Activo <span style={{fontFamily:C.fB,fontSize:9,color:C.inkD,textTransform:"none",letterSpacing:0,fontWeight:300}}>en millones COP · Solo si el Excel no los detecta</span>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:12}}>
            {[["Act. Corriente (Año Activo)",manualAC,setManualAC,"ac"],["Inventarios (Año Activo)",manualINV,setManualINV,"inv"],["Pas. Corriente (Año Activo)",manualPC,setManualPC,"pc"]].map(([label,val,setter,k])=>(
              <div key={k}>
                <div style={{fontFamily:C.fM,fontSize:8,color:C.inkD,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>{label}</div>
                <input key={k+"-"+rutKey} defaultValue={val} placeholder="ej. 12782"
                  onBlur={(e)=>{
                    const num=parseFloat(e.target.value.replace(/[^0-9.]/g,""))||0;
                    // Auto-escala: si el usuario escribe COP completo (>100M), convertir a millones
                    const raw=num>1e8 ? Math.round(num/1e6) : num;
                    setter(String(raw)||"");
                    if(k==="ac") MANUAL_AC=raw;
                    else if(k==="pc") MANUAL_PC=raw;
                    else MANUAL_INV=raw;
                    recompute();
                  }}
                  style={{width:"100%",background:C.bg,border:`1px solid ${val?C.indigo:C.border}`,borderRadius:8,padding:"9px 12px",color:C.inkB,fontFamily:C.fB,fontSize:12,boxSizing:"border-box"}}/>
              </div>
            ))}
          </div>
          {/* Año Anterior */}
          <div style={{fontFamily:C.fM,fontSize:8,color:C.amber,letterSpacing:"0.12em",textTransform:"uppercase",marginBottom:6,display:"flex",alignItems:"center",gap:6}}>
            <span>📅</span> Valores Corrientes — Año Anterior <span style={{fontFamily:C.fB,fontSize:9,color:C.inkD,textTransform:"none",letterSpacing:0,fontWeight:300}}>en millones COP · para comparativo</span>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
            {[["Act. Corriente (Año Ant.)",manualAC24,setManualAC24,"ac24"],["Inventarios (Año Ant.)",manualINV24,setManualINV24,"inv24"],["Pas. Corriente (Año Ant.)",manualPC24,setManualPC24,"pc24"]].map(([label,val,setter,k])=>(
              <div key={k}>
                <div style={{fontFamily:C.fM,fontSize:8,color:C.inkD,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>{label}</div>
                <input key={k+"-"+rutKey} defaultValue={val} placeholder="ej. 11495"
                  onBlur={(e)=>{
                    const raw=parseFloat(e.target.value.replace(/[^0-9.]/g,""))||0;
                    setter(e.target.value.replace(/[^0-9.]/g,""));
                    if(k==="ac24"&&raw>0) BAL24.actc=raw;
                    else if(k==="pc24"&&raw>0) BAL24.pasc=raw;
                    else if(k==="inv24"&&raw>0) BAL24.inv=raw;
                    recompute();
                  }}
                  style={{width:"100%",background:C.bg,border:`1px solid ${val?C.amber:C.border}`,borderRadius:8,padding:"9px 12px",color:C.inkB,fontFamily:C.fB,fontSize:12,boxSizing:"border-box"}}/>
              </div>
            ))}
          </div>
        </div>

        {/* Files */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          {[
            {key:"pyg25",label:"P&amp;G 2026",icon:"📈",color:C.emerald},
            {key:"pyg24",label:"P&amp;G 2025",icon:"📈",color:C.amber},
            {key:"bal25",label:"Balance 2026",icon:"📊",color:C.indigo},
            {key:"bal24",label:"Balance 2025",icon:"📊",color:C.rose},
          ].map((fc)=>(
            <div key={fc.key} onClick={()=>{const inp=document.createElement("input");inp.type="file";inp.accept=".xlsx,.xls";inp.onchange=(e)=>{if(e.target.files[0])handleFile(fc.key,e.target.files[0]);};inp.click();}}
              style={{border:`2px dashed ${status[fc.key]==="ok"?fc.color:"rgba(99,102,241,0.2)"}`,borderRadius:12,padding:"20px",textAlign:"center",cursor:"pointer",
                background:status[fc.key]==="ok"?fc.color+"08":"#fafbff",transition:"all 0.2s"}}>
              <div style={{fontSize:28,marginBottom:8}}>{fc.icon}</div>
              <div style={{fontFamily:C.fD,fontSize:13,fontWeight:700,color:status[fc.key]==="ok"?fc.color:C.inkB}}>{fc.label}</div>
              <div style={{fontFamily:C.fM,fontSize:20,color:SC[status[fc.key]],marginTop:6,fontWeight:700}}>{SI[status[fc.key]]}</div>
              {files[fc.key]&&<div style={{fontFamily:C.fM,fontSize:9,color:fc.color,marginTop:4}}>{files[fc.key].name.slice(0,30)}</div>}
            </div>
          ))}
        </div>

        {/* Apply button */}
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <button disabled={allLoaded===0}
            style={{padding:"14px 32px",borderRadius:10,border:"none",
              background:allLoaded>0?"linear-gradient(135deg,#6366F1,#8B5CF6)":"rgba(99,102,241,0.2)",
              color:allLoaded>0?"#fff":"#9CA3AF",fontFamily:C.fD,fontSize:14,fontWeight:700,
              cursor:allLoaded>0?"pointer":"not-allowed",transition:"all 0.2s",
              boxShadow:allLoaded>0?"0 6px 20px rgba(99,102,241,0.35)":"none"}}
            onClick={async()=>{
              if(allLoaded===0) return;
              setMsg("Procesando...");
              const XLSX=await loadXLSX();
              if(!XLSX){setMsg("Error cargando SheetJS");return;}
              const toRows=async(file)=>{
                const buf=await file.arrayBuffer();
                const wb=XLSX.read(new Uint8Array(buf),{type:"array"});
                return XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]],{header:1,defval:""});
              };
              // Convert Excel value to millions COP
              const toM=(v)=>{const n=parseFloat(String(v).replace(/[,$]/g,""));return isNaN(n)?0:n/1000000;};
              // Find a row by label and return 12 monthly values in millions
              const findRow=(rows,label)=>{
                const row=rows.find(r=>String(r[0]).trim()===label);
                if(!row)return null;
                // Find header row to locate Enero column
                const hdr=rows.find(r=>r.some(cell=>String(cell).includes("Enero")));
                const startCol=hdr?hdr.findIndex(cell=>String(cell).includes("Enero")):1;
                const cols=startCol>0?startCol:1;
                return Array.from({length:12},(_, i)=>toM(row[cols+i]||0));
              };
              try{
                // -- PYG 2026 ---------------------------------------------
                if(files.pyg25){
                  const rows=await toRows(files.pyg25);
                  const ing4 =findRow(rows,"Total 4 INGRESOS");
                  const ing41=findRow(rows,"Total 41 OPERACIONALES");
                  const g5  =findRow(rows,"Total 5 GASTOS");
                  const g51 =findRow(rows,"Total 51 OPERACIONALES DE ADMINISTRACION");
                  const g52 =findRow(rows,"Total 52 OPERACIONALES DE VENTAS");
                  const g53 =findRow(rows,"Total 53 NO OPERACIONALES");
                  const g54 =findRow(rows,"Total 54 IMPUESTO DE RENTA Y COMPLEMENTARIOS");
                  const cv  =findRow(rows,"Total 6 COSTOS DE VENTAS");
                  const cp  =findRow(rows,"Total 7 COSTOS DE PRODUCCION O DE OPERACION");
                  const ingFinal=ing4||ing41;
                  if(ingFinal) ING25.splice(0,12,...ingFinal);
                  if(g51) GADM25.splice(0,12,...g51);
                  if(g52) GVTA25.splice(0,12,...g52);
                  if(g53) GNOOP25.splice(0,12,...g53);
                  if(g54) IMP25.splice(0,12,...g54);
                  if(g5)  G5_25.splice(0,12,...g5);
                  if(cv)  CV25.splice(0,12,...cv);
                  if(cp)  CP25.splice(0,12,...cp);
                  // Resultado = Cta4 - Cta5 - Cta6 - Cta7
                  if(ingFinal){
                    const g5f=g5||g51.map((_,i)=>(g51[i]||0)+(g52[i]||0)+(g53[i]||0)+(g54?g54[i]:0));
                    const cvf=cv||Array(12).fill(0);
                    const cpf=cp||Array(12).fill(0);
                    UTIL25.splice(0,12,...ingFinal.map((_,i)=>ingFinal[i]-(g5f[i]||0)-(cvf[i]||0)-(cpf[i]||0)));
                  }
                }
                // -- PYG 2025 ---------------------------------------------
                if(files.pyg24){
                  const rows=await toRows(files.pyg24);
                  const ing4 =findRow(rows,"Total 4 INGRESOS");
                  const ing41=findRow(rows,"Total 41 OPERACIONALES");
                  const g5  =findRow(rows,"Total 5 GASTOS");
                  const g51 =findRow(rows,"Total 51 OPERACIONALES DE ADMINISTRACION");
                  const g52 =findRow(rows,"Total 52 OPERACIONALES DE VENTAS");
                  const g53 =findRow(rows,"Total 53 NO OPERACIONALES");
                  const g54 =findRow(rows,"Total 54 IMPUESTO DE RENTA Y COMPLEMENTARIOS");
                  const cv  =findRow(rows,"Total 6 COSTOS DE VENTAS");
                  const cp  =findRow(rows,"Total 7 COSTOS DE PRODUCCION O DE OPERACION");
                  const ingFinal=ing4||ing41;
                  if(ingFinal) ING24.splice(0,12,...ingFinal);
                  // Guardar gastos 2025 para comparativo YoY
                  if(g51) GADM24.splice(0,12,...g51);
                  if(g52) GVTA24.splice(0,12,...g52);
                  // Calcular UTIL24 mes a mes igual que UTIL25
                  if(ingFinal){
                    const g5f=g5||(g51||Array(12).fill(0)).map((_,i)=>(g51?g51[i]:0)+(g52?g52[i]:0)+(g53?g53[i]:0)+(g54?g54[i]:0));
                    const cvf=cv||Array(12).fill(0);
                    const cpf=cp||Array(12).fill(0);
                    UTIL24.splice(0,12,...ingFinal.map((_,i)=>ingFinal[i]-(g5f[i]||0)-(cvf[i]||0)-(cpf[i]||0)));
                  }
                }
                // -- BALANCE 2026 ------------------------------------------
                if(files.bal25){
                  const rows=await toRows(files.bal25);
                  const disp=findRow(rows,"Total 11 DISPONIBLE");
                  const cxc =findRow(rows,"Total 13 DEUDORES");
                  const inv =findRow(rows,"Total 14 INVENTARIOS");
                  const ta  =findRow(rows,"Total 1 ACTIVO");
                  const tp  =findRow(rows,"Total 2 PASIVO");
                  const pat =findRow(rows,"Total 3 PATRIMONIO");
                  const oblf=findRow(rows,"Total 21 OBLIGACIONES FINANCIERAS");
                  const prov=findRow(rows,"Total 22 PROVEEDORES");
                  if(disp) DISP25.splice(0,12,...disp);
                  if(cxc)  CXC25.splice(0,12,...cxc);
                  if(inv)  INV25.splice(0,12,...inv);
                  if(ta)   TACT25.splice(0,12,...ta);
                  if(tp)   TPAS25.splice(0,12,...tp);
                  if(pat)  PAT25.splice(0,12,...pat);
                  if(oblf) OBLF25.splice(0,12,...oblf);
                  if(prov) PROV25.splice(0,12,...prov);
                  // Filas NIIF corrientes (case-insensitive via findRow)
                  const actcRow=findRow(rows,"Total Activo corriente");
                  const pascRow=findRow(rows,"Total Pasivo Corriente");
                  if(actcRow) ACTC25.splice(0,12,...actcRow);
                  if(pascRow) PASC25.splice(0,12,...pascRow);
                  // UT24 desde BAL25 solo como fallback si PYG24 no lo proveyó
                  const allZero=UTIL24.every(v=>v===0);
                  if(allZero){
                    const util24row=findRow(rows,"Total 36 RESULTADOS DEL EJERCICIO")||findRow(rows,"Total 3605")||findRow(rows,"Total 36");
                    // Usamos el valor de Diciembre (índice 11) que es el acumulado anual
                    if(util24row){ const v=util24row[11]||util24row[0]||0; if(v!==0) UTIL24.splice(0,12,...Array(12).fill(v/12)); }
                  }
                }
                // -- BALANCE 2025 ------------------------------------------
                if(files.bal24){
                  const rows=await toRows(files.bal24);
                  const getD=(lbl)=>{const r=findRow(rows,lbl);return r?r[11]||r[r.length-1]||0:0;};
                  BAL24.disp=getD("Total 11 DISPONIBLE");
                  BAL24.cxc =getD("Total 13 DEUDORES");
                  BAL24.inv =getD("Total 14 INVENTARIOS");
                  BAL24.tact=getD("Total 1 ACTIVO");
                  BAL24.tpas=getD("Total 2 PASIVO");
                  BAL24.pat =getD("Total 3 PATRIMONIO");
                  BAL24.oblf=getD("Total 21 OBLIGACIONES FINANCIERAS");
                  BAL24.prov=getD("Total 22 PROVEEDORES");
                  // Filas NIIF corrientes 2025
                  BAL24.actc=getD("Total Activo corriente");
                  BAL24.pasc=getD("Total Pasivo Corriente");
                  // UT24 desde balance 2025 solo como fallback si PYG24 no lo proveyó
                  const allZero=UTIL24.every(v=>v===0);
                  if(allZero){
                    const ut24r=findRow(rows,"Total 36 RESULTADOS DEL EJERCICIO")||findRow(rows,"Total 36");
                    // Diciembre (índice 11) = resultado acumulado anual → dividir entre 12 para llenar meses
                    if(ut24r){ const v=ut24r[11]||0; if(v!==0) UTIL24.splice(0,12,...Array(12).fill(v/12)); }
                  }
                }

                recompute();
                key.current++;
                setTab("overview");
                setMsg("✅ Dashboard actualizado con tus archivos");
              }catch(e){setMsg("⚠ Error: "+e.message);}
            }}>
            {allLoaded===4?"✅ Aplicar Datos al Dashboard":"⊕ Cargar Datos"} ({allLoaded}/4)
          </button>
          <button onClick={()=>{
            const z=()=>[0,0,0,0,0,0,0,0,0,0,0,0];
            ING25.splice(0,12,...z());ING24.splice(0,12,...z());
            GADM25.splice(0,12,...z());GVTA25.splice(0,12,...z());GNOOP25.splice(0,12,...z());
            GADM24.splice(0,12,...z());GVTA24.splice(0,12,...z());
            UTIL25.splice(0,12,...z());
            DISP25.splice(0,12,...z());CXC25.splice(0,12,...z());INV25.splice(0,12,...z());
            PPE25.splice(0,12,...z());TACT25.splice(0,12,...z());TPAS25.splice(0,12,...z());
            PAT25.splice(0,12,...z());OBLF25.splice(0,12,...z());PROV25.splice(0,12,...z());
            CXP25.splice(0,12,...z());IMP25.splice(0,12,...z());OBL25.splice(0,12,...z());
            Object.keys(BAL24).forEach(k=>{BAL24[k]=0;});
            MANUAL_AC=0; MANUAL_PC=0; MANUAL_INV=0;
            setManualAC(""); setManualPC(""); setManualINV("");
            setManualAC24(""); setManualPC24(""); setManualINV24("");
            setEmpresa("");setNit("");setSector("");setPeriodo("Enero - Diciembre 2026");
            setFiles({pyg25:null,pyg24:null,bal25:null,bal24:null});
            setStatus({pyg25:"idle",pyg24:"idle",bal25:"idle",bal24:"idle"});
            setRutStatus("idle");setRutMsg("");setRutKey(k=>k+1);
            recompute();
            setMsg("Datos limpiados - listo para nueva carga");
            key.current++;
          }}
            style={{padding:"14px 22px",borderRadius:10,border:"2px solid rgba(244,63,94,0.3)",
              background:"rgba(244,63,94,0.05)",color:"#F43F5E",fontFamily:C.fD,fontSize:13,
              fontWeight:700,cursor:"pointer",transition:"all 0.2s"}}
            onMouseEnter={e=>e.currentTarget.style.background="rgba(244,63,94,0.12)"}
            onMouseLeave={e=>e.currentTarget.style.background="rgba(244,63,94,0.05)"}>
            Limpiar Data
          </button>
          <button onClick={()=>{
            const z=()=>[0,0,0,0,0,0,0,0,0,0,0,0];
            ING25.splice(0,12,...z());ING24.splice(0,12,...z());
            GADM25.splice(0,12,...z());GVTA25.splice(0,12,...z());GNOOP25.splice(0,12,...z());
            GADM24.splice(0,12,...z());GVTA24.splice(0,12,...z());
            UTIL25.splice(0,12,...z());UTIL24.splice(0,12,...z());
            CV25.splice(0,12,...z());CP25.splice(0,12,...z());G5_25.splice(0,12,...z());
            DISP25.splice(0,12,...z());CXC25.splice(0,12,...z());INV25.splice(0,12,...z());
            PPE25.splice(0,12,...z());TACT25.splice(0,12,...z());TPAS25.splice(0,12,...z());
            PAT25.splice(0,12,...z());OBLF25.splice(0,12,...z());PROV25.splice(0,12,...z());
            CXP25.splice(0,12,...z());IMP25.splice(0,12,...z());OBL25.splice(0,12,...z());
            Object.keys(BAL24).forEach(k=>{BAL24[k]=0;});
            IT=0;IT24=0;UT=0;UT24=0;GT=0;GV=0;GN=0;
            recompute();
            setEmpresa("");setNit("");setSector("");setCiuuInput("");
            setPeriodo("Enero - Diciembre 2026");
            setFiles({pyg25:null,pyg24:null,bal25:null,bal24:null});
            setStatus({pyg25:"idle",pyg24:"idle",bal25:"idle",bal24:"idle"});
            setRutStatus("idle");setRutMsg("");setRutKey(k=>k+1);
            setMsg("");key.current++;
          }}
            style={{padding:"14px 20px",borderRadius:10,border:"2px solid rgba(244,63,94,0.3)",
              background:"rgba(244,63,94,0.05)",color:C.rose,fontFamily:C.fD,fontSize:13,fontWeight:700,cursor:"pointer"}}
            onMouseEnter={e=>{e.currentTarget.style.background="rgba(244,63,94,0.1)";}}
            onMouseLeave={e=>{e.currentTarget.style.background="rgba(244,63,94,0.05)";}}>
            Limpiar
          </button>
          {msg&&<div style={{fontFamily:C.fB,fontSize:11,color:msg.startsWith("✅")?C.emerald:C.rose,fontWeight:300}}>{msg}</div>}
        </div>
      </div>
    );
  };

  const pages={datos:<CARGAR_DATOS/>,overview:<Overview/>,pyg:<PYG/>,balance:<BALANCE/>,ratios:<RATIOS/>,riesgos:<RIESGOS/>,comparativo:<COMPARATIVO/>,ejecutivo:<INFORME_EJ/>,ia:<IA/>};

  return(
    <div style={{background:C.bg,minHeight:"100vh",display:"flex",flexDirection:"column",fontFamily:C.fB}}>
      <style>{CSS}</style>
      {/* TABS */}
      <div style={{background:"rgba(255,255,255,0.7)",backdropFilter:"blur(20px)",borderBottom:`1px solid ${C.border}`,padding:"0 32px",display:"flex",gap:2,position:"sticky",top:57,zIndex:99}}>
        {TABS.map(t=>{
          const a=tab===t.id;
          return(
            <button key={t.id} className="tab-btn" onClick={()=>changeTab(t.id)}
              style={{fontFamily:C.fB,fontSize:12.5,fontWeight:a?600:400,color:a?C.indigo:C.inkD,
                background:a?"rgba(99,102,241,0.08)":"transparent",border:"none",
                padding:"12px 18px",borderRadius:"8px 8px 0 0",
                borderBottom:a?`2px solid ${C.indigo}`:"2px solid transparent",
                letterSpacing:"0.01em",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:6}}>
              <span style={{opacity:a?1:0.5}}>{t.icon}</span>{t.label}
            </button>
          );
        })}
      </div>

      {/* CONTENT */}
      <div style={{flex:1,padding:"24px 32px",maxWidth:1440,width:"100%",margin:"0 auto",alignSelf:"stretch"}}>
        {pages[tab]}
      </div>

    </div>
  );
}
